import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { apiTokenRequest, apiConfig } from "../authConfig";
import { message, Modal, Tooltip, Tag, Alert, Spin, Select } from "antd";
import { GapAnalysisAiFailureBanner, describeAiFailureReason } from "./GapAnalysisAiFailureBanner";
import { CollapsiblePane } from "./CollapsiblePane";
import AiAgreementIndicator from "./AiAgreementIndicator";
import ImpersonationDialog from "./ImpersonationDialog";
import { getActiveImpersonation, impersonationHeaders } from "../utils/impersonationFetch";
import { Role as UserRole, View } from "../constants/roles";
import { ragBandForAvg, ragBandForScore, ragBandForPercent, RAG_BG_CLASS, RAG_TEXT_CLASS } from "../utils/ragBand";
import { formatDdMmYyyy, formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import { apiErrorFromResponse, toApiError, userMessageForError } from "../utils/apiError";
import { gapEvidenceErrorText, gapProfileSaveErrorText, techEvaluationHistoryRows, SPOC_TOOL_TESTS_NOT_PRESENTED_TEXT, LEGACY_COMPLETED_TEXT, REOPENED_WITH_EVALUATION_TEXT, REOPENED_TEXT, REOPEN_REASON_MIN_LENGTH, REOPEN_REASON_MAX_LENGTH, TECH_HISTORY_TAMPERED_TEXT, reopenReasonRequired, normalisedReopenReason, evaluateToolEvidenceCompletion, isGapAnalysisLocked, reopenBlockedReason as computeReopenBlockedReason, evidenceWriteBlockedReason, techEvalBlockedReason, linkableToolRecords, gapPushBlockedReason, GAP_PUSH_REFUSAL_REASONS, techAssessmentPrerequisiteMet, techOverrideRowsMissingNote, techRowIsOverridden, TECH_ASSESSMENT_REQUIRED_TEXT, TECH_WAIVER_MIN_REASON_LENGTH, OVERRIDE_NOTE_MIN_LENGTH } from "../utils/gapToolEvidence";
import { useApp } from "../context/AppContext";
import { pickScopingPersistPayload } from "../utils/pickScopingPersistPayload";
import { ROLE_TOKEN_MAP } from "../constants/roles";
import { ContractAnnotationsSidebar } from "./ContractAnnotationsSidebar";
import { ContractGoogleDocsShell } from "./ContractGoogleDocsShell";
import { resolveCompanyLogoUrl } from "../utils/persistableBlobUrl";
import GapReportSectionField from "./GapReportSectionField";
import { aiRowHasScore, isGapRowScored, computeOverallPercent, UNCLASSIFIED_DOMAIN } from "../utils/gapRowScored";
import { buildGapAiCompanyContext } from "../utils/gapAiContext";
import { buildReportDraft, unconfirmedReportSections, withReportField, withReportFieldStatus, hasLegacyCannedReportText, withoutLegacyCannedText, reportCompanyName } from "../utils/gapReportDraft";
import SubStepNav, { buildSubSteps } from "./page/SubStepNav";
import StageHeader from "./page/StageHeader";
import {
  GAP_MEETING_DEPARTMENT_OPTIONS,
  meetingDateToInputValue,
  mapApiMeetingToScheduledRow,
  isMeetingFromGapAnalysis,
  formatSpocMeetingDate,
  SPOC_MEETING_TYPE_LABELS,
  MEETING_SCHEDULE_SOURCE_GAP_ANALYSIS,
  normalizeMeetingAttendees,
} from "../utils/gapMeetingScheduleShared";
import {
  normalizeContractAnnotations,
  createContractAnnotation,
  addReplyToAnnotation,
  resolveAnnotation,
  stableAnchorKey,
} from "../utils/contractAnnotations";
const COMMIT_RELOAD_REASONS = new Set(["GAP_ANALYSIS_LOCKED", "STAGE_PREREQUISITE_NOT_MET", "ASSESSMENT_COMPLETE", "REOPEN_BLOCKED_SHARED_WITH_LA", "REOPEN_REASON_REQUIRED", "EVIDENCE_CHANGED", "CONCURRENT_CHANGE", "TECH_ASSESSMENT_REQUIRED", ...GAP_PUSH_REFUSAL_REASONS]);
const SELF_APPROVED_TEXT = "Approved by the same person who generated it.";
const SHARE_NEEDS_VALIDATED_SCORES = "Sharing needs validated technical scores: run the AI evaluation and approve it in Review & Validate first, or mark the technical assessment not applicable.";
function isRoleDelegate(role) {
  if (!role) return false;
  const upper = (ROLE_TOKEN_MAP[role] ?? role).toUpperCase();
  return upper === "COMPANY_DELEGATE";
}

function normalizeGapControlMappings(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const m of raw.slice(0, 6)) {
    if (!m || typeof m !== "object") continue;
    const number = typeof m.number === "string" ? m.number.trim() : String(m.number ?? "").trim();
    const name = typeof m.name === "string" ? m.name.trim() : String(m.name ?? "").trim();
    if (!number && !name) continue;
    out.push({ number: number.slice(0, 40), name: name.slice(0, 200) });
  }
  return out;
}
const fmtControlCount = (v) => (typeof v === "number" && Number.isFinite(v) ? v : "-");
function GapControlsCell({ item }) {
  const maps = normalizeGapControlMappings(item.controlMappings);
  // A missing / null count stays unknown ("\u2014"); it is never coerced to 0.
  const count = typeof item.applicableControls === "number" && Number.isFinite(item.applicableControls) ? item.applicableControls : null;
  const displayCount = maps.length > 0 ? maps.length : count;
  const primaryLabel = displayCount != null && displayCount > 0 ? String(displayCount) : displayCount === 0 ? "0" : "-";
  const tooltipInner =
    maps.length > 0
      ? /* @__PURE__ */ jsxs("div", {
          className: "max-w-[min(100vw-24px,22rem)] py-0.5 text-left space-y-1.5",
          children: maps.map((m, i) =>
            /* @__PURE__ */ jsxs("div", { className: "text-[11px] leading-snug text-[color:var(--color-text-hero)]", children: [
              /* @__PURE__ */ jsx("span", { className: "font-mono font-bold text-[color:var(--color-accent)]", children: m.number || "-" }),
              m.name && m.name !== "-"
                ? /* @__PURE__ */ jsxs("span", { className: "text-[color:var(--color-text-muted-dark)]", children: [" - ", m.name] })
                : null
            ] }, `cm-${i}`),
          ),
        })
      : count != null && count > 0
        ? /* @__PURE__ */ jsxs("span", { className: "text-[11px] text-[color:var(--color-text-muted-dark)]", children: [
            count,
            " Annex A control",
            count === 1 ? "" : "s",
            " considered applicable (no specific control reference returned)."
          ] })
        : null;
  const pill = /* @__PURE__ */ jsx("span", {
    className: `inline-flex max-w-[104px] min-w-0 justify-center truncate px-2 py-0.5 bg-[var(--color-bg-alt)] rounded-lg text-[10px] font-bold text-[color:var(--color-text-muted-dark)] ${tooltipInner ? "cursor-help border border-transparent hover:border-[color:var(--color-border-strong)] hover:bg-[var(--color-bg-alt)]" : ""}`,
    children: primaryLabel
  });
  return tooltipInner
    ? /* @__PURE__ */ jsx(Tooltip, {
        color: "#ffffff",
        title: tooltipInner,
        placement: "topLeft",
        mouseEnterDelay: 0.18,
        children: pill
      })
    : pill;
}

function normalizeAnnexAControlRefForIso(raw) {
  if (raw == null || raw === "") return "";
  let s = String(raw).trim();
  s = s.replace(/^Annex\s*A\.?\s*/i, "");
  s = s.replace(/\s+/g, "");
  s = s.replace(/^A\.?/i, "");
  const m = s.match(/(\d+(?:\.\d+)*)/);
  return m ? m[1] : "";
}
function compareCtrlNumberStr(a, b) {
  const pa = String(a).split(".").map((x) => parseInt(x, 10) || 0);
  const pb = String(b).split(".").map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da - db;
  }
  return 0;
}
function annexAvgToHeatClass(avg) {
  const band = ragBandForAvg(avg);
  if (!band) return "bg-[var(--color-border)]";
  return band === "green" ? "bg-emerald-500" : band === "amber" ? "bg-amber-400" : "bg-rose-500";
}
function parseMilestoneDateString(s) {
  if (!s || typeof s !== "string") return null;
  const d = new Date(s.trim());
  return Number.isNaN(d.getTime()) ? null : d;
}
/** API/Prisma uses startDate/dueDate/taskTitle/keyDeliverables; legacy profile rows may use start/due/title/keyDeliverable. */
function getMilestoneStartDueStrings(row) {
  if (!row || typeof row !== "object") return { startStr: "", dueStr: "" };
  const startStr = row.start ?? row.startDate ?? "";
  const dueStr = row.due ?? row.dueDate ?? "";
  return {
    startStr: typeof startStr === "string" ? startStr : String(startStr ?? ""),
    dueStr: typeof dueStr === "string" ? dueStr : String(dueStr ?? ""),
  };
}
function getMilestoneDisplayTitle(row) {
  if (!row || typeof row !== "object") return "";
  const t = row.taskTitle ?? row.title ?? row.wbs;
  if (t != null && String(t).trim()) return String(t).trim().slice(0, 200);
  return "";
}
function getMilestoneDeliverablesLine(row) {
  if (!row || typeof row !== "object") return "";
  const k = row.keyDeliverables ?? row.keyDeliverable;
  if (k != null && String(k).trim()) return String(k).trim().slice(0, 500);
  return "";
}
/** Aligns with summarizeMilestonesForPush bucket logic (today at midnight). */
function milestonePlanStatusKey(start, due, t0) {
  const dEnd = due ? new Date(due).setHours(0, 0, 0, 0) : null;
  const dStart = start ? new Date(start).setHours(0, 0, 0, 0) : null;
  if (dEnd != null && t0 > dEnd) return "overdue";
  if (dStart != null && t0 < dStart) return "upcoming";
  return "in_progress";
}
function summarizeMilestonesForPush(rows) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t0 = today.getTime();
  let overdue = 0;
  let upcoming = 0;
  let inProgress = 0;
  const parsed = [];
  const arr = Array.isArray(rows) ? rows : [];
  for (const row of arr) {
    const { startStr, dueStr } = getMilestoneStartDueStrings(row);
    const start = parseMilestoneDateString(startStr);
    const due = parseMilestoneDateString(dueStr);
    if (!start && !due) continue;
    parsed.push({ row, start, due });
  }
  const total = parsed.length;
  for (const { start, due } of parsed) {
    const dEnd = due ? new Date(due).setHours(0, 0, 0, 0) : null;
    const dStart = start ? new Date(start).setHours(0, 0, 0, 0) : null;
    if (dEnd != null && t0 > dEnd) overdue++;
    else if (dStart != null && t0 < dStart) upcoming++;
    else inProgress++;
  }
  return { overdue, upcoming, inProgress, total, parsed };
}

const PUSH_ISO_THEME_ORDER = ["Organizational", "People", "Physical", "Technological"];

function PushIsoHeatmapSection({ isoControls, readinessMap, loading, versionLabel, showIso }) {
  if (!showIso) {
    return /* @__PURE__ */ jsx("p", {
      className: "text-xs text-[color:var(--color-text-muted)] leading-relaxed",
      children: "Annex A heatmap is shown for ISO 27001\u2013aligned projects. Readiness and remediation below still reflect this engagement.",
    });
  }
  if (loading) {
    return /* @__PURE__ */ jsx("div", {
      className: "h-32 flex items-center justify-center text-xs font-medium text-[color:var(--color-text-muted)]",
      children: "Loading Annex A control registry\u2026",
    });
  }
  if (!isoControls?.length) {
    return /* @__PURE__ */ jsx("p", {
      className: "text-xs text-[color:var(--color-warn-fg)] bg-[var(--color-warn-bg)] border border-[color:var(--color-warn-line)] rounded-xl px-4 py-3",
      children: "No ISO 27001 controls returned from Policy Hub. Heatmap requires a seeded Annex A registry.",
    });
  }
  const byTheme = {};
  for (const c of isoControls) {
    const th = c.controlTheme || "Other";
    if (!byTheme[th]) byTheme[th] = [];
    byTheme[th].push(c);
  }
  for (const k of Object.keys(byTheme)) {
    byTheme[k].sort((a, b) => compareCtrlNumberStr(a.ctrlNumber, b.ctrlNumber));
  }
  const themes = [...PUSH_ISO_THEME_ORDER.filter((t) => byTheme[t]?.length), ...Object.keys(byTheme).filter((t) => !PUSH_ISO_THEME_ORDER.includes(t))];
  return /* @__PURE__ */ jsxs("div", {
    className: "space-y-6",
    children: [
      versionLabel &&
        /* @__PURE__ */ jsxs("p", {
          className: "text-[10px] text-[color:var(--color-text-muted)] font-medium",
          children: ["Policy Hub: ", versionLabel, " \u00b7 ", isoControls.length, " controls"],
        }),
      themes.map((theme) => {
        const list = byTheme[theme];
        if (!list?.length) return null;
        return /* @__PURE__ */ jsxs(
          "div",
          {
            className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] p-4",
            children: [
              /* @__PURE__ */ jsxs("h6", {
                className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-3",
                children: [theme, " (", list.length, ")"],
              }),
              /* @__PURE__ */ jsx("div", {
                className: "flex flex-wrap gap-1",
                children: list.map((c) => {
                  const key = String(c.ctrlNumber || "").trim();
                  const agg = key && readinessMap?.get ? readinessMap.get(key) : null;
                  const avg = agg?.avg;
                  const count = agg?.count ?? 0;
                  const assessed = agg != null;
                  const heatClass = assessed ? annexAvgToHeatClass(avg) : "bg-[var(--color-border)]";
                  const label = `A.${c.ctrlNumber} - ${(c.ctrlName || "").slice(0, 80)}`;
                  const tip = assessed
                    ? /* @__PURE__ */ jsxs("div", {
                        className: "max-w-xs text-[11px] text-[color:var(--color-text-hero)]",
                        children: [
                          /* @__PURE__ */ jsx("div", { className: "font-mono font-bold text-[color:var(--color-accent)]", children: `A.${c.ctrlNumber}` }),
                          /* @__PURE__ */ jsx("div", { className: "text-[color:var(--color-text-muted-dark)] mt-1", children: c.ctrlName }),
                          /* @__PURE__ */ jsxs("div", { className: "mt-2 text-[color:var(--color-text-muted)]", children: [
                            "Avg readiness: ",
                            avg != null ? avg.toFixed(1) : "-",
                            " / 5",
                          ] }),
                          /* @__PURE__ */ jsxs("div", { className: "text-[color:var(--color-text-muted)] text-[10px] mt-1", children: [count, " citation", count === 1 ? "" : "s", " in gap analysis"] }),
                        ],
                      })
                    : /* @__PURE__ */ jsxs("div", {
                        className: "max-w-xs text-[11px] text-[color:var(--color-text-hero)]",
                        children: [
                          /* @__PURE__ */ jsx("div", { className: "font-mono font-bold text-[color:var(--color-text-muted-dark)]", children: `A.${c.ctrlNumber}` }),
                          /* @__PURE__ */ jsx("div", { className: "text-[color:var(--color-text-muted-dark)] mt-1", children: c.ctrlName }),
                          /* @__PURE__ */ jsx("div", { className: "mt-2 text-[color:var(--color-text-muted)] text-[10px]", children: "Not cited in gap analysis mappings" }),
                        ],
                      });
                  return /* @__PURE__ */ jsx(
                    Tooltip,
                    {
                      color: "#ffffff",
                      title: tip,
                      placement: "top",
                      mouseEnterDelay: 0.15,
                      children: /* @__PURE__ */ jsx("button", {
                        type: "button",
                        className: `w-5 h-5 rounded-sm shrink-0 ${heatClass} ring-1 ring-white/40 hover:ring-2 hover:ring-blue-300 transition-all focus:outline-none ${assessed ? "cursor-help" : "cursor-default opacity-90"}`,
                        "aria-label": label,
                      }),
                    },
                    c.id || `${theme}-${c.ctrlNumber}`,
                  );
                }),
              }),
            ],
          },
          theme,
        );
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "flex flex-wrap items-center gap-4 text-[9px] text-[color:var(--color-text-muted)] font-bold uppercase tracking-wider pt-2 border-t border-[color:var(--color-border)]",
        children: [
          /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
            /* @__PURE__ */ jsx("span", { className: "w-3 h-3 rounded-sm bg-emerald-500" }),
            " High (4\u20135)",
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
            /* @__PURE__ */ jsx("span", { className: "w-3 h-3 rounded-sm bg-amber-400" }),
            " Medium (2\u20133)",
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
            /* @__PURE__ */ jsx("span", { className: "w-3 h-3 rounded-sm bg-rose-500" }),
            " Low (0\u20131)",
          ] }),
          /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
            /* @__PURE__ */ jsx("span", { className: "w-3 h-3 rounded-sm bg-[var(--color-border)]" }),
            " Not cited",
          ] }),
        ],
      }),
    ],
  });
}

function PushMilestoneGantt({ milestones }) {
  const { overdue, upcoming, inProgress, total, parsed } = summarizeMilestonesForPush(milestones);
  if (total === 0) {
    return /* @__PURE__ */ jsx("div", {
      className: "rounded-2xl border border-dashed border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] px-6 py-12 text-center",
      children: /* @__PURE__ */ jsx("p", {
        className: "text-xs text-[color:var(--color-text-muted)] font-medium",
        children: "No milestones with dates in the scoping contract plan yet. Add start and due dates under Scoping \u2192 Milestones to see the remediation timeline.",
      }),
    });
  }
  let minT = Infinity;
  let maxT = -Infinity;
  for (const { start, due } of parsed) {
    const s = start ? new Date(start).getTime() : null;
    const d = due ? new Date(due).getTime() : null;
    if (s != null) {
      minT = Math.min(minT, s);
      maxT = Math.max(maxT, s);
    }
    if (d != null) {
      minT = Math.min(minT, d);
      maxT = Math.max(maxT, d);
    }
  }
  const pad = 86400000 * 3;
  minT -= pad;
  maxT += pad;
  const span = maxT - minT || 1;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const todayT = today.getTime();
  const todayPct = Math.max(0, Math.min(100, ((todayT - minT) / span) * 100));
  const slice = parsed.slice(0, 14);
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  const t0 = dayStart.getTime();
  const asOfLabel = formatDdMmYyyy(Date.now());
  const healthSentence =
    overdue > 0
      ? `As of ${asOfLabel}: ${overdue} milestone${overdue === 1 ? "" : "s"} past due - remediation is behind the scoping schedule.`
      : `As of ${asOfLabel}: no milestones past due - dates are on track relative to today.`;

  return /* @__PURE__ */ jsxs("div", {
    className: "space-y-4",
    children: [
      /* @__PURE__ */ jsxs("div", {
        className: "flex flex-wrap gap-3 text-[10px] font-bold text-[color:var(--color-text-muted-dark)]",
        children: [
          /* @__PURE__ */ jsxs("span", { className: "px-2 py-1 rounded-lg bg-[var(--color-bg-alt)]", children: [total, " milestones"] }),
          /* @__PURE__ */ jsxs("span", { className: "px-2 py-1 rounded-lg bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)]", children: [inProgress, " in progress"] }),
          /* @__PURE__ */ jsxs("span", { className: "px-2 py-1 rounded-lg bg-[var(--color-info-bg)] text-[color:var(--color-accent)]", children: [upcoming, " upcoming"] }),
          /* @__PURE__ */ jsxs("span", { className: "px-2 py-1 rounded-lg bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)]", children: [overdue, " overdue"] }),
        ],
      }),
      /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[color:var(--color-text-muted-dark)] font-medium leading-snug", children: healthSentence }),
      /* @__PURE__ */ jsx("div", {
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-bg-white)] p-4 overflow-x-auto",
        children: /* @__PURE__ */ jsxs("div", {
          className: "min-w-[520px] space-y-3",
          children: [
            /* @__PURE__ */ jsxs("div", {
              className: "relative h-8 mb-2",
              children: [
                /* @__PURE__ */ jsx("div", { className: "absolute inset-x-0 top-1/2 h-px bg-[var(--color-bg-alt)] -translate-y-1/2" }),
                /* @__PURE__ */ jsx("div", {
                  className: "absolute top-0 bottom-0 w-0.5 bg-[var(--color-accent-fill)] z-10 shadow-sm",
                  style: { left: `${todayPct}%`, transform: "translateX(-50%)" },
                }),
                /* @__PURE__ */ jsx("span", {
                  className: "absolute -top-1 text-[9px] font-black text-[color:var(--color-accent)] uppercase tracking-wider",
                  style: { left: `${todayPct}%`, transform: "translateX(-50%)" },
                  children: "Today",
                }),
              ],
            }),
            slice.map((p, i) => {
              const sT = p.start ? new Date(p.start).getTime() : p.due ? new Date(p.due).getTime() : minT;
              const eT = p.due ? new Date(p.due).getTime() : sT + 86400000 * 7;
              const left = ((sT - minT) / span) * 100;
              const width = Math.max(0.8, ((eT - sT) / span) * 100);
              const label = getMilestoneDisplayTitle(p.row) || `Task ${i + 1}`;
              const title = label.slice(0, 64);
              const statusKey = milestonePlanStatusKey(p.start, p.due, t0);
              const badgeLabel = statusKey === "overdue" ? "Overdue" : statusKey === "upcoming" ? "Upcoming" : "In progress";
              const badgeClass =
                statusKey === "overdue"
                  ? "bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-line)]"
                  : statusKey === "upcoming"
                    ? "bg-[var(--color-info-bg)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]"
                    : "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]";
              const barClass =
                statusKey === "overdue" ? "bg-rose-500" : statusKey === "upcoming" ? "bg-violet-500" : "bg-amber-500";
              const del = getMilestoneDeliverablesLine(p.row);
              const dep = p.row.dependencies != null && String(p.row.dependencies).trim() ? String(p.row.dependencies).trim().slice(0, 300) : "";
              const tip = [del && `Deliverables: ${del}`, dep && `Dependencies: ${dep}`].filter(Boolean).join("\n\n");
              return /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "grid grid-cols-[minmax(140px,1fr)_3fr] gap-3 items-center",
                  children: [
                    /* @__PURE__ */ jsxs("div", {
                      className: "min-w-0 pr-2 flex flex-col gap-1",
                      children: [
                        /* @__PURE__ */ jsxs("div", {
                          className: "flex items-start gap-1.5 min-w-0",
                          children: [
                            /* @__PURE__ */ jsx("span", {
                              className: "text-[10px] font-semibold text-[color:var(--color-text-muted-dark)] truncate flex-1 min-w-0",
                              title: tip || title,
                              children: title,
                            }),
                            /* @__PURE__ */ jsx("span", {
                              className: `shrink-0 px-1.5 py-0.5 rounded border text-[8px] font-black uppercase tracking-wide ${badgeClass}`,
                              children: badgeLabel,
                            }),
                          ],
                        }),
                      ],
                    }),
                    /* @__PURE__ */ jsx("div", {
                      className: "relative h-6 bg-[var(--color-bg-alt)] rounded-md overflow-hidden",
                      title: tip || undefined,
                      children: /* @__PURE__ */ jsx("div", {
                        className: `absolute top-1 bottom-1 rounded ${barClass} opacity-90`,
                        style: { left: `${left}%`, width: `${width}%`, minWidth: "4px" },
                      }),
                    }),
                  ],
                },
                p.row?.id || `ms-${i}`,
              );
            }),
            parsed.length > slice.length &&
              /* @__PURE__ */ jsxs("p", {
                className: "text-[10px] text-[color:var(--color-text-muted)] text-center pt-2",
                children: ["Showing ", slice.length, " of ", parsed.length, " milestones"],
              }),
          ],
        }),
      }),
      /* @__PURE__ */ jsx("p", {
        className: "text-[10px] text-[color:var(--color-text-muted)]",
        children: "Dates come from Scoping \u2192 Milestones (same data as the contract milestone plan). Bars: overdue (past due date), in progress (started, not past due), upcoming (before start).",
      }),
    ],
  });
}

const LA_SUBSTEPS = [
  { key: "release", label: "Release Questionnaire", num: 1 },
  { key: "responses", label: "Responses", num: 2 },
  { key: "schedule", label: "Schedule Interviews", num: 3 },
  { key: "review-scores", label: "Review & Validate Scores", num: 4 },
  { key: "report", label: "Report", num: 5 },
  { key: "push-dashboard", label: "Push to Dashboard", num: 6 }
];
const ADV_SUBSTEPS = [
  { key: "setup-tool", label: "Setup Tool", num: 1 },
  { key: "conduct-assessment", label: "Conduct Assessment", num: 2 },
  { key: "ai-eval-tech", label: "AI Evaluation", num: 3 },
  { key: "review-scores-tech", label: "Review & Validate", num: 4 },
  { key: "share-la", label: "Share with Lead Advisor", num: 5 }
];
const SPOC_SUBSTEPS = [
  { key: "respond", label: "Respond to Questions", num: 1 },
  { key: "delegate", label: "Delegate Questionnaire", num: 2 },
  { key: "review-approve", label: "Review & Finalize Delegation", num: 3 },
  { key: "meetings", label: "Meeting requests", num: 4 },
  { key: "report-review", label: "Review Report", num: 5 }
];
const GAP_ANALYSIS_STATUS_STEPS = [
  { key: "draft", title: "Draft", sub: "Prepare & score", icon: "file" },
  { key: "review", title: "Under Review", sub: "SPOC reviewing", icon: "search" },
  { key: "approved", title: "Approved", sub: "Report approved", icon: "check" },
  { key: "final", title: "Finalized", sub: "Complete", icon: "pencil" },
];

function gapAnalysisStatusStepIndex(status) {
  if (status === "Under Review") return 1;
  if (status === "Approved by SPOC") return 2;
  if (status === "Finalized") return 3;
  return 0;
}

function gapAnalysisStatusBarIcon(kind) {
  const common = { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" };
  if (kind === "file") {
    return /* @__PURE__ */ jsx("svg", {
      ...common,
      children: /* @__PURE__ */ jsx("path", {
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      }),
    });
  }
  if (kind === "search") {
    return /* @__PURE__ */ jsx("svg", {
      ...common,
      children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" }),
    });
  }
  if (kind === "check") {
    return /* @__PURE__ */ jsx("svg", {
      ...common,
      children: /* @__PURE__ */ jsx("path", {
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
      }),
    });
  }
  return /* @__PURE__ */ jsx("svg", {
    ...common,
    children: /* @__PURE__ */ jsx("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "2",
      d: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
    }),
  });
}

export function renderGapAnalysisStatusBar(gapAnalysisStatus) {
  const currentIdx = gapAnalysisStatusStepIndex(gapAnalysisStatus);
  const row = [];
  GAP_ANALYSIS_STATUS_STEPS.forEach((step, i) => {
    row.push(
      /* @__PURE__ */ jsxs(
        "div",
        {
          key: step.key,
          className: "flex flex-1 flex-col items-center text-center min-w-0 px-0.5",
          children: [
            /* @__PURE__ */ jsx("div", {
              className: `flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${i < currentIdx ? "border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" : i === currentIdx ? "border-[1.5px] border-[color:var(--color-accent)] bg-[var(--color-info-bg)] text-[color:var(--color-accent)] shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-accent)_18%,transparent)]" : "border border-dashed border-[color:var(--color-border-strong)] bg-transparent text-[color:var(--color-text-muted)]"}`,
              children: gapAnalysisStatusBarIcon(step.icon),
            }),
            /* @__PURE__ */ jsx("div", {
              className: `mt-2.5 text-[8px] font-black uppercase tracking-widest text-balance sm:text-[10px] ${i === currentIdx ? "text-[color:var(--color-text-hero)]" : "text-[color:var(--color-text-muted)]"}`,
              children: step.title,
            }),
            /* @__PURE__ */ jsx("div", {
              className: "mt-0.5 text-[8px] font-medium leading-snug text-[color:var(--color-text-muted)] sm:text-[9px]",
              children: step.sub,
            }),
          ],
        },
      ),
    );
    if (i < GAP_ANALYSIS_STATUS_STEPS.length - 1) {
      row.push(
        /* @__PURE__ */ jsx("div", {
          key: `${step.key}-connector`,
          className: `mx-1 h-1 min-w-[12px] max-w-[4rem] flex-1 self-center rounded-full sm:mx-2 ${i < currentIdx ? "bg-[var(--color-accent-fill)]" : "bg-[var(--color-border)]"}`,
          "aria-hidden": true,
        }),
      );
    }
  });
  return /* @__PURE__ */ jsx("div", {
    className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-bg-white)] px-3 py-5 shadow-lg sm:px-6 md:px-8",
    children: /* @__PURE__ */ jsx("div", {
      className: "mx-auto flex w-full max-w-5xl items-center",
      children: row,
    }),
  });
}

function normalizeLaSubStep(s) {
  if (s === "present") return "push-dashboard";
  return s || "release";
}

function initialDelegateGapDelSubStep(gw, delProg, key, delId) {
  const stored = delProg?.gapDelSubStep ?? gw.delegateProgress?.[key]?.gapDelSubStep ?? gw.delegateProgress?.[delId]?.gapDelSubStep;
  if (stored) return stored;
  return delProg?.acknowledged ? "respond-del" : "acknowledge";
}

function normalizeDelegateAnswersMap(raw) {
  if (!raw || typeof raw !== "object") return {};
  const o = {};
  for (const k of Object.keys(raw)) {
    const v = raw[k];
    if (v && typeof v === "object" && "response" in v) o[k] = v.response || "";
    else if (typeof v === "string") o[k] = v;
    else o[k] = "";
  }
  return o;
}
function extractDelegateAttachmentsFromAnswers(raw) {
  if (!raw || typeof raw !== "object") return {};
  const o = {};
  for (const k of Object.keys(raw)) {
    const v = raw[k];
    if (v && typeof v === "object" && Array.isArray(v.attachments)) o[k] = v.attachments;
  }
  return o;
}
/** Dedupe by attachment id so local UI state and a fresh server profile can be merged safely. */
function mergeAttachmentListsById(...lists) {
  const byId = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const a of list) {
      if (a && a.id) byId.set(a.id, a);
    }
  }
  return [...byId.values()];
}
function buildLaResponsesAttachmentsByQuestion(spocQA, delegateProgress, evidenceFileArtifacts) {
  const byQ = {};
  const seenByQ = {};
  const markSeen = (qid, aid) => {
    if (!seenByQ[qid]) seenByQ[qid] = new Set();
    if (seenByQ[qid].has(aid)) return false;
    seenByQ[qid].add(aid);
    return true;
  };
  const pushAtt = (qid, att, role) => {
    if (!qid || !att || !att.id) return;
    if (!markSeen(qid, att.id)) return;
    if (!byQ[qid]) byQ[qid] = [];
    byQ[qid].push({
      id: att.id,
      url: att.url,
      fileName: att.fileName || "File",
      uploadedAt: att.uploadedAt,
      role
    });
  };
  if (spocQA && typeof spocQA === "object") {
    for (const qid of Object.keys(spocQA)) {
      const arr = spocQA[qid];
      if (!Array.isArray(arr)) continue;
      for (const att of arr) pushAtt(qid, att, "spoc");
    }
  }
  const dp = delegateProgress && typeof delegateProgress === "object" ? delegateProgress : {};
  for (const prog of Object.values(dp)) {
    if (!prog || typeof prog !== "object" || !prog.answers) continue;
    const answers = prog.answers;
    for (const qid of Object.keys(answers)) {
      const ans = answers[qid];
      if (!ans || typeof ans !== "object" || !Array.isArray(ans.attachments)) continue;
      for (const att of ans.attachments) pushAtt(qid, att, "delegate");
    }
  }
  if (Array.isArray(evidenceFileArtifacts)) {
    for (const row of evidenceFileArtifacts) {
      if (!row || row.source !== "gap_spoq" && row.source !== "gap_delegate") continue;
      const qid = row.questionId;
      if (!qid) continue;
      const role = row.source === "gap_spoq" ? "spoc" : "delegate";
      pushAtt(qid, { id: row.id, url: row.url, fileName: row.fileName, uploadedAt: row.uploadedAt }, role);
    }
  }
  let totalFiles = 0;
  let questionsWithFiles = 0;
  for (const qid of Object.keys(byQ)) {
    const n = byQ[qid].length;
    if (n > 0) {
      questionsWithFiles += 1;
      totalFiles += n;
    }
  }
  return { byQuestion: byQ, totalFiles, questionsWithFiles };
}
function sortReleasedQuestionsForLa(questions) {
  return [...questions].sort((a, b) => {
    const na = parseInt(String(a.qNo || "").replace(/\D/g, ""), 10);
    const nb = parseInt(String(b.qNo || "").replace(/\D/g, ""), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
    if (!Number.isNaN(na) && Number.isNaN(nb)) return -1;
    if (Number.isNaN(na) && !Number.isNaN(nb)) return 1;
    return String(a.id).localeCompare(String(b.id));
  });
}
const DEL_SUBSTEPS = [
  { key: "acknowledge", label: "Acknowledge Assignment", num: 1 },
  { key: "respond-del", label: "Respond to Questions", num: 2 }
];
export const GapAnalysis = ({ project, user, onAfterGapDashboardPush }) => {
  const { updateProjectProfile, saveScopingProfile, fetchProjectProfile, fetchProjectMembers, updateProjectMembers, createMeeting, updateMeeting, generateAiScores, setCurrentView, runGapAnalysisTechnicalEvaluation, approveGapAnalysisTechnicalEvaluation, fetchNotifications, fetchMeetings, updateMeetingAttendees, uploadGapQuestionFile, deleteGapQuestionFile, openBlobUrl, fetchMilestones, fetchIso27001Controls } = useApp();
  const isLeadAdvisor = user?.role === UserRole.LEAD_ADVISOR || user?.role === UserRole.ADMIN;
  const isAdvisor = user?.role === UserRole.ADVISOR;
  const isSPOC = user?.role === UserRole.COMPANY_SPOC;
  const isDelegate = user?.role === UserRole.COMPANY_DELEGATE;
  /**
   * "Act on behalf of Advisor" — backlog #17 (Setup Tool "Mark Tools as Ready"), #18 (Conduct
   * Assessment) and #21 (Share with Lead Advisor). .claude/design/approval-matrix-clarification.md
   * (R019, "Read B"): the Advisor-only gate on this screen is NOT weakened — a Lead Advisor can
   * instead start an audited impersonation session and complete the Advisor's step through it.
   * Session-polling mirrors LeadAdvisorApprovalMatrixPanel.jsx's identical pattern verbatim:
   * sessionStorage writes don't trigger a re-render on their own, and a session can expire while
   * this screen is open. Backend contract: assertGapAnalysisWizardSaveAllowed
   * (registerRouteHelpers.ts) accepts a `toolSetup` / `techAssessmentDone` / `sharedWithLA` write
   * from an impersonated `advisor` session. AI Evaluation and Review & Validate are ALSO in scope
   * now: `techAiEvalRun` / `techScoresValidated` are backend-persisted facts set only by the real
   * technical-evaluation trigger (invokeAI) and the distinct human-approval route, both of which
   * accept an identity-verified impersonated Advisor session
   * (gapAnalysisTechnicalEvaluation.controller.ts). The LA sees the same observations, AI scores
   * and agreement indicator and the same override inputs as the Advisor, so the approval is made
   * on visible scores. Only a real Lead Advisor can start such a session: platform_admin cannot
   * (and the backend refuses), so the cards are not rendered for it.
   *
   * UPDATED — Engineering Director reconciliation ruling (Devil's Advocate PROCEED-WITH-CONDITIONS
   * finding): each card and its handler ALSO gate on the immediately-preceding step's PERSISTED
   * completion flag, mirroring the real Advisor's own inline button `disabled` conditions, so an
   * LA can never open an impersonation session and land on a doomed no-op write:
   *   - Conduct Assessment (#18) requires `toolSetup` (mirrors the real "Complete Assessment"
   *     button's `!toolSetup` disabled condition verbatim).
   *   - Share with Lead Advisor (#21) requires `techAssessmentDone` — deliberately NOT
   *     `techScoresValidated`/`techAiEvalRun`. This matches the backend companion precondition
   *     on the `sharedWithLA` gate in registerRouteHelpers.ts.
   * Setup Tool (#17) has no prior step in this flow and keeps its original solely-own-flag gate.
   */
  const [activeImpersonationSession, setActiveImpersonationSession] = useState(() => getActiveImpersonation());
  useEffect(() => {
    const tick = () => setActiveImpersonationSession(getActiveImpersonation());
    tick();
    const timer = setInterval(tick, 15000);
    return () => clearInterval(timer);
  }, []);
  const canActOnBehalfOfAdvisor = user?.role === UserRole.LEAD_ADVISOR;
  const advisorImpersonationSessionMatches =
    canActOnBehalfOfAdvisor &&
    !!activeImpersonationSession &&
    activeImpersonationSession.effectiveRole === "advisor" &&
    activeImpersonationSession.projectId === project?.id;
  /**
   * Engineering Director REQUEST-CHANGES (backlog #17 follow-up): the backend's
   * identity-verification gate (assertGapAnalysisWizardSaveAllowed, registerRouteHelpers.ts)
   * rejects a role-generic advisor session (no `effectiveUserId`) for `toolSetup` — so a session
   * missing it must never reach the "Mark Tools as Ready" completion button at all.
   */
  const advisorImpersonationActive =
    advisorImpersonationSessionMatches && !!activeImpersonationSession?.effectiveUserId;
  const advisorImpersonationNeedsNamedUser =
    advisorImpersonationSessionMatches && !activeImpersonationSession?.effectiveUserId;
  const [advisorActOnBehalfOpen, setAdvisorActOnBehalfOpen] = useState(false);
  const [advisorActOnBehalfAssessmentOpen, setAdvisorActOnBehalfAssessmentOpen] = useState(false);
  const [advisorActOnBehalfShareOpen, setAdvisorActOnBehalfShareOpen] = useState(false);
  // Backlog Sheet-1 rows 19/20 — LA "Act on behalf of Advisor" dialogs for the two now-real
  // AI-backed steps, mirroring advisorActOnBehalf{Assessment,Share}Open exactly.
  const [advisorActOnBehalfTechEvalOpen, setAdvisorActOnBehalfTechEvalOpen] = useState(false);
  const [advisorActOnBehalfTechApproveOpen, setAdvisorActOnBehalfTechApproveOpen] = useState(false);
  /**
   * The backend's identity-verification gate on `toolSetup`
   * (assertGapAnalysisWizardSaveAllowed, registerRouteHelpers.ts) rejects a role-generic
   * advisor-impersonation session (no `effectiveUserId`) — the LA must name a specific
   * Advisor project member when starting the session. Fetched only for the LD, so the
   * ImpersonationDialog's "specific user" picker has real Advisor rows to offer.
   */
  const [advisorMembersForActOnBehalf, setAdvisorMembersForActOnBehalf] = useState([]);
  useEffect(() => {
    if (!canActOnBehalfOfAdvisor || !project?.id || !fetchProjectMembers) return;
    let cancelled = false;
    void (async () => {
      const members = await fetchProjectMembers(project.id);
      if (cancelled) return;
      setAdvisorMembersForActOnBehalf((members || []).filter((m) => m.role === "advisor"));
    })();
    return () => {
      cancelled = true;
    };
  }, [canActOnBehalfOfAdvisor, project?.id, fetchProjectMembers]);
  const gw = project?.profile?.gapAnalysisWizard || {};
  /** Commandment VIII: client logo for the printable Gap Analysis Report header, sourced from project.profile (already fetched — no new call). */
  const gapReportCompanyLogoUrl = useMemo(() => resolveCompanyLogoUrl(project), [project]);
  const projectProfileRef = useRef(project?.profile);
  useEffect(() => {
    projectProfileRef.current = project?.profile;
  }, [project?.profile]);
  // AI tool-test summariser (backlog #29, R040) needs a bearer token for the multipart
  // upload — mirrors the getApiToken pattern in ProjectOnboarding.jsx.
  const { instance: gapMsalInstance } = useMsal();
  const gapMsalAccount = gapMsalInstance.getActiveAccount();
  const gapMsalInstanceRef = useRef(gapMsalInstance);
  const gapMsalAccountRef = useRef(gapMsalAccount);
  useEffect(() => {
    gapMsalInstanceRef.current = gapMsalInstance;
  }, [gapMsalInstance]);
  useEffect(() => {
    gapMsalAccountRef.current = gapMsalAccount;
  }, [gapMsalAccount]);
  const getApiToken = useCallback(async () => {
    const tokenResponse = await gapMsalInstanceRef.current.acquireTokenSilent({
      ...apiTokenRequest,
      account: gapMsalAccountRef.current,
    });
    return tokenResponse.accessToken;
  }, []);
  const myDelegateEmail = user?.dbEmail || user?.email || "";
  const myDelegateId = user?.dbUserId || user?.id || myDelegateEmail;
  const myDelegateProgressKey = myDelegateEmail || myDelegateId;
  const myDelegateProgress = gw.delegateProgress?.[myDelegateProgressKey] || gw.delegateProgress?.[myDelegateId] || {};
  const [laSubStep, setLaSubStep] = useState(() => normalizeLaSubStep(gw.laSubStep));
  const [advSubStep, setAdvSubStep] = useState(() => gw.advSubStep || "setup-tool");
  const [spocSubStep, setSpocSubStep] = useState(() => gw.spocSubStep || "respond");
  const [delSubStep, setDelSubStep] = useState(() => initialDelegateGapDelSubStep(gw, myDelegateProgress, myDelegateProgressKey, myDelegateId));
  const [step4Status, setStep4Status] = useState(() => gw.gapAnalysisStatus || "Draft");
  // Under Review or later: the API refuses evidence and technical-evaluation writes (409 GAP_ANALYSIS_LOCKED).
  const gapLocked = isGapAnalysisLocked(step4Status);
  const [questionnaireReleased, setQuestionnaireReleased] = useState(() => !!gw.questionnaireReleased);
  const [interviewsScheduled, setInterviewsScheduled] = useState(false);
  const [scoresValidated, setScoresValidated] = useState(() => !!gw.scoresValidated);
  const [scoresSaved, setScoresSaved] = useState(() => !!gw.scoresSaved);
  const [aiScoresGenerating, setAiScoresGenerating] = useState(false);
  const [validationGenerating, setValidationGenerating] = useState(false);
  const [aiScoresGenerated, setAiScoresGenerated] = useState(() => !!gw.aiScoresGenerated);
  // Task #72 / lesson [[silent-zero-scoring-ai-fallback]]: the ai-scores endpoint returns
  // top-level warnings[]/failedBatches[] on a partial AI failure — Commandment IV requires
  // that fallback rows be distinguishable from legitimate zeros, with a retry path out.
  const [aiScoreWarnings, setAiScoreWarnings] = useState([]);
  const [aiScoreFailedBatches, setAiScoreFailedBatches] = useState([]);
  const [retryingAiScores, setRetryingAiScores] = useState(false);
  const lastAiScorePayloadRef = useRef([]);
  const [reportDrafted, setReportDrafted] = useState(() => !!gw.reportDrafted && !hasLegacyCannedReportText(gw.gapReportDraft));
  const [reportDraftSaving, setReportDraftSaving] = useState(false);
  const [pushedToDashboard, setPushedToDashboard] = useState(() => !!gw.pushedToDashboard);
  const [pushTabIsoControls, setPushTabIsoControls] = useState([]);
  const [pushTabIsoLoading, setPushTabIsoLoading] = useState(false);
  const [pushTabIsoVersion, setPushTabIsoVersion] = useState(null);
  const [pushTabMilestones, setPushTabMilestones] = useState([]);
  const [toolSetup, setToolSetup] = useState(() => !!gw.toolSetup);
  const [techAssessmentDone, setTechAssessmentDone] = useState(() => !!gw.techAssessmentDone);
  // STUB INVENTORY finding #2 fix (SESSION_CHECKLIST.md, 2026-09-30) — real, persisted,
  // tenant/project-scoped records of which assessment tools are actually in scope for this
  // engagement (tool name, scope/target, configuration notes, who set it up, when), replacing
  // the previous four hardcoded "Setup Tool" cards that never reflected any real
  // configuration. Server-enforced via `assertGapAnalysisWizardCompletionRecordsNotFabricated`
  // (registerRouteHelpers.ts) — `toolSetup` cannot become `true` without at least one of these.
  const toolSetupRecords = useMemo(() => (Array.isArray(gw.toolSetupRecords) ? gw.toolSetupRecords : []), [gw.toolSetupRecords]);
  const [newToolName, setNewToolName] = useState("");
  const [newToolScope, setNewToolScope] = useState("");
  const [newToolNotes, setNewToolNotes] = useState("");
  const [addingToolRecord, setAddingToolRecord] = useState(false);
  const [removingToolRecordId, setRemovingToolRecordId] = useState(null);
  // Backlog Sheet-1 rows 19/20 (.claude/lessons/completion-flag-implies-ai-ran-with-no-real-call.md
  // — now fixed): these two flags are real, backend-persisted facts — `techAiEvalRun` is set
  // ONLY by a successful POST .../gap-analysis/technical-evaluation (a real invokeAI() call),
  // and `techScoresValidated` ONLY by the distinct POST .../technical-evaluation/approve human-
  // approval call (Commandment IV — never flipped by the trigger route's AI output alone).
  const [techAiEvalRun, setTechAiEvalRun] = useState(() => gw.techAiEvalRun === true);
  const [techScoresValidated, setTechScoresValidated] = useState(() => gw.techScoresValidated === true);
  const [techEvalRunning, setTechEvalRunning] = useState(false);
  const [techApproving, setTechApproving] = useState(false);
  // Task #74 (Gap A) / lesson [[silent-zero-scoring-ai-fallback]]: mirrors aiScoreWarnings /
  // aiScoreFailedBatches above for the Advisor's technical-scoring path. Populated from the
  // real technical-evaluation response (warnings[]/failedBatches[]) — see
  // `runTechnicalAiEvaluation` below.
  const [techAiWarnings, setTechAiWarnings] = useState(() => (Array.isArray(gw.techAiWarnings) ? gw.techAiWarnings : []));
  const [techFailedBatches, setTechFailedBatches] = useState(() => (Array.isArray(gw.techAiFailedBatches) ? gw.techAiFailedBatches : []));
  const [retryingTechAiScores, setRetryingTechAiScores] = useState(false);
  // Backend contract (ea411e9): the trigger returns techAiEvalFailedRows / techAiEvalPartial; the
  // approve 409 returns error.details.failedRowIds. Both are rows with NO real AI score.
  const [techApproveConflictRows, setTechApproveConflictRows] = useState([]);
  const [techEvaluationId, setTechEvaluationId] = useState(null);
  // A 502/503 from the trigger means NOTHING was saved: shown inline (with the Reference) while Run stays available.
  const [techRunError, setTechRunError] = useState(null);
  const [techApprovalInfo, setTechApprovalInfo] = useState(null);
  // AI tool-test outcome summariser (backlog #29, R040 — Gap Analysis > Conduct Assessment).
  // `toolTestSummaries` are the Advisor-approved entries only (Commandment IV: nothing is
  // written here until an explicit accept); `toolTestModal` holds the in-flight/reviewable
  // draft returned by POST /projects/:id/gap-analysis/tool-test-summary before that approval.
  const [toolTestSummaries, setToolTestSummaries] = useState([]);
  // Unapproved AI drafts the server is holding for this project (reviewable, never counted as evidence).
  const [toolTestDrafts, setToolTestDrafts] = useState([]);
  // Server-reported totals (advisor DTO only) and the keyset cursor for the next page of the main list.
  const [toolTestCounts, setToolTestCounts] = useState(null);
  const [toolTestNextCursor, setToolTestNextCursor] = useState(null);
  const [toolTestLoadingMore, setToolTestLoadingMore] = useState(false);
  // Rejected summaries are fetched on demand with ?status=REJECTED (terminal, never evidence).
  const [toolTestRejected, setToolTestRejected] = useState({ open: false, status: "idle", items: [], nextCursor: null, error: null });
  const toolTestLoadSeq = useRef(0);
  // Which recorded tool (toolSetupRecords entry) the next uploaded output belongs to.
  const [toolTestSelectedRecordId, setToolTestSelectedRecordId] = useState(undefined);
  // Per-tool "no output" form: { toolRecordId, reason, busy, error }
  const [noOutputForm, setNoOutputForm] = useState(null);
  // "Link to a recorded tool" form for one approved, unlinked legacy summary: { summaryId, toolRecordId, reason, busy, error }
  const [linkForm, setLinkForm] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [sourceDownloadBusyId, setSourceDownloadBusyId] = useState(null);
  // Evidence is server-persisted (GET .../tool-test-summaries); the client can no longer write it via the
  // profile PUT. idle | loading | ready | denied | error - distinct renders, not one spinner.
  const [toolTestEvidence, setToolTestEvidence] = useState({ status: "idle", error: null });
  const [toolTestModal, setToolTestModal] = useState(null);
  const toolTestFileInputRef = useRef(null);
  const [toolTestSelectedType, setToolTestSelectedType] = useState("generic");
  // Only Advisor / Lead Advisor / admin may read raw tool-test evidence (the API 403s company roles).
  const isToolTestSpocView = user?.role === UserRole.COMPANY_SPOC;
  // The Company SPOC gets a restricted, APPROVED-only projection (no drafts, emails, model/prompt ids, hash).
  // Delegates remain excluded.
  const canViewToolTestEvidence = user?.role === UserRole.ADVISOR || user?.role === UserRole.LEAD_ADVISOR || user?.role === UserRole.ADMIN || isToolTestSpocView;
  // STUB INVENTORY finding #3 - "Complete Assessment" is enabled only once the SERVER holds at least
  // one Advisor-APPROVED tool-test summary. The client sends just `techAssessmentDone: true`; the
  // server derives `techAssessmentOutcomes` from those approved rows and rejects client-supplied ones.
  // The API stays the source of truth; this mirrors its rule to explain what blocks: >= 1 approved summary,
  // zero unresolved drafts, every recorded tool linked to an approved summary or marked no-output.
  const toolNoOutputMarks = useMemo(() => (Array.isArray(gw.toolNoOutputMarks) ? gw.toolNoOutputMarks : []), [gw.toolNoOutputMarks]);
  const toolEvidenceCompletion = useMemo(
    () => evaluateToolEvidenceCompletion({
      toolRecords: toolSetupRecords,
      marks: toolNoOutputMarks,
      approved: toolTestSummaries,
      drafts: toolTestDrafts,
      counts: toolTestCounts,
      loadStatus: toolTestEvidence.status,
      loadError: toolTestEvidence.error,
    }),
    [toolSetupRecords, toolNoOutputMarks, toolTestSummaries, toolTestDrafts, toolTestCounts, toolTestEvidence],
  );
  const canCompleteAssessment = toolEvidenceCompletion.blockers.length === 0;
  const [sharedWithLA, setSharedWithLA] = useState(() => !!gw.sharedWithLA);
  // Pre-disable (the API still enforces): evidence writes once the assessment is complete or the gap analysis is locked;
  // Reopen while shared with the Lead Advisor or locked (validated scores no longer block it: the server archives them); AI evaluation until the assessment is complete and while locked.
  const evidenceWriteBlocked = evidenceWriteBlockedReason({ techAssessmentDone, gapLocked });
  const reopenBlocked = computeReopenBlockedReason({ sharedWithLA, gapLocked });
  const techEvalBlocked = techEvalBlockedReason({ techAssessmentDone, gapLocked });
  const techHistoryRows = useMemo(() => techEvaluationHistoryRows(gw.techEvaluationHistory), [gw.techEvaluationHistory]);
  // Read-only, server-written archive (Commandments IV / VII): Advisor and Lead Advisor only.
  const techHistoryTampered = project?.techEvaluationHistoryTampered === true;
  const showTechEvaluationHistory = (isAdvisor || isLeadAdvisor) && (techHistoryRows.length > 0 || techHistoryTampered);
  const renderTechEvaluationHistory = () => showTechEvaluationHistory ? /* @__PURE__ */ jsxs("div", {
    "data-testid": "tech-evaluation-history", className: "bg-[var(--color-surface-sunken)] rounded-2xl p-6 border space-y-3",
    children: [
      /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Previous technical evaluations" }),
      techHistoryTampered ? /* @__PURE__ */ jsx("div", { "data-testid": "tech-evaluation-history-tampered", role: "alert", className: "rounded-xl border-2 border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] px-4 py-3 text-[12px] font-bold text-[color:var(--color-danger-fg)]", children: TECH_HISTORY_TAMPERED_TEXT }) : null,
      /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: techHistoryRows.map((h) => /* @__PURE__ */ jsxs("li", {
        "data-testid": "tech-evaluation-history-entry", className: "bg-[var(--color-bg-white)] rounded-xl px-4 py-3 border text-[11px] text-[color:var(--color-text-muted-dark)] space-y-0.5",
        children: [
          /* @__PURE__ */ jsx("p", { className: "font-bold", children: `${h.label} on ${formatDdMmYyyy(h.supersededAt)}${h.supersededBy ? ` by ${h.supersededBy}` : ""}` }),
          h.reason ? /* @__PURE__ */ jsx("p", { children: `Reason: ${h.reason}` }) : null,
          h.humanReason ? /* @__PURE__ */ jsx("p", { "data-testid": "tech-evaluation-history-human-reason", children: `Advisor's reason: ${h.humanReason}` }) : null,
          /* @__PURE__ */ jsx("p", { className: "text-[color:var(--color-text-muted)]", children: `${h.observationCount != null ? `${h.observationCount} observation(s)` : "Observations archived"}${h.scoresValidated ? ", scores had been validated" : ""}` }),
          h.sha256 ? /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] break-all", children: `SHA-256: ${h.sha256}` }) : null,
        ],
      }, h.key)) }),
    ],
  }) : null;
  const linkTargets = useMemo(
    () => linkableToolRecords({ toolRecords: toolSetupRecords, marks: toolNoOutputMarks, approved: toolTestSummaries }),
    [toolSetupRecords, toolNoOutputMarks, toolTestSummaries],
  );
  const toolUploadNeedsTool = toolSetupRecords.length > 0 && !linkTargets.some((r) => r.id === toolTestSelectedRecordId);
  const toolUploadNeedsToolText = linkTargets.length === 0
    ? "Every recorded tool already has an approved summary or is marked as having no output, so there is nothing left to upload."
    : "Pick the recorded tool this output belongs to before uploading. Only tools that still need evidence are listed; a summary not linked to a recorded tool does not count toward completing the assessment.";
  const [delegated, setDelegated] = useState(() => !!gw.delegated);
  const [spocResponded, setSpocResponded] = useState(() => !!gw.spocResponded);
  const [spocWorkComplete, setSpocWorkComplete] = useState(() => gw.spocWorkComplete === true);
  const [delegationFinalized, setDelegationFinalized] = useState(() => gw.delegationFinalized === true);
  const [responsesReviewed, setResponsesReviewed] = useState(() => !!gw.responsesReviewed);
  const [delAcknowledged, setDelAcknowledged] = useState(() => !!myDelegateProgress.acknowledged);
  const [delResponded, setDelResponded] = useState(() => !!myDelegateProgress.submitted);
  const [delAnswers, setDelAnswers] = useState(() => normalizeDelegateAnswersMap(myDelegateProgress.answers));
  const [delNotes, setDelNotes] = useState(() => {
    const n = { ...(myDelegateProgress.notes || {}) };
    const a = myDelegateProgress.answers || {};
    for (const k of Object.keys(a)) {
      const v = a[k];
      if (v && typeof v === "object" && v.notes != null && n[k] == null) n[k] = v.notes;
    }
    return n;
  });
  const [delAttachments, setDelAttachments] = useState(() => extractDelegateAttachmentsFromAnswers(myDelegateProgress.answers));
  const [interviewDate, setInterviewDate] = useState("");
  const [interviewType, setInterviewType] = useState("in_person");
  const [interviewTitle, setInterviewTitle] = useState("");
  const [interviewNotes, setInterviewNotes] = useState("");
  const [meetingDepartmentSelect, setMeetingDepartmentSelect] = useState("");
  const [meetingDepartmentCustom, setMeetingDepartmentCustom] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [meetingLocation, setMeetingLocation] = useState("");
  const [scheduledMeetings, setScheduledMeetings] = useState(() => gw.scheduledMeetings || []);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({});
  const [spocAnswers, setSpocAnswers] = useState(() => gw.spocAnswers || {});
  const mergedSpocAnswers = useMemo(
    () => ({ ...(gw.spocAnswers || {}), ...spocAnswers }),
    [gw.spocAnswers, spocAnswers]
  );
  const [spocDocs, setSpocDocs] = useState(() => gw.spocDocs || {});
  const [spocUploadingQid, setSpocUploadingQid] = useState(null);
  const [delUploadingQid, setDelUploadingQid] = useState(null);
  const [spocNotes, setSpocNotes] = useState(() => gw.spocNotes || {});
  /** Latest draft fields for autosave / unmount flush (navigating to Evidence unmounts this component). */
  const spocAnswersRef = useRef(spocAnswers);
  const spocNotesRef = useRef(spocNotes);
  const spocDocsRef = useRef(spocDocs);
  spocAnswersRef.current = spocAnswers;
  spocNotesRef.current = spocNotes;
  spocDocsRef.current = spocDocs;
  const [delegateQuestionAssign, setDelegateQuestionAssign] = useState({});
  const [assignTarget, setAssignTarget] = useState("");
  const [domainFilter, setDomainFilter] = useState("all");
  const [expandedDelegateCards, setExpandedDelegateCards] = useState({});
  const [spocProjectMeetings, setSpocProjectMeetings] = useState([]);
  const [spocMeetingsLoading, setSpocMeetingsLoading] = useState(false);
  /** True after at least one successful fetch of project meetings (avoids green "meetings" before data exists). */
  const [spocMeetingsHydrated, setSpocMeetingsHydrated] = useState(false);
  const [spocTabProjectMembers, setSpocTabProjectMembers] = useState([]);
  const [spocRosterLoading, setSpocRosterLoading] = useState(false);
  const [spocDelegatePicksByMeeting, setSpocDelegatePicksByMeeting] = useState({});
  const [spocSavingMeetingId, setSpocSavingMeetingId] = useState(null);
  const [spocAwaitingDelegatesCount, setSpocAwaitingDelegatesCount] = useState(0);
  const [spocResponseAnswers, setSpocResponseAnswers] = useState({});
  const [gapReportAnnotations, setGapReportAnnotations] = useState(() => normalizeContractAnnotations(gw.gapReportAnnotations));
  const [gapReportAnnotationFocusKey, setGapReportAnnotationFocusKey] = useState("");
  const [gapReportNewCommentDraft, setGapReportNewCommentDraft] = useState("");
  // Why the last refused/failed save failed (plain text); read once via takeSaveFailureText.
  const lastSaveFailureRef = useRef(null);
  // The 409 details.reason of the last refused save (null when it succeeded or failed another way).
  const lastSaveRefusalReasonRef = useRef(null);
  const takeSaveFailureText = useCallback((fallback) => {
    const t = lastSaveFailureRef.current || fallback;
    lastSaveFailureRef.current = null;
    return t;
  }, []);
  const persistGapWizard = useCallback(async (patch, options = {}) => {
    const { impersonated = false, reopenReason, sendBackReason } = options;
    lastSaveFailureRef.current = null;
    lastSaveRefusalReasonRef.current = null;
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) return false;
    const base = projectProfileRef.current || {};
    const prevWiz = base.gapAnalysisWizard && typeof base.gapAnalysisWizard === "object" ? base.gapAnalysisWizard : {};
    const nextWiz = { ...prevWiz, ...patch };
    const newProfile = { ...base, gapAnalysisWizard: nextWiz };
    projectProfileRef.current = newProfile;
    updateProjectProfile(project.id, newProfile);
    // Server is the source of truth: a refused/failed save must put context, the ref and the UI back
    // on the pre-save snapshot, otherwise refused values render as recorded and ride along on the next save.
    const revert = () => {
      projectProfileRef.current = base;
      updateProjectProfile(project.id, base);
    };
    try {
      let refusal = null;
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile), {
        impersonated,
        reopenReason,
        sendBackReason,
        onError: (err) => {
          refusal = err;
          lastSaveFailureRef.current = gapProfileSaveErrorText(err);
          lastSaveRefusalReasonRef.current = err?.status === 409 ? err.details?.reason || null : null;
        },
      });
      if (!ok) {
        revert();
        // State changed under us (locked, stage no longer approved, assessment completed/reopen refused):
        // the snapshot we just restored may itself be stale, so reload the server's version.
        if (refusal?.status === 409 && COMMIT_RELOAD_REASONS.has(refusal.details?.reason) && fetchProjectProfile) {
          await fetchProjectProfile(project.id);
        }
        return false;
      }
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      return true;
    } catch (e) {
      console.error("Failed to persist gap wizard:", e);
      revert();
      return false;
    }
  }, [project?.id, updateProjectProfile, saveScopingProfile, fetchProjectProfile]);
  /**
   * STUB INVENTORY finding #2 fix (2026-09-30) — the Advisor's real "Setup Tool" record.
   * Persisted immediately (not batched with `toolSetup`; provenance is server-stamped) so a page refresh mid-setup never
   * loses an already-recorded tool. `assertGapAnalysisWizardCompletionRecordsNotFabricated`
   * (registerRouteHelpers.ts) requires `toolName` to be a non-blank string.
   */
  const addToolSetupRecord = useCallback(async () => {
    const toolName = newToolName.trim();
    if (!toolName || addingToolRecord) return;
    setAddingToolRecord(true);
    const record = {
      id: `tool-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      toolName,
      scopeTarget: newToolScope.trim(),
      configNotes: newToolNotes.trim(),
      // No client-side setupBy/setupAt: the server stamps who/when/role; existing records are echoed
      // unchanged (editing in place is rejected - remove and re-add instead).
    };
    const ok = await persistGapWizard({ toolSetupRecords: [...toolSetupRecords, record] });
    if (ok) {
      setNewToolName("");
      setNewToolScope("");
      setNewToolNotes("");
      message.success("Tool recorded.");
    } else {
      message.error(takeSaveFailureText("Failed to record the tool. Please try again."));
    }
    setAddingToolRecord(false);
  }, [newToolName, newToolScope, newToolNotes, addingToolRecord, toolSetupRecords, persistGapWizard, takeSaveFailureText, user]);
  const removeToolSetupRecord = useCallback(
    async (id) => {
      setRemovingToolRecordId(id);
      const ok = await persistGapWizard({ toolSetupRecords: toolSetupRecords.filter((r) => r.id !== id) });
      if (!ok) message.error(takeSaveFailureText("Failed to remove the tool. Please try again."));
      setRemovingToolRecordId(null);
    },
    [toolSetupRecords, persistGapWizard, takeSaveFailureText],
  );
  /**
   * LA "Act on behalf of Advisor" completion — backlog #17, Setup Tool step only. Mirrors
   * OrganizationalSetup.jsx's onSpocApprove*Impersonated shape: (a) not gated on the real
   * `isAdvisor` role, (b) requires a live impersonation session already scoped to `advisor` on
   * THIS project, checked fresh at click time, (c) passes `{ impersonated: true }` so the save
   * carries `X-Impersonation-Id`. Never call this for a real advisor — the inline "Mark Tools
   * as Ready" handler in the advisor track (below) stays their only path, unchanged.
   */
  const markToolsAsReadyOnBehalfOfAdvisor = useCallback(async () => {
    if (toolSetup) return;
    // STUB INVENTORY finding #2 fix (2026-09-30): the server now rejects `toolSetup: true`
    // with no real `toolSetupRecords` behind it — the LA's on-behalf-of banner has no form to
    // collect one, so this must stay blocked (with real guidance) until the Advisor has
    // recorded at least one tool, same shape as the pre-existing toolSetup/session checks
    // below. See `assertGapAnalysisWizardCompletionRecordsNotFabricated` (registerRouteHelpers.ts).
    if (toolSetupRecords.length === 0) {
      message.warning(
        "No assessment tools have been recorded yet. Ask the Advisor to record at least one tool (Setup Tool step) before marking this ready on their behalf.",
      );
      return;
    }
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    setToolSetup(true);
    const ok = await persistGapWizard({ toolSetup: true }, { impersonated: true });
    if (ok) message.success("Tools marked as ready (acting on behalf of Advisor).");
    else {
      setToolSetup(false);
      message.error(takeSaveFailureText("The server did not accept this step as complete."));
    }
  }, [toolSetup, toolSetupRecords, project?.id, persistGapWizard, takeSaveFailureText]);
  /**
   * LA "Act on behalf of Advisor" completion — backlog #18, Conduct Assessment step. Mirrors
   * markToolsAsReadyOnBehalfOfAdvisor verbatim.
   */
  const completeAssessmentOnBehalfOfAdvisor = useCallback(async () => {
    if (techAssessmentDone) return;
    // Engineering Director reconciliation ruling (backlog #18): mirrors the real Advisor's own
    // "Complete Assessment" button's `disabled: !toolSetup || ...` condition (~line 3909) — a
    // doomed write must never leave the browser, not just be rejected by the backend.
    if (!toolSetup) {
      message.warning("Setup Tool must be marked ready first.");
      return;
    }
    // STUB INVENTORY finding #3 fix (2026-09-30): mirrors the toolSetupRecords check in
    // markToolsAsReadyOnBehalfOfAdvisor above — the server now rejects `techAssessmentDone:
    // true` with no real `techAssessmentOutcomes` behind it.
    if (!canCompleteAssessment) {
      message.warning(
        `The assessment cannot be completed yet: ${toolEvidenceCompletion.blockers[0]}${toolEvidenceCompletion.blockers.length > 1 ? ` (+${toolEvidenceCompletion.blockers.length - 1} more)` : ""}`,
      );
      return;
    }
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    setTechAssessmentDone(true);
    const ok = await persistGapWizard({ techAssessmentDone: true }, { impersonated: true });
    if (ok) message.success("Technical assessment marked complete (acting on behalf of Advisor).");
    else {
      setTechAssessmentDone(false);
      message.error(takeSaveFailureText("The server did not accept the assessment as complete."));
    }
  }, [techAssessmentDone, toolSetup, canCompleteAssessment, toolEvidenceCompletion, project?.id, persistGapWizard, takeSaveFailureText]);
  /**
   * LA "Act on behalf of Advisor" completion — backlog #21, Share with Lead Advisor step.
   * Mirrors markToolsAsReadyOnBehalfOfAdvisor verbatim.
   */
  const shareTechnicalScoresOnBehalfOfAdvisor = useCallback(async () => {
    if (sharedWithLA) return;
    // Backend (aa39af9): the `sharedWithLA` gate requires `techAssessmentDone` AND `techScoresValidated`
    // (registerRouteHelpers.ts). Both are checked here so a doomed write never leaves the browser.
    const waivedOnBehalf = !!(gw.techAssessmentWaiver && typeof gw.techAssessmentWaiver.reason === "string");
    if (!techAssessmentDone && !waivedOnBehalf) {
      message.warning("Conduct Assessment must be completed first.");
      return;
    }
    // The API now also requires human-validated technical scores (Review & Validate) before sharing.
    if (!techScoresValidated && !waivedOnBehalf) {
      message.warning(SHARE_NEEDS_VALIDATED_SCORES);
      return;
    }
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    setSharedWithLA(true);
    const ok = await persistGapWizard({ sharedWithLA: true }, { impersonated: true });
    if (ok) message.success("Technical scores shared with Lead Advisor (acting on behalf of Advisor).");
    else {
      setSharedWithLA(false);
      message.error(takeSaveFailureText("The server did not accept sharing the technical scores."));
    }
  }, [sharedWithLA, techAssessmentDone, techScoresValidated, gw.techAssessmentWaiver, project?.id, persistGapWizard, takeSaveFailureText]);
  /**
   * Backlog Sheet-1 row 19 — "Run Technical AI Evaluation" for the REAL, non-impersonated
   * Advisor. Calls the real backend route (POST .../gap-analysis/technical-evaluation — a real
   * invokeAI() call over the Advisor's approved tool-test summaries) instead of locally
   * flipping a flag (.claude/lessons/completion-flag-implies-ai-ran-with-no-real-call.md).
   * `impersonated` lets the LA-on-behalf-of-Advisor variant below reuse this same function.
   */
  const runTechnicalAiEvaluation = useCallback(async ({ impersonated = false, retryFailedOnly = false } = {}) => {
    if (!project?.id || techEvalRunning) return;
    if (techAiEvalRun && !retryFailedOnly) return;
    if (techEvalBlocked) {
      setTechRunError(techEvalBlocked);
      return;
    }
    setTechEvalRunning(true);
    setTechRunError(null);
    if (!retryFailedOnly) {
      setTechAiWarnings([]);
      setTechFailedBatches([]);
    }
    try {
      const failedBefore = retryFailedOnly ? (Array.isArray(gw.techObservations) ? gw.techObservations : []).filter((o) => o && o.aiFailed === true).map((o) => String(o.id)) : [];
      const result = await runGapAnalysisTechnicalEvaluation(project.id, retryFailedOnly ? { impersonated, retryFailedOnly: true } : { impersonated });
      if (result.ok) {
        if (result.data.evaluationId != null) setTechEvaluationId(String(result.data.evaluationId));
        if (retryFailedOnly) {
          // A row that failed and was then scored on retry: the hand-entered scores belong to the old run and the
          // server would ignore them. Drop them and say so, so the Advisor re-enters one only if still needed.
          const stillFailed = new Set((result.data.techAiEvalFailedRows || []).map((r) => String(r && typeof r === "object" ? r.id : r)));
          const recovered = failedBefore.filter((id) => !stillFailed.has(id));
          if (recovered.length > 0) {
            setTechScores((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !recovered.includes(String(id)))));
            message.info(`The AI scored ${recovered.length} retried row${recovered.length === 1 ? "" : "s"}. Scores you entered by hand for ${recovered.length === 1 ? "it" : "them"} were dropped; override again, with a reason, only if the AI score is still wrong.`);
          }
        }
        setTechAiEvalRun(true);
        setTechAiWarnings(result.data.warnings || []);
        setTechFailedBatches(result.data.failedBatches || []);
        setTechApproveConflictRows([]);
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        const partial = result.data.techAiEvalPartial === true || (result.data.techAiEvalFailedRows || []).length > 0;
        const done = impersonated ? " (acting on behalf of Advisor)" : "";
        if (partial) {
          message.warning(`${retryFailedOnly ? "Retry finished" : "Technical AI evaluation finished"} but some rows still have no AI score. Retry them or override both scores before approving.${done}`);
        } else {
          message.success(retryFailedOnly ? `Failed rows re-scored.${done}` : `Technical AI evaluation complete${done}.`);
        }
      } else {
        // 502/503: nothing was saved, so AI is NOT marked as run and Run stays available.
        const text = result.errorMessage || "Technical AI evaluation failed. Please try again.";
        setTechRunError(text);
        message.error(text);
        if (result.status === 409 && fetchProjectProfile) await fetchProjectProfile(project.id);
      }
    } catch (e) {
      console.error("Technical AI evaluation failed:", e);
      const text = "Technical AI evaluation failed. Please try again.";
      setTechRunError(text);
      message.error(text);
    }
    setTechEvalRunning(false);
  }, [project?.id, techAiEvalRun, techEvalRunning, techEvalBlocked, runGapAnalysisTechnicalEvaluation, fetchProjectProfile, gw.techObservations]);
  /** LA "Act on behalf of Advisor" variant of runTechnicalAiEvaluation — mirrors completeAssessmentOnBehalfOfAdvisor's session-validation shape verbatim. */
  const runTechnicalAiEvaluationOnBehalfOfAdvisor = useCallback(async () => {
    if (techAiEvalRun) return;
    if (!techAssessmentDone) {
      message.warning("Conduct Assessment must be completed first.");
      return;
    }
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    await runTechnicalAiEvaluation({ impersonated: true });
  }, [techAiEvalRun, techAssessmentDone, project?.id, runTechnicalAiEvaluation]);
  /** LA "Act on behalf of Advisor" variant of "Retry failed rows" - session re-checked at click time. */
  const retryFailedTechRowsOnBehalfOfAdvisor = useCallback(async () => {
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    await runTechnicalAiEvaluation({ impersonated: true, retryFailedOnly: true });
  }, [project?.id, runTechnicalAiEvaluation]);
  // approveTechnicalAiEvaluation / approveTechnicalAiEvaluationOnBehalfOfAdvisor (Backlog
  // Sheet-1 row 20) are defined further below, right after the `techScores` local override
  // state they read — see there for the full doc comment.
  // --- AI tool-test outcome summariser (backlog #29, R040) ---------------------------------
  // POST .../gap-analysis/tool-test-summary (multipart) creates a server-side AI_DRAFT row and returns
  // its draftId/contentHash/meta. Approval is a separate server call (Commandment IV): the row becomes
  // evidence only when POST .../tool-test-summary/:draftId/approve succeeds for the named Advisor.
  const toolTestBase = project?.id ? `${apiConfig.baseUrl}/api/v1/projects/${project.id}/gap-analysis` : null;
  // Provenance for auditors: hash version, SHA-256 of the original upload, and the retained original (when kept).
  const renderToolTestAuditLine = (t) =>
    /* @__PURE__ */ jsxs("p", { className: "text-[9px] text-[color:var(--color-text-muted)] flex items-center gap-2 flex-wrap", children: [
      t.hashVersion != null ? `Content hash v${t.hashVersion}` : null,
      t.sourceFileSha256 ? ` · original SHA-256 ${String(t.sourceFileSha256).slice(0, 12)}…` : null,
      t.sourceRetained
        ? /* @__PURE__ */ jsx("button", {
            type: "button", disabled: sourceDownloadBusyId === t.id,
            onClick: () => void downloadToolTestSource(t.id, t.sourceFileName),
            className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] disabled:opacity-50",
            children: sourceDownloadBusyId === t.id ? "Downloading…" : "Download original",
          })
        : t.sourceFileSha256 ? " · original not retained" : null,
    ] });
  // Every evidence call goes through here: token, optional impersonation header, JSON envelope, and a
  // typed ApiError (status, details.reason) on refusal so the UI can explain it in plain words.
  const toolTestRequest = useCallback(
    async (path, { method = "GET", body, form, onBehalf = false } = {}) => {
      let res;
      let json = {};
      try {
        const token = await getApiToken();
        const headers = { Authorization: `Bearer ${token}`, ...(onBehalf ? impersonationHeaders() : {}) };
        if (body !== undefined) headers["Content-Type"] = "application/json";
        res = await fetch(`${toolTestBase}${path}`, { method, headers, body: form || (body !== undefined ? JSON.stringify(body) : undefined) });
        json = await res.json().catch(() => ({}));
      } catch (err) {
        throw toApiError(err);
      }
      if (!res.ok || !json.success) throw apiErrorFromResponse(res, json);
      return json.data || {};
    },
    [toolTestBase, getApiToken],
  );
  // An identity-verified Lead Advisor "act on behalf of Advisor" session carries X-Impersonation-Id.
  const isOnBehalfSession = useCallback(() => {
    const session = getActiveImpersonation();
    return !!(session && session.effectiveRole === "advisor" && session.projectId === project?.id);
  }, [project?.id]);
  const loadToolTestSummaries = useCallback(async () => {
    if (!toolTestBase) return;
    const seq = ++toolTestLoadSeq.current;
    setToolTestEvidence((prev) => ({ status: prev.status === "ready" ? "ready" : "loading", error: null }));
    try {
      const data = await toolTestRequest(`/tool-test-summaries${isToolTestSpocView ? "?status=APPROVED" : ""}`);
      if (seq !== toolTestLoadSeq.current) return;
      // The SPOC's list is empty by design until the Lead Advisor presents the report: not an error.
      if (isToolTestSpocView && data.available === false) {
        setToolTestSummaries([]);
        setToolTestDrafts([]);
        setToolTestCounts(null);
        setToolTestNextCursor(null);
        setToolTestEvidence({ status: "unavailable", error: null });
        return;
      }
      const items = Array.isArray(data.items) ? data.items : [];
      setToolTestSummaries(items.filter((i) => i.status === "APPROVED"));
      setToolTestDrafts(isToolTestSpocView ? [] : items.filter((i) => i.status === "AI_DRAFT"));
      setToolTestCounts(data.counts && !isToolTestSpocView ? data.counts : null);
      setToolTestNextCursor(data.nextCursor || null);
      setToolTestEvidence({ status: "ready", error: null });
    } catch (err) {
      if (seq !== toolTestLoadSeq.current) return;
      const text = gapEvidenceErrorText(err, "Could not load tool-test summaries.");
      setToolTestEvidence({ status: err?.status === 403 ? "denied" : "error", error: text });
    }
  }, [toolTestBase, toolTestRequest, isToolTestSpocView]);
  // The SPOC's availability flips when the gap analysis enters / leaves review (status only: pushedToDashboard never unlocks it);
  // the server's `available` / `reason` is the sole authority, so re-ask it on a status change.
  const spocPresentationKey = isToolTestSpocView ? step4Status : "";
  useEffect(() => {
    if (canViewToolTestEvidence) void loadToolTestSummaries();
  }, [canViewToolTestEvidence, loadToolTestSummaries, spocPresentationKey]);
  // Follow nextCursor ("Load more"): appends the next keyset page; a reload in between discards it.
  const loadMoreToolTestSummaries = useCallback(async () => {
    if (!toolTestNextCursor || toolTestLoadingMore) return;
    const seq = toolTestLoadSeq.current;
    setToolTestLoadingMore(true);
    try {
      const qs = new URLSearchParams();
      if (isToolTestSpocView) qs.set("status", "APPROVED");
      qs.set("cursor", toolTestNextCursor);
      const data = await toolTestRequest(`/tool-test-summaries?${qs.toString()}`);
      if (seq !== toolTestLoadSeq.current) return;
      const items = Array.isArray(data.items) ? data.items : [];
      setToolTestSummaries((prev) => [...prev, ...items.filter((i) => i.status === "APPROVED" && !prev.some((p) => p.id === i.id))]);
      if (!isToolTestSpocView) setToolTestDrafts((prev) => [...prev, ...items.filter((i) => i.status === "AI_DRAFT" && !prev.some((p) => p.id === i.id))]);
      if (data.counts && !isToolTestSpocView) setToolTestCounts(data.counts);
      setToolTestNextCursor(data.nextCursor || null);
    } catch (err) {
      message.error(gapEvidenceErrorText(err, "Could not load more tool-test summaries."));
    } finally {
      setToolTestLoadingMore(false);
    }
  }, [toolTestNextCursor, toolTestLoadingMore, toolTestRequest, isToolTestSpocView]);
  const loadRejectedToolTestSummaries = useCallback(
    async ({ more = false } = {}) => {
      if (isToolTestSpocView) return;
      setToolTestRejected((prev) => ({ ...prev, open: true, status: "loading", error: null }));
      try {
        const qs = new URLSearchParams({ status: "REJECTED" });
        if (more && toolTestRejected.nextCursor) qs.set("cursor", toolTestRejected.nextCursor);
        const data = await toolTestRequest(`/tool-test-summaries?${qs.toString()}`);
        const items = Array.isArray(data.items) ? data.items : [];
        setToolTestRejected((prev) => ({
          open: true,
          status: "ready",
          items: more ? [...prev.items, ...items.filter((i) => !prev.items.some((p) => p.id === i.id))] : items,
          nextCursor: data.nextCursor || null,
          error: null,
        }));
        if (data.counts) setToolTestCounts(data.counts);
      } catch (err) {
        setToolTestRejected((prev) => ({ ...prev, open: true, status: "error", error: gapEvidenceErrorText(err, "Could not load rejected summaries.") }));
      }
    },
    [toolTestRequest, toolTestRejected.nextCursor, isToolTestSpocView],
  );
  // Reload the main list, and the rejected list too when it is open (a reject just added to it).
  const refreshToolTestEvidence = useCallback(async () => {
    await loadToolTestSummaries();
    if (toolTestRejected.open) await loadRejectedToolTestSummaries();
  }, [loadToolTestSummaries, loadRejectedToolTestSummaries, toolTestRejected.open]);
  const openToolTestDraft = useCallback((dto) => {
    setToolTestModal({
      loading: false,
      error: null,
      approveError: null,
      approving: false,
      rejecting: false,
      rejectOpen: false,
      rejectReason: "",
      draftId: dto.id,
      toolType: dto.toolType,
      toolRecordId: dto.toolRecordId || null,
      summary: {
        severity: dto.severity,
        summary: dto.summary || [],
        mapped_controls: dto.mapped_controls || [],
        recommended_next_steps: dto.recommended_next_steps || [],
      },
      warnings: Array.isArray(dto.warnings) ? dto.warnings : [],
      meta: dto.meta || null,
    });
  }, []);
  const summariseToolTestFile = useCallback(
    async (file, toolType, toolRecordId) => {
      if (!project?.id) {
        message.warning("No project selected.");
        return;
      }
      setToolTestModal({ loading: true, error: null, toolType, toolRecordId: toolRecordId || null, summary: null, warnings: [], meta: null, draftId: null });
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("toolType", toolType);
        if (toolRecordId) fd.append("toolRecordId", toolRecordId);
        const data = await toolTestRequest("/tool-test-summary", { method: "POST", form: fd });
        const draft = data.draft || null;
        const draftId = data.draftId || draft?.id || null;
        const summary =
          data.summary ||
          (draft
            ? {
                severity: draft.severity,
                summary: draft.summary || [],
                mapped_controls: draft.mapped_controls || [],
                recommended_next_steps: draft.recommended_next_steps || [],
              }
            : null);
        setToolTestModal((prev) =>
          prev
            ? {
                ...prev,
                loading: false,
                draftId,
                rejectOpen: false,
                rejectReason: "",
                summary,
                warnings: Array.isArray(data.warnings) ? data.warnings : Array.isArray(draft?.warnings) ? draft.warnings : [],
                legacyUnlinkedWarning: data.legacyUnlinked === true && typeof data.legacyUnlinkedWarning === "string" ? data.legacyUnlinkedWarning : null,
                meta: { ...(draft?.meta || {}), ...(data.meta || {}) },
              }
            : prev,
        );
        void loadToolTestSummaries();
      } catch (err) {
        console.warn("Tool-test summary failed", err);
        // DRAFT_CAP_REACHED / stage-lock 409s get plain text; other errors keep the server's sentence.
        setToolTestModal((prev) =>
          prev ? { ...prev, loading: false, error: gapEvidenceErrorText(err, "Tool-test summary failed.") } : prev,
        );
      }
    },
    [project?.id, toolTestRequest, loadToolTestSummaries],
  );
  const closeToolTestModal = useCallback(() => setToolTestModal(null), []);
  const acceptToolTestSummary = useCallback(async () => {
    if (!toolTestModal?.draftId || toolTestModal.approving || toolTestModal.rejecting) return;
    const draftId = toolTestModal.draftId;
    setToolTestModal((prev) => (prev ? { ...prev, approving: true, approveError: null } : prev));
    try {
      await toolTestRequest(`/tool-test-summary/${encodeURIComponent(draftId)}/approve`, { method: "POST", onBehalf: isOnBehalfSession() });
      setToolTestModal(null);
      await refreshToolTestEvidence();
      message.success("Tool-test summary approved and added to the gap report.");
    } catch (err) {
      const text =
        err?.status === 403
          ? "Only the project's named Advisor can approve this (not a company role, an admin, or a Lead Advisor without an identity-verified Advisor session)."
          : err?.status === 404
            ? "This draft no longer exists on the server."
            : gapEvidenceErrorText(err, "The server did not approve this summary.");
      setToolTestModal((prev) => (prev ? { ...prev, approving: false, approveError: text } : prev));
      message.error(text);
      if (err?.status === 409 || err?.status === 404) void refreshToolTestEvidence();
    }
  }, [toolTestModal, toolTestRequest, isOnBehalfSession, refreshToolTestEvidence]);
  // Reject is terminal: the draft can never be approved afterwards, and the reason is mandatory.
  const rejectToolTestSummary = useCallback(async () => {
    if (!toolTestModal?.draftId || toolTestModal.approving || toolTestModal.rejecting) return;
    const reason = (toolTestModal.rejectReason || "").trim();
    if (!reason) return;
    const draftId = toolTestModal.draftId;
    setToolTestModal((prev) => (prev ? { ...prev, rejecting: true, approveError: null } : prev));
    try {
      await toolTestRequest(`/tool-test-summary/${encodeURIComponent(draftId)}/reject`, { method: "POST", body: { reason }, onBehalf: isOnBehalfSession() });
      setToolTestModal(null);
      await refreshToolTestEvidence();
      message.success("Tool-test summary rejected. It will not count as evidence.");
    } catch (err) {
      const text =
        err?.status === 403
          ? "Only the project's named Advisor can reject this (not a company role, an admin, or a Lead Advisor without an identity-verified Advisor session)."
          : err?.status === 404
            ? "This draft no longer exists on the server."
            : gapEvidenceErrorText(err, "The server did not reject this summary.");
      setToolTestModal((prev) => (prev ? { ...prev, rejecting: false, approveError: text } : prev));
      message.error(text);
      if (err?.status === 409 || err?.status === 404) void refreshToolTestEvidence();
    }
  }, [toolTestModal, toolTestRequest, isOnBehalfSession, refreshToolTestEvidence]);
  const markToolNoOutput = useCallback(async () => {
    if (!noOutputForm || noOutputForm.busy) return;
    const reason = (noOutputForm.reason || "").trim();
    if (!reason) return;
    const { toolRecordId } = noOutputForm;
    setNoOutputForm((prev) => (prev ? { ...prev, busy: true, error: null } : prev));
    try {
      await toolTestRequest(`/tool-record/${encodeURIComponent(toolRecordId)}/no-output`, { method: "POST", body: { reason }, onBehalf: isOnBehalfSession() });
      setNoOutputForm(null);
      // The mark lives in the persisted wizard (gapAnalysisWizard.toolNoOutputMarks): re-fetch the profile.
      if (fetchProjectProfile && project?.id) await fetchProjectProfile(project.id);
      await refreshToolTestEvidence();
      message.success("Recorded tool marked as having no output.");
    } catch (err) {
      const text = err?.status === 403 ? "Only the project's named Advisor can mark a tool as having no output." : gapEvidenceErrorText(err, "The server did not record this.");
      setNoOutputForm((prev) => (prev ? { ...prev, busy: false, error: text } : prev));
      if (err?.status === 409) {
        if (fetchProjectProfile && project?.id) void fetchProjectProfile(project.id);
        void refreshToolTestEvidence();
      }
    }
  }, [noOutputForm, toolTestRequest, isOnBehalfSession, fetchProjectProfile, project?.id, refreshToolTestEvidence]);
  // Legacy repair: link an already-APPROVED summary to a recorded tool (once per summary, reason required, append-only history).
  const linkToolTestSummary = useCallback(async () => {
    if (!linkForm || linkForm.busy) return;
    const reason = (linkForm.reason || "").trim();
    if (!linkForm.toolRecordId || !reason) return;
    const { summaryId, toolRecordId } = linkForm;
    setLinkForm((prev) => (prev ? { ...prev, busy: true, error: null } : prev));
    try {
      await toolTestRequest(`/tool-test-summary/${encodeURIComponent(summaryId)}/link`, { method: "POST", body: { toolRecordId, reason }, onBehalf: isOnBehalfSession() });
      setLinkForm(null);
      await refreshToolTestEvidence();
      message.success("Summary linked to the recorded tool.");
    } catch (err) {
      const text = err?.status === 403 ? "Only the project's named Advisor can link a summary to a recorded tool." : gapEvidenceErrorText(err, "The server did not link this summary.");
      setLinkForm((prev) => (prev ? { ...prev, busy: false, error: text } : prev));
      if (err?.status === 409 || err?.status === 404) {
        if (fetchProjectProfile && project?.id) void fetchProjectProfile(project.id);
        void refreshToolTestEvidence();
      }
    }
  }, [linkForm, toolTestRequest, isOnBehalfSession, fetchProjectProfile, project?.id, refreshToolTestEvidence]);
  // Reopen the completed assessment. Optimistic like Complete; a refusal (409 REOPEN_BLOCKED_* / GAP_ANALYSIS_LOCKED) puts
  // the flag, context and ref back on the pre-save snapshot (persistGapWizard) and reloads the profile.
  // Technical-assessment prerequisite: a completed AND validated assessment, or the Advisor's audited waiver.
  // The API refuses (409 TECH_ASSESSMENT_REQUIRED) regardless; this only explains it up front.
  const techWaiver = gw.techAssessmentWaiver && typeof gw.techAssessmentWaiver === "object" && typeof gw.techAssessmentWaiver.reason === "string" ? gw.techAssessmentWaiver : null;
  const techPrereqMet = techAssessmentPrerequisiteMet({ techAssessmentDone, techScoresValidated, waived: !!techWaiver });
  const [waiverDialog, setWaiverDialog] = useState(null); // { mode: "set" | "revoke", reason, busy, error }
  const submitWaiver = useCallback(async () => {
    if (!waiverDialog || waiverDialog.busy || !project?.id) return;
    const reason = (waiverDialog.reason || "").trim();
    if (waiverDialog.mode === "set" && reason.length < TECH_WAIVER_MIN_REASON_LENGTH) return;
    setWaiverDialog((d) => (d ? { ...d, busy: true, error: null } : d));
    try {
      await toolTestRequest("/technical-assessment-waiver", {
        method: waiverDialog.mode === "set" ? "POST" : "DELETE",
        body: reason ? { reason } : {},
        onBehalf: isOnBehalfSession(),
      });
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      setWaiverDialog(null);
      message.success(waiverDialog.mode === "set" ? "The technical assessment is marked not applicable." : "The technical assessment is required again.");
    } catch (err) {
      const text = gapEvidenceErrorText(err, "The server did not record this.");
      setWaiverDialog((d) => (d ? { ...d, busy: false, error: text } : d));
      message.error(text);
      if (err?.status === 409 && fetchProjectProfile) await fetchProjectProfile(project.id);
    }
  }, [waiverDialog, project?.id, toolTestRequest, isOnBehalfSession, fetchProjectProfile]);
  // SPOC send-back (Under Review -> Draft): a reason is required once validated scores or a pushed snapshot would be superseded.
  const [sendBackDialog, setSendBackDialog] = useState(null); // { reason, busy, error, serverRequired }
  const sendBackReasonRequired = techScoresValidated || pushedToDashboard || !!gw.gapDashboardSnapshot || sendBackDialog?.serverRequired === true;
  const sendBackToDraft = useCallback(async () => {
    if (!sendBackDialog || sendBackDialog.busy) return;
    const reason = (sendBackDialog.reason || "").trim();
    if (sendBackReasonRequired && !reason) return;
    setSendBackDialog((d) => (d ? { ...d, busy: true, error: null } : d));
    setStep4Status("Draft");
    const ok = await persistGapWizard({ gapAnalysisStatus: "Draft" }, reason ? { sendBackReason: reason } : {});
    if (!ok) {
      setStep4Status("Under Review");
      const serverRequired = lastSaveRefusalReasonRef.current === "SEND_BACK_REASON_REQUIRED";
      const text = takeSaveFailureText("The server did not accept sending the gap analysis back. Please try again.");
      setSendBackDialog((d) => (d ? { ...d, busy: false, error: text, serverRequired: d.serverRequired || serverRequired } : d));
      message.error(text);
      return;
    }
    setSendBackDialog(null);
  }, [sendBackDialog, sendBackReasonRequired, persistGapWizard, takeSaveFailureText]);
  // D6: Complete Assessment freezes the evidence, so it asks first.
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const completeAssessment = useCallback(async () => {
    setCompleteDialogOpen(false);
    setTechAssessmentDone(true);
    const ok = await persistGapWizard({ techAssessmentDone: true });
    if (!ok) { setTechAssessmentDone(false); message.error(takeSaveFailureText("The server did not accept the assessment as complete. It requires at least one Advisor-approved tool-test summary.")); }
  }, [persistGapWizard, takeSaveFailureText]);
  // D12: the Advisor can take back a share while the gap analysis is still in Draft (the API allows the Advisor to clear sharedWithLA).
  const [withdrawDialog, setWithdrawDialog] = useState(null);
  const withdrawShare = useCallback(async () => {
    if (!sharedWithLA || withdrawDialog?.busy) return;
    setWithdrawDialog({ busy: true, error: null });
    setSharedWithLA(false);
    const ok = await persistGapWizard({ sharedWithLA: false });
    if (!ok) {
      setSharedWithLA(true);
      const text = takeSaveFailureText("The server did not accept withdrawing the share. The Lead Advisor may already have started on the technical scores, or the gap analysis has moved on.");
      setWithdrawDialog({ busy: false, error: text });
      message.error(text);
      return;
    }
    setWithdrawDialog(null);
    message.success("Share withdrawn. You can now reopen the assessment if the evidence needs to change.");
  }, [sharedWithLA, withdrawDialog, persistGapWizard, takeSaveFailureText]);
  const renderTechWaiverPanel = () => {
    const canChange = isAdvisor && step4Status === "Draft";
    if (!techWaiver && !(canChange && !(techAssessmentDone && techScoresValidated))) return null;
    return /* @__PURE__ */ jsxs("div", {
      "data-testid": "tech-waiver-panel",
      className: `rounded-2xl border px-5 py-4 space-y-2 ${techWaiver ? "border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)]" : "border-[color:var(--color-border)] bg-[var(--color-surface-sunken)]"}`,
      children: [
        techWaiver
          ? /* @__PURE__ */ jsxs("div", { "data-testid": "tech-waiver-summary", className: "space-y-1 text-[11px] text-[color:var(--color-warn-fg)]", children: [
              /* @__PURE__ */ jsx("p", { className: "font-black uppercase tracking-widest text-[10px]", children: "Technical assessment marked not applicable" }),
              /* @__PURE__ */ jsx("p", { children: `By ${techWaiver.by?.email || "the Advisor"} on ${formatDdMmYyyy(techWaiver.at)}.` }),
              /* @__PURE__ */ jsx("p", { children: `Reason: ${techWaiver.reason}` }),
            ] })
          : /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[color:var(--color-text-muted-dark)]", children: "If the technical assessment does not apply to this engagement, record why. This is audited, and the report then goes on without technical scores." }),
        canChange && (techWaiver
          ? /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "tech-waiver-revoke", onClick: () => setWaiverDialog({ mode: "revoke", reason: "", busy: false, error: null }), className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border-strong)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]", children: "Restore technical assessment" })
          : /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "tech-waiver-open", onClick: () => setWaiverDialog({ mode: "set", reason: "", busy: false, error: null }), className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border-strong)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]", children: "Technical assessment not applicable" })),
        /* @__PURE__ */ jsx(Modal, {
          open: !!waiverDialog, maskClosable: false, closable: !waiverDialog?.busy,
          title: waiverDialog?.mode === "revoke" ? "Restore the technical assessment?" : "Mark the technical assessment not applicable?",
          onCancel: () => { if (!waiverDialog?.busy) setWaiverDialog(null); },
          footer: /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
            /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "tech-waiver-cancel", disabled: !!waiverDialog?.busy, onClick: () => setWaiverDialog(null), className: "px-4 py-2 border border-[color:var(--color-border)] rounded-lg text-xs font-bold disabled:opacity-50", children: "Cancel" }),
            /* @__PURE__ */ jsx("button", {
              type: "button", "data-testid": "tech-waiver-confirm", onClick: () => void submitWaiver(),
              disabled: !!waiverDialog?.busy || (waiverDialog?.mode === "set" && (waiverDialog?.reason || "").trim().length < TECH_WAIVER_MIN_REASON_LENGTH),
              className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed",
              children: waiverDialog?.busy ? "Saving\u2026" : waiverDialog?.mode === "revoke" ? "Restore" : "Mark not applicable",
            }),
          ] }),
          children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 text-sm text-[color:var(--color-text-muted-dark)]", children: [
            /* @__PURE__ */ jsx("p", { children: waiverDialog?.mode === "revoke"
              ? "The technical assessment becomes required again before the gap analysis can be sent for review, shared or pushed."
              : "The report can then be sent for review, shared and pushed without technical scores. Who, when and why is recorded in the audit log and cannot be edited." }),
            waiverDialog?.error ? /* @__PURE__ */ jsx("p", { "data-testid": "tech-waiver-error", role: "alert", className: "text-[color:var(--color-danger-fg)] font-medium", children: waiverDialog.error }) : null,
            /* @__PURE__ */ jsx("label", { htmlFor: "tech-waiver-reason", className: "block text-xs font-bold", children: waiverDialog?.mode === "revoke" ? "Reason (optional)" : `Reason (required, at least ${TECH_WAIVER_MIN_REASON_LENGTH} characters)` }),
            /* @__PURE__ */ jsx("textarea", {
              id: "tech-waiver-reason", "data-testid": "tech-waiver-reason", rows: 3, maxLength: 1000, value: waiverDialog?.reason || "", disabled: !!waiverDialog?.busy,
              onChange: (e) => setWaiverDialog((d) => (d ? { ...d, reason: e.target.value } : d)),
              className: "w-full border border-[color:var(--color-border)] rounded-lg p-2 text-sm",
            }),
          ] }),
        }),
      ],
    });
  };
  // D15: after a push the user sees the result and chooses where to go next.
  const [pushResultOpen, setPushResultOpen] = useState(false);
  // D17: the Lead Advisor re-pushes updated data (false then true: the API computes a new snapshot on each false -> true).
  const [repushDialog, setRepushDialog] = useState(null);
  const repushToDashboard = useCallback(async () => {
    if (repushDialog?.busy) return;
    setRepushDialog({ busy: true, error: null });
    const cleared = await persistGapWizard({ pushedToDashboard: false });
    if (!cleared) {
      const text = takeSaveFailureText("The server did not accept the updated push. The dashboard still shows the previous snapshot.");
      setRepushDialog({ busy: false, error: text });
      message.error(text);
      return;
    }
    setPushedToDashboard(false);
    const pushed = await persistGapWizard({ pushedToDashboard: true });
    if (!pushed) {
      setPushedToDashboard(false);
      const text = takeSaveFailureText("The server did not accept the updated push.");
      const full = `${text} The dashboard is not showing the gap data right now; use Push to Dashboard to try again.`;
      setRepushDialog({ busy: false, error: full });
      message.error(full);
      return;
    }
    setPushedToDashboard(true);
    setRepushDialog(null);
    setPushResultOpen(true);
    message.success("Updated data pushed to the dashboard.");
  }, [repushDialog, persistGapWizard, takeSaveFailureText]);
  const [reopenDialog, setReopenDialog] = useState(null);
  const reopenNeedsReason = reopenReasonRequired({ techScoresValidated });
  const openReopenDialog = useCallback(() => {
    if (!techAssessmentDone || reopening || reopenBlocked) return;
    setReopenDialog({ reason: "", error: null });
  }, [techAssessmentDone, reopening, reopenBlocked]);
  const reopenAssessment = useCallback(async (rawReason) => {
    if (!techAssessmentDone || reopening || reopenBlocked) return;
    const reopenReason = normalisedReopenReason(rawReason);
    if (reopenNeedsReason && (!reopenReason || reopenReason.length < REOPEN_REASON_MIN_LENGTH)) return;
    setReopening(true);
    const hadEvaluation = techAiEvalRun || techScoresValidated || (Array.isArray(gw.techObservations) && gw.techObservations.length > 0);
    setTechAssessmentDone(false);
    // persistGapWizard refetches the profile on success, which carries the server-cleared observations / history.
    const ok = await persistGapWizard({ techAssessmentDone: false }, reopenReason ? { reopenReason } : {});
    if (!ok) {
      setTechAssessmentDone(true);
      const text = takeSaveFailureText("The server did not reopen the assessment. Please try again.");
      if (lastSaveRefusalReasonRef.current === "REOPEN_REASON_REQUIRED") {
        // The server archives validated scores and insists on a reason: put the modal back with the message.
        setReopenDialog({ reason: reopenReason || "", error: text });
      } else {
        setReopenDialog(null);
        message.error(text);
      }
    } else {
      setReopenDialog(null);
      message.success(hadEvaluation ? REOPENED_WITH_EVALUATION_TEXT : REOPENED_TEXT);
    }
    setReopening(false);
  }, [techAssessmentDone, reopening, reopenBlocked, reopenNeedsReason, techAiEvalRun, techScoresValidated, gw.techObservations, persistGapWizard, takeSaveFailureText]);
  // Auditor download of the original upload. The API re-hashes the bytes: 409 = no longer matches its SHA-256.
  const downloadToolTestSource = useCallback(
    async (draftId, fileName) => {
      if (sourceDownloadBusyId) return;
      setSourceDownloadBusyId(draftId);
      try {
        const token = await getApiToken();
        const res = await fetch(`${toolTestBase}/tool-test-summary/${encodeURIComponent(draftId)}/source`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          if (res.status === 409) {
            message.error("The stored original no longer matches the SHA-256 fingerprint recorded for it, so it cannot be trusted as audit evidence. Report this to your platform administrator.");
          } else if (res.status === 502) {
            message.error("The original could not be retrieved right now. Please try again in a moment.");
          } else if (res.status === 404) {
            message.error("No original was retained for this summary.");
          } else {
            const json = await res.json().catch(() => ({}));
            message.error(userMessageForError(apiErrorFromResponse(res, json), "Could not download the original."));
          }
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "tool-output.txt";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        message.error(userMessageForError(err, "Could not download the original."));
      } finally {
        setSourceDownloadBusyId(null);
      }
    },
    [toolTestBase, getApiToken, sourceDownloadBusyId],
  );
  const handleToolTestFileSelected = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      // An upload not linked to a recorded tool counts for nothing, so once tools exist one must be chosen,
      // and it must still be one that needs evidence (not already evidenced, not marked no output).
      if (toolSetupRecords.length > 0 && !linkTargets.some((r) => r.id === toolTestSelectedRecordId)) {
        message.warning("Choose the recorded tool this output belongs to before uploading.");
        return;
      }
      void summariseToolTestFile(file, toolTestSelectedType, toolTestSelectedRecordId);
    },
    [summariseToolTestFile, toolTestSelectedType, toolTestSelectedRecordId, toolSetupRecords, linkTargets],
  );
  const setGapAnalysisReviewStatus = useCallback(
    async (status) => {
      setStep4Status(status);
      const ok = await persistGapWizard({ gapAnalysisStatus: status });
      if (!ok) {
        // Refused (e.g. 409 CONCURRENT_CHANGE on a send-back): persistGapWizard already restored/reloaded the
        // profile and the status effect follows it; only the reason needs saying.
        message.error(takeSaveFailureText("The server did not accept this status change. Please try again."));
      }
    },
    [persistGapWizard, takeSaveFailureText]
  );
  /** Finalize report: persist status and land on Report tab (Lead Advisor). */
  const finalizeLeadGapReport = useCallback(() => {
    setStep4Status("Finalized");
    setLaSubStep("report");
    message.success("Report finalized.");
    void persistGapWizard({ gapAnalysisStatus: "Finalized", laSubStep: "report" });
  }, [persistGapWizard]);
  const persistGapReportAnnotations = useCallback(
    async (nextList) => {
      setGapReportAnnotations(nextList);
      await persistGapWizard({ gapReportAnnotations: nextList });
    },
    [persistGapWizard]
  );

  const prevGwLaSubStepRef = useRef(gw.laSubStep);
  const prevGwSpocSubStepRef = useRef(gw.spocSubStep);
  const prevGwAdvSubStepRef = useRef(gw.advSubStep);
  const prevGwDelegateGapDelSubRef = useRef(
    gw.delegateProgress?.[myDelegateProgressKey]?.gapDelSubStep ?? gw.delegateProgress?.[myDelegateId]?.gapDelSubStep
  );
  const skipSubStepPersistRef = useRef({ la: false, adv: false, spoc: false, del: false });

  /** When profile loads or refetches, apply server sub-step without fighting in-flight local edits (ref detects real server changes). */
  useEffect(() => {
    if (!isLeadAdvisor) return;
    if (gw.laSubStep === prevGwLaSubStepRef.current) return;
    prevGwLaSubStepRef.current = gw.laSubStep;
    if (gw.laSubStep == null) return;
    skipSubStepPersistRef.current.la = true;
    setLaSubStep(normalizeLaSubStep(gw.laSubStep));
  }, [gw.laSubStep, isLeadAdvisor]);

  useEffect(() => {
    if (!isSPOC) return;
    if (gw.spocSubStep === prevGwSpocSubStepRef.current) return;
    prevGwSpocSubStepRef.current = gw.spocSubStep;
    if (gw.spocSubStep == null) return;
    skipSubStepPersistRef.current.spoc = true;
    setSpocSubStep(gw.spocSubStep);
  }, [gw.spocSubStep, isSPOC]);

  useEffect(() => {
    if (!isAdvisor) return;
    if (gw.advSubStep === prevGwAdvSubStepRef.current) return;
    prevGwAdvSubStepRef.current = gw.advSubStep;
    if (gw.advSubStep == null) return;
    skipSubStepPersistRef.current.adv = true;
    setAdvSubStep(gw.advSubStep);
  }, [gw.advSubStep, isAdvisor]);

  const gwDelegateGapDelStored =
    gw.delegateProgress?.[myDelegateProgressKey]?.gapDelSubStep ?? gw.delegateProgress?.[myDelegateId]?.gapDelSubStep;

  useEffect(() => {
    if (!isDelegate) return;
    if (gwDelegateGapDelStored === prevGwDelegateGapDelSubRef.current) return;
    prevGwDelegateGapDelSubRef.current = gwDelegateGapDelStored;
    if (gwDelegateGapDelStored == null) return;
    skipSubStepPersistRef.current.del = true;
    setDelSubStep(gwDelegateGapDelStored);
  }, [gwDelegateGapDelStored, isDelegate]);

  /** Persist sub-tab when it differs from server (covers navigation + reload restoring from profile). */
  useEffect(() => {
    if (!isLeadAdvisor || !project?.id) return;
    if (skipSubStepPersistRef.current.la) {
      skipSubStepPersistRef.current.la = false;
      return;
    }
    const server = normalizeLaSubStep(gw.laSubStep);
    if (laSubStep === server) return;
    void persistGapWizard({ laSubStep });
  }, [laSubStep, gw.laSubStep, isLeadAdvisor, project?.id, persistGapWizard]);

  useEffect(() => {
    if (!isAdvisor || !project?.id) return;
    if (skipSubStepPersistRef.current.adv) {
      skipSubStepPersistRef.current.adv = false;
      return;
    }
    const server = gw.advSubStep || "setup-tool";
    if (advSubStep === server) return;
    void persistGapWizard({ advSubStep });
  }, [advSubStep, gw.advSubStep, isAdvisor, project?.id, persistGapWizard]);

  useEffect(() => {
    if (!isSPOC || !project?.id) return;
    if (skipSubStepPersistRef.current.spoc) {
      skipSubStepPersistRef.current.spoc = false;
      return;
    }
    const server = gw.spocSubStep || "respond";
    if (spocSubStep === server) return;
    void persistGapWizard({
      spocSubStep,
      delegationFinalized: delegationFinalized === true,
      spocWorkComplete: spocWorkComplete === true,
    });
  }, [spocSubStep, gw.spocSubStep, isSPOC, project?.id, persistGapWizard, delegationFinalized, spocWorkComplete]);

  useEffect(() => {
    if (!isDelegate || !project?.id || !myDelegateProgressKey) return;
    if (skipSubStepPersistRef.current.del) {
      skipSubStepPersistRef.current.del = false;
      return;
    }
    const server =
      gw.delegateProgress?.[myDelegateProgressKey]?.gapDelSubStep ??
      (myDelegateProgress.acknowledged ? "respond-del" : "acknowledge");
    if (delSubStep === server) return;
    void persistGapWizard({
      delegateProgress: {
        ...(gw.delegateProgress || {}),
        [myDelegateProgressKey]: {
          ...myDelegateProgress,
          gapDelSubStep: delSubStep,
        },
      },
    });
  }, [delSubStep, gw.delegateProgress, isDelegate, project?.id, myDelegateProgressKey, myDelegateProgress, persistGapWizard]);
  const GAP_REPORT_FIELD_ANCHOR = { type: "field", fieldId: "gapReport" };
  const handleGapReportAnnotationPost = useCallback(
    async (payload) => {
      const fromShell = payload && typeof payload === "object" && payload.anchor != null;
      const body = fromShell ? String(payload.body ?? "").trim() : gapReportNewCommentDraft.trim();
      const anchor = fromShell ? payload.anchor : GAP_REPORT_FIELD_ANCHOR;
      const kind = fromShell ? payload.kind || "comment" : "comment";
      if (!body) return;
      const ann = createContractAnnotation({
        authorRole: user?.role || "UNKNOWN",
        body,
        anchor,
        kind
      });
      if (!fromShell) setGapReportNewCommentDraft("");
      setGapReportAnnotationFocusKey(stableAnchorKey(anchor));
      await persistGapReportAnnotations([...gapReportAnnotations, ann]);
    },
    [gapReportNewCommentDraft, user?.role, gapReportAnnotations, persistGapReportAnnotations]
  );
  const handleGapReportAnnotationReply = useCallback(
    async (annotationId, text) => {
      const next = addReplyToAnnotation(gapReportAnnotations, annotationId, {
        authorRole: user?.role || "UNKNOWN",
        body: text
      });
      await persistGapReportAnnotations(next);
    },
    [gapReportAnnotations, persistGapReportAnnotations, user?.role]
  );
  const handleGapReportAnnotationResolve = useCallback(
    async (annotationId) => {
      const next = resolveAnnotation(gapReportAnnotations, annotationId, user?.role || "UNKNOWN");
      await persistGapReportAnnotations(next);
    },
    [gapReportAnnotations, persistGapReportAnnotations, user?.role]
  );
  const appendSpocQuestionFile = useCallback(
    async (qid, file, qNoLabel) => {
      if (!project?.id || !uploadGapQuestionFile || !saveScopingProfile || !updateProjectProfile) return false;
      setSpocUploadingQid(qid);
      try {
        const freshProfile = fetchProjectProfile ? await fetchProjectProfile(project.id) : null;
        const base = freshProfile || project.profile || {};
        const uploadResult = await uploadGapQuestionFile(project.id, file, { questionId: qid, source: "spoc" });
        if (!uploadResult.ok) {
          message.error(uploadResult.errorMessage);
          return false;
        }
        const data = uploadResult.data;
        if (!data?.url || !data.artifactId) {
          message.error("Upload did not return file details.");
          return false;
        }
        const attachment = {
          id: data.artifactId,
          url: data.url,
          fileName: data.fileName,
          uploadedAt: data.uploadedAt
        };
        const prevWiz = base.gapAnalysisWizard && typeof base.gapAnalysisWizard === "object" ? base.gapAnalysisWizard : {};
        const prevList = Array.isArray(prevWiz.spocQuestionAttachments?.[qid]) ? prevWiz.spocQuestionAttachments[qid] : [];
        const nextWiz = {
          ...prevWiz,
          spocQuestionAttachments: {
            ...(prevWiz.spocQuestionAttachments || {}),
            [qid]: [...prevList, attachment]
          }
        };
        const evidenceRow = {
          id: data.artifactId,
          url: data.url,
          fileName: data.fileName,
          uploadedAt: data.uploadedAt,
          questionId: qid,
          qNo: qNoLabel || "",
          source: "gap_spoq",
          uploadedByRole: "company_spoc",
          uploadedByUserId: user?.dbUserId || user?.id || ""
        };
        const newProfile = {
          ...base,
          gapAnalysisWizard: nextWiz,
          evidenceFileArtifacts: [...(base.evidenceFileArtifacts || []), evidenceRow]
        };
        updateProjectProfile(project.id, newProfile);
        let ok = false;
        try {
          ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
        } finally {
          if (!ok) {
            // Roll the optimistic profile back to the pre-save snapshot (server truth).
            projectProfileRef.current = base;
            updateProjectProfile(project.id, base);
          }
        }
        if (!ok) {
          message.error("Could not save evidence to the project.");
          return false;
        }
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        message.success("Evidence file saved.");
        return true;
      } catch (e) {
        console.error("appendSpocQuestionFile:", e);
        message.error(e?.message || "Upload failed");
        return false;
      } finally {
        setSpocUploadingQid(null);
      }
    },
    [project?.id, project?.profile, uploadGapQuestionFile, updateProjectProfile, saveScopingProfile, fetchProjectProfile, user?.dbUserId, user?.id]
  );
  const appendDelegateQuestionFile = useCallback(
    async (qid, file, qNoLabel) => {
      if (!project?.id || !uploadGapQuestionFile || !saveScopingProfile || !updateProjectProfile) return false;
      setDelUploadingQid(qid);
      try {
        const freshProfile = fetchProjectProfile ? await fetchProjectProfile(project.id) : null;
        const base = freshProfile || project.profile || {};
        const uploadResult = await uploadGapQuestionFile(project.id, file, { questionId: qid, source: "delegate" });
        if (!uploadResult.ok) {
          message.error(uploadResult.errorMessage);
          return false;
        }
        const data = uploadResult.data;
        if (!data?.url || !data.artifactId) {
          message.error("Upload did not return file details.");
          return false;
        }
        const attachment = {
          id: data.artifactId,
          url: data.url,
          fileName: data.fileName,
          uploadedAt: data.uploadedAt
        };
        const prevWiz = base.gapAnalysisWizard && typeof base.gapAnalysisWizard === "object" ? base.gapAnalysisWizard : {};
        const dp = { ...(prevWiz.delegateProgress || {}) };
        const key = myDelegateProgressKey;
        const cur = dp[key] && typeof dp[key] === "object" ? dp[key] : {};
        const answers = { ...(cur.answers || {}) };
        const prevAns = answers[qid];
        const baseAns =
          prevAns && typeof prevAns === "object"
            ? prevAns
            : { response: typeof prevAns === "string" ? prevAns : "", notes: delNotes[qid] || "" };
        const prevAtt = mergeAttachmentListsById(baseAns.attachments, delAttachments[qid]);
        const nextAttachments = prevAtt.some((a) => a.id === attachment.id) ? prevAtt : [...prevAtt, attachment];
        setDelAttachments((prev) => ({
          ...prev,
          [qid]: nextAttachments
        }));
        answers[qid] = {
          ...baseAns,
          response: (baseAns.response ?? delAnswers[qid]) || "",
          notes: (baseAns.notes ?? delNotes[qid]) || "",
          attachments: nextAttachments
        };
        const nextWiz = {
          ...prevWiz,
          delegateProgress: {
            ...dp,
            [key]: { ...cur, answers },
            ...(myDelegateId !== myDelegateProgressKey ? { [myDelegateId]: { ...cur, answers } } : {})
          }
        };
        const evidenceRow = {
          id: data.artifactId,
          url: data.url,
          fileName: data.fileName,
          uploadedAt: data.uploadedAt,
          questionId: qid,
          qNo: qNoLabel || "",
          source: "gap_delegate",
          uploadedByRole: "company_delegate",
          uploadedByUserId: user?.dbUserId || user?.id || ""
        };
        const newProfile = {
          ...base,
          gapAnalysisWizard: nextWiz,
          evidenceFileArtifacts: [...(base.evidenceFileArtifacts || []), evidenceRow]
        };
        updateProjectProfile(project.id, newProfile);
        let ok = false;
        try {
          ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
        } finally {
          if (!ok) {
            // Roll the optimistic profile back to the pre-save snapshot (server truth).
            projectProfileRef.current = base;
            updateProjectProfile(project.id, base);
          }
        }
        if (!ok) {
          message.error("Could not save evidence to the project.");
          return false;
        }
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        message.success("Evidence file saved.");
        return true;
      } catch (e) {
        console.error("appendDelegateQuestionFile:", e);
        message.error(e?.message || "Upload failed");
        return false;
      } finally {
        setDelUploadingQid(null);
      }
    },
    [
      project?.id,
      project?.profile,
      uploadGapQuestionFile,
      updateProjectProfile,
      saveScopingProfile,
      fetchProjectProfile,
      myDelegateProgressKey,
      myDelegateId,
      delAnswers,
      delNotes,
      delAttachments,
      user?.dbUserId,
      user?.id
    ]
  );
  const [deletingArtifactId, setDeletingArtifactId] = useState(null);
  const removeSpocQuestionFile = useCallback(
    async (qid, artifactId) => {
      if (!project?.id || !deleteGapQuestionFile || !saveScopingProfile || !updateProjectProfile) return;
      setDeletingArtifactId(artifactId);
      try {
        const result = await deleteGapQuestionFile(project.id, artifactId);
        if (!result.ok) {
          message.error(result.errorMessage || "Delete failed");
          return;
        }
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        if (result.blobDeleteDenied) {
          message.warning("Reference removed. The underlying file was not deleted - it belongs to a different workspace.");
        } else {
          message.success("Evidence file removed.");
        }
      } catch (e) {
        console.error("removeSpocQuestionFile:", e);
        message.error(e?.message || "Delete failed");
      } finally {
        setDeletingArtifactId(null);
      }
    },
    [project?.id, deleteGapQuestionFile, saveScopingProfile, updateProjectProfile, fetchProjectProfile]
  );
  const removeDelegateQuestionFile = useCallback(
    async (qid, artifactId) => {
      if (!project?.id || !deleteGapQuestionFile || !saveScopingProfile || !updateProjectProfile) return;
      setDeletingArtifactId(artifactId);
      try {
        const result = await deleteGapQuestionFile(project.id, artifactId);
        if (!result.ok) {
          message.error(result.errorMessage || "Delete failed");
          return;
        }
        setDelAttachments((prev) => {
          const list = Array.isArray(prev[qid]) ? prev[qid].filter((a) => a.id !== artifactId) : [];
          return { ...prev, [qid]: list };
        });
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        if (result.blobDeleteDenied) {
          message.warning("Reference removed. The underlying file was not deleted - it belongs to a different workspace.");
        } else {
          message.success("Evidence file removed.");
        }
      } catch (e) {
        console.error("removeDelegateQuestionFile:", e);
        message.error(e?.message || "Delete failed");
      } finally {
        setDeletingArtifactId(null);
      }
    },
    [project?.id, deleteGapQuestionFile, saveScopingProfile, updateProjectProfile, fetchProjectProfile]
  );
  const syncScheduledMeetingsFromServer = useCallback(async () => {
    if (!isLeadAdvisor || !project?.id || !fetchMeetings) return;
    try {
      const all = await fetchMeetings();
      const forProject = (all || []).filter((x) => x.projectId === project.id && isMeetingFromGapAnalysis(x));
      forProject.sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime());
      const rows = forProject.map(mapApiMeetingToScheduledRow);
      setScheduledMeetings(rows);
    } catch (e) {
      console.error("Failed to sync scheduled meetings:", e);
    }
  }, [isLeadAdvisor, project?.id, fetchMeetings]);
  const [isReportEditMode, setIsReportEditMode] = useState(false);
  const [reportSections, setReportSections] = useState(() => {
    if (gw.gapReportDraft && typeof gw.gapReportDraft === "object") return withoutLegacyCannedText(gw.gapReportDraft);
    return { executiveSummary: "", legalEntities: "", productsServices: "", activitiesDepts: "", affirmationName: "", affirmationDate: "" };
  });
  const releasedQuestions = useMemo(() => {
    const raw = project?.profile?.gapAnalysisQuestionnaire?.questions;
    if (Array.isArray(raw) && raw.length > 0) return raw.filter((q) => q.selected !== false);
    // No placeholder bank slice: empty profile must show 0 questions, not a fake first-40 set.
    return [];
  }, [project?.profile?.gapAnalysisQuestionnaire]);
  const membersFetchedRef = useRef(false);
  useEffect(() => {
    if (membersFetchedRef.current || !project?.id || !fetchProjectMembers) return;
    if (Array.isArray(project.members) && project.members.length > 0) return;
    membersFetchedRef.current = true;
    (async () => {
      const members = await fetchProjectMembers(project.id);
      if (Array.isArray(members) && members.length > 0 && updateProjectMembers) {
        updateProjectMembers(members);
      }
    })();
  }, [project?.id, project?.members, fetchProjectMembers, updateProjectMembers]);
  const projectDelegates = useMemo(() => {
    const members = project?.members || [];
    return members.filter((m) => isRoleDelegate(m.role));
  }, [project?.members]);
  const [delegateList, setDelegateList] = useState(() => {
    const savedAssignments = gw.delegateAssignments;
    const members = project?.members || [];
    const dels = members.filter((m) => isRoleDelegate(m.role));
    if (dels.length > 0) {
      return dels.map((d) => {
        const saved = Array.isArray(savedAssignments) ? savedAssignments.find((a) => a.delegateId === d.id || a.delegateEmail === d.email) : null;
        return { id: d.id, name: d.name || d.email, email: d.email, assigned: !!(saved && saved.questionIds?.length > 0), questions: saved?.questionIds || [] };
      });
    }
    return [];
  });
  const delegateSyncRef = useRef(false);
  const spocReportReviewAutoNavDoneRef = useRef(false);
  useEffect(() => {
    if (step4Status !== "Under Review") {
      spocReportReviewAutoNavDoneRef.current = false;
    }
  }, [step4Status]);
  useEffect(() => {
    if (!isSPOC || step4Status !== "Under Review" || spocReportReviewAutoNavDoneRef.current) return;
    spocReportReviewAutoNavDoneRef.current = true;
    setSpocSubStep("report-review");
  }, [isSPOC, step4Status]);
  useEffect(() => {
    const s = project?.profile?.gapAnalysisWizard?.gapAnalysisStatus;
    if (s && typeof s === "string") setStep4Status(s);
  }, [project?.id, project?.profile?.gapAnalysisWizard?.gapAnalysisStatus]);
  /** Keep score/report draft flags aligned with persisted wizard (refetch, multi-tab). */
  useEffect(() => {
    setScoresValidated(gw.scoresValidated === true);
    setScoresSaved(gw.scoresSaved === true);
    setReportDrafted(gw.reportDrafted === true && !hasLegacyCannedReportText(gw.gapReportDraft));
    setAiScoresGenerated(gw.aiScoresGenerated === true);
  }, [
    project?.id,
    gw.scoresValidated,
    gw.scoresSaved,
    gw.reportDrafted,
    gw.gapReportDraft,
    gw.aiScoresGenerated,
  ]);
  useEffect(() => {
    const raw = project?.profile?.gapAnalysisWizard?.gapReportAnnotations;
    setGapReportAnnotations(normalizeContractAnnotations(raw));
  }, [project?.id, project?.profile?.gapAnalysisWizard?.gapReportAnnotations]);
  useEffect(() => {
    // Server is the source of truth both ways (a withdrawn / re-pushed state must be able to clear it).
    const w = project?.profile?.gapAnalysisWizard;
    if (w) setPushedToDashboard(w.pushedToDashboard === true);
  }, [project?.id, project?.profile?.gapAnalysisWizard?.pushedToDashboard]);
  /** Bug fix (Task: Setup Tool "Tools Configured" refresh reset) — toolSetup was local-only
   * state with no persist call, so a page refresh silently dropped it back to unset. Mirrors
   * the pushedToDashboard hydration effect above: re-sync from the persisted wizard on
   * refetch / multi-tab, same as the useState(() => !!gw.toolSetup) mount-time initializer. */
  useEffect(() => {
    const v = project?.profile?.gapAnalysisWizard?.toolSetup;
    if (v === true) setToolSetup(true);
  }, [project?.id, project?.profile?.gapAnalysisWizard?.toolSetup]);
  /** Backlog #18 — same refresh-reset bug fix as toolSetup above, for techAssessmentDone. */
  useEffect(() => {
    // Server is the source of truth both ways: a reopen (or refused completion) must be able to clear it.
    const w = project?.profile?.gapAnalysisWizard;
    if (w) setTechAssessmentDone(w.techAssessmentDone === true);
  }, [project?.id, project?.profile?.gapAnalysisWizard?.techAssessmentDone]);
  /** Backlog #21 — same refresh-reset bug fix as toolSetup above, for sharedWithLA. */
  useEffect(() => {
    // Cleared server-side when the SPOC sends the gap analysis back, so follow false as well as true.
    const w = project?.profile?.gapAnalysisWizard;
    if (w) setSharedWithLA(w.sharedWithLA === true);
  }, [project?.id, project?.profile?.gapAnalysisWizard?.sharedWithLA]);
  /**
   * Backlog Sheet-1 rows 19/20 — same refresh-reset bug fix as toolSetup above, for the two
   * NOW-real, backend-persisted flags `techAiEvalRun` (set only by a real invokeAI() call) and
   * `techScoresValidated` (set only by the distinct human-approval call). Also re-syncs the
   * editable score-override map and the AI warnings/failedBatches telemetry on refetch, so a
   * page reload never silently drops a prior run's state.
   */
  useEffect(() => {
    const w = project?.profile?.gapAnalysisWizard;
    if (!w) return;
    // Reopen and SPOC send-back archive and clear the evaluation server-side: follow the server in both directions.
    setTechAiEvalRun(w.techAiEvalRun === true);
    setTechScoresValidated(w.techScoresValidated === true);
    setTechAiWarnings(Array.isArray(w.techAiWarnings) ? w.techAiWarnings : []);
    setTechFailedBatches(Array.isArray(w.techAiFailedBatches) ? w.techAiFailedBatches : []);
    setTechScores(w.techScoreOverrides && typeof w.techScoreOverrides === "object" ? w.techScoreOverrides : {});
    if (w.techScoresValidated !== true) setTechApprovalInfo(null);
  }, [
    project?.id,
    project?.profile?.gapAnalysisWizard?.techAiEvalRun,
    project?.profile?.gapAnalysisWizard?.techScoresValidated,
    project?.profile?.gapAnalysisWizard?.techAiWarnings,
    project?.profile?.gapAnalysisWizard?.techAiFailedBatches,
    project?.profile?.gapAnalysisWizard?.techScoreOverrides,
  ]);
  /** Keep finalized flags aligned with persisted profile when it updates (refetch or other tab). Primitives only: gw values do not change until save returns, so local clears on Confirm are not overwritten. */
  useEffect(() => {
    setDelegationFinalized(gw.delegationFinalized === true);
    setSpocWorkComplete(gw.spocWorkComplete === true);
  }, [project?.id, gw.delegationFinalized, gw.spocWorkComplete]);
  /** After server-side gap reset, gapAnalysisWizard is removed; local React state could still show "Released". */
  useEffect(() => {
    if (project?.profile != null && project.profile.gapAnalysisWizard == null) {
      setQuestionnaireReleased(false);
    }
  }, [project?.id, project?.profile?.gapAnalysisWizard]);
  useEffect(() => {
    if (projectDelegates.length === 0) return;
    if (delegateSyncRef.current) return;
    delegateSyncRef.current = true;
    const savedAssignments = project?.profile?.gapAnalysisWizard?.delegateAssignments;
    setDelegateList(projectDelegates.map((d) => {
      const saved = Array.isArray(savedAssignments) ? savedAssignments.find((a) => a.delegateId === d.id || a.delegateEmail === d.email) : null;
      return { id: d.id, name: d.name || d.email, email: d.email, assigned: !!(saved && saved.questionIds?.length > 0), questions: saved?.questionIds || [] };
    }));
  }, [projectDelegates, project?.profile?.gapAnalysisWizard?.delegateAssignments]);
  const answeredQuestionIds = useMemo(() => {
    return new Set(Object.keys(mergedSpocAnswers).filter((id) => mergedSpocAnswers[id]));
  }, [mergedSpocAnswers]);
  const unansweredQuestions = useMemo(() => {
    return releasedQuestions.filter((q) => !answeredQuestionIds.has(q.id));
  }, [releasedQuestions, answeredQuestionIds]);
  /** Resolve delegated question ids for display (assigned qs may not be in unansweredQuestions if SPOC answered them, or id set may differ after reload). */
  const gapQuestionById = useMemo(() => {
    const m = new Map();
    // Released questions only. An id that is not among them is shown as "Question not found" by the callers; it is
    // never resolved against a bundled sample bank (its ids are row positions and would name an unrelated question).
    releasedQuestions.forEach((q) => m.set(q.id, q));
    return m;
  }, [releasedQuestions]);
  const resolveDelegatedQuestion = (qid) => gapQuestionById.get(qid) || { id: qid, qNo: "", question: "Question not found", domain: "", notFound: true };
  const allAssignedIds = useMemo(() => {
    const s = new Set();
    delegateList.forEach((d) => (d.questions || []).forEach((qid) => s.add(qid)));
    return s;
  }, [delegateList]);
  const poolQuestions = useMemo(() => {
    return unansweredQuestions.filter((q) => !allAssignedIds.has(q.id));
  }, [unansweredQuestions, allAssignedIds]);
  /** True when SPOC may confirm delegation: nothing left in pool, or there were no unanswered questions to assign. */
  const canConfirmDelegation = useMemo(() => {
    if (unansweredQuestions.length === 0) return true;
    if (delegateList.length === 0) return false;
    return poolQuestions.length === 0;
  }, [unansweredQuestions.length, delegateList.length, poolQuestions.length]);
  const filteredPoolQuestions = useMemo(() => {
    if (domainFilter === "all") return poolQuestions;
    return poolQuestions.filter((q) => q.domain === domainFilter);
  }, [poolQuestions, domainFilter]);
  const poolDomains = useMemo(() => {
    const s = new Set();
    poolQuestions.forEach((q) => { if (q.domain) s.add(q.domain); });
    return [...s].sort();
  }, [poolQuestions]);
  const DELEGATE_COLORS = ["bg-blue-500", "bg-violet-500", "bg-teal-500", "bg-rose-500", "bg-amber-500", "bg-indigo-500"];
  const persistDelegateAssignmentDraftFromList = useCallback((list) => {
    const assignments = list
      .filter((d) => (d.questions || []).length > 0)
      .map((d) => ({
        delegateId: d.id,
        delegateName: d.name,
        delegateEmail: d.email || "",
        questionIds: d.questions,
      }));
    void persistGapWizard({
      delegateAssignments: assignments,
      delegationFinalized: false,
      spocWorkComplete: false,
    });
  }, [persistGapWizard]);
  const splitEvenly = useCallback(() => {
    if (delegateList.length === 0 || poolQuestions.length === 0) return;
    const ids = poolQuestions.map((q) => q.id);
    setDelegateList((prev) => {
      const updated = prev.map((d) => ({ ...d, questions: [...(d.questions || [])] }));
      ids.forEach((qid, i) => {
        const idx = i % updated.length;
        updated[idx].questions.push(qid);
        updated[idx].assigned = true;
      });
      queueMicrotask(() => persistDelegateAssignmentDraftFromList(updated));
      return updated;
    });
    setDelegateQuestionAssign({});
  }, [delegateList.length, poolQuestions, persistDelegateAssignmentDraftFromList]);
  const assignAllRemaining = useCallback((delegateId) => {
    if (!delegateId || poolQuestions.length === 0) return;
    const ids = poolQuestions.map((q) => q.id);
    setDelegateList((prev) => {
      const next = prev.map((d) =>
        d.id === delegateId
          ? { ...d, assigned: true, questions: [...new Set([...(d.questions || []), ...ids])] }
          : d
      );
      queueMicrotask(() => persistDelegateAssignmentDraftFromList(next));
      return next;
    });
    setDelegateQuestionAssign({});
  }, [poolQuestions, persistDelegateAssignmentDraftFromList]);
  const assignCheckedToTarget = useCallback((targetId) => {
    const checkedIds = Object.keys(delegateQuestionAssign).filter((k) => delegateQuestionAssign[k]);
    if (checkedIds.length === 0 || !targetId) return;
    setDelegateList((prev) => {
      const next = prev.map((d) =>
        d.id === targetId
          ? { ...d, assigned: true, questions: [...new Set([...(d.questions || []), ...checkedIds])] }
          : d
      );
      queueMicrotask(() => persistDelegateAssignmentDraftFromList(next));
      return next;
    });
    setDelegateQuestionAssign({});
  }, [delegateQuestionAssign, persistDelegateAssignmentDraftFromList]);
  const unassignQuestion = useCallback((questionId) => {
    setDelegateList((prev) => {
      const next = prev.map((d) => {
        const qs = (d.questions || []).filter((qid) => qid !== questionId);
        return { ...d, questions: qs, assigned: qs.length > 0 };
      });
      queueMicrotask(() => persistDelegateAssignmentDraftFromList(next));
      return next;
    });
  }, [persistDelegateAssignmentDraftFromList]);
  const unassignAllFromDelegate = useCallback((delegateId) => {
    setDelegateList((prev) => {
      const next = prev.map((d) => (d.id === delegateId ? { ...d, questions: [], assigned: false } : d));
      queueMicrotask(() => persistDelegateAssignmentDraftFromList(next));
      return next;
    });
  }, [persistDelegateAssignmentDraftFromList]);
  const resolveQuestionResponse = useCallback((qId) => {
    if (mergedSpocAnswers[qId]) return mergedSpocAnswers[qId];
    const dp = gw.delegateProgress || {};
    for (const [, prog] of Object.entries(dp)) {
      if (prog?.answers?.[qId]?.response) return prog.answers[qId].response;
    }
    return "";
  }, [mergedSpocAnswers, gw.delegateProgress]);

  /** Matches precedence in resolveQuestionResponse: SPOC first, then any delegate answer. */
  const getResponseSourceForLa = useCallback((qId) => {
    if (mergedSpocAnswers[qId]) return "spoc";
    const dp = gw.delegateProgress || {};
    for (const [, prog] of Object.entries(dp)) {
      if (prog?.answers?.[qId]?.response) return "delegate";
    }
    return null;
  }, [mergedSpocAnswers, gw.delegateProgress]);

  const laGapAttachmentIndex = useMemo(
    () =>
      buildLaResponsesAttachmentsByQuestion(
        gw.spocQuestionAttachments,
        gw.delegateProgress,
        project?.profile?.evidenceFileArtifacts
      ),
    [gw.spocQuestionAttachments, gw.delegateProgress, project?.profile?.evidenceFileArtifacts]
  );

  const scoredQuestions = useMemo(() => {
    const saved = gw.aiGeneratedScores || {};
    return releasedQuestions.map((q) => {
      const response = resolveQuestionResponse(q.id);
      const ai = saved[q.id] || {};
      return {
        id: q.id,
        qNo: q.qNo || "",
        question: (q.question || "").substring(0, 150),
        // Missing domain stays empty: never sent to the AI as an invented "General".
        domain: q.domain || "",
        applicableControls: typeof ai.applicableControls === "number" ? ai.applicableControls : null,
        controlMappings: normalizeGapControlMappings(ai.controlMappings),
        response: response || "-",
        readinessScore: ai.readinessScore ?? 0,
        expectedScore: ai.expectedScore ?? 0,
        // false = the AI never produced a score for this row; the 0/0 above is only an input
        // placeholder and must not enter any average, count or the readiness figure.
        scored: aiRowHasScore(ai),
        nextSteps: ai.nextSteps || "",
        // aiFailed is the ONLY signal distinguishing a fallback row from a legitimate
        // 0/0/0 score \u2014 lesson [[silent-zero-scoring-ai-fallback]].
        aiFailed: ai.aiFailed === true,
        aiFailureReason: ai.aiFailureReason || null
      };
    });
  }, [releasedQuestions, resolveQuestionResponse, gw.aiGeneratedScores]);

  const realIssueResponses = useMemo(() => {
    const ito = project?.profile?.issuesThreatsOpportunities?.byCategory || {};
    const saved = gw.aiGeneratedScores || {};
    const result = {};
    const catMap = { "Internal Issues": "ii", "External Issues": "ei", "Risk and Threats": "rt", "Opportunities for Improvement": "op" };
    for (const [cat, prefix] of Object.entries(catMap)) {
      const rows = Array.isArray(ito[cat]) ? ito[cat] : [];
      if (rows.length === 0) continue;
      result[cat] = rows.map((r, i) => {
        const id = `${prefix}-${i}`;
        const ai = saved[id] || {};
        return {
          id,
          label: r.issue || "",
          applicableControls: typeof ai.applicableControls === "number" ? ai.applicableControls : null,
          controlMappings: normalizeGapControlMappings(ai.controlMappings),
          response: "-",
          readinessScore: ai.readinessScore ?? 0,
          expectedScore: ai.expectedScore ?? 0,
          scored: aiRowHasScore(ai),
          nextSteps: ai.nextSteps || "",
          aiFailed: ai.aiFailed === true,
          aiFailureReason: ai.aiFailureReason || null
        };
      });
    }
    return result;
  }, [project?.profile?.issuesThreatsOpportunities, gw.aiGeneratedScores]);
  // Backlog Sheet-1 row 19 fix — was `useMemo(() => [], [])`, a permanent empty-array stub
  // (.claude/lessons/completion-flag-implies-ai-ran-with-no-real-call.md). Now derived from the
  // real, backend-persisted `gapAnalysisWizard.techObservations` (written by
  // POST .../gap-analysis/technical-evaluation — a real invokeAI() call over the Advisor's
  // approved tool-test summaries).
  const techObservations = useMemo(() => (Array.isArray(gw.techObservations) ? gw.techObservations : []), [gw.techObservations]);
  const [nonTechScores, setNonTechScores] = useState(() => gw.nonTechScoreOverrides || {});
  const [issueScores, setIssueScores] = useState(() => gw.issueScoreOverrides || {});
  // Local override map for the editable "Review & Validate Technical Scores" table — mirrors
  // nonTechScores/issueScores above. Seeded from the last-persisted overrides (set by a prior
  // approval call); `getScore()` falls back to each observation's own AI-drafted score when no
  // override exists for that id.
  const [techScores, setTechScores] = useState(() => (gw.techScoreOverrides && typeof gw.techScoreOverrides === "object" ? gw.techScoreOverrides : {}));
  // Task #74 (Gap A) - rows with NO real AI score: the server's persisted `aiFailed` flag plus any
  // ids the approve route just refused (409 error.details.failedRowIds). A failed row carries a
  // placeholder 0/0, so it may only be approved after a retry or an override of BOTH scores.
  const techFailedRowIds = useMemo(() => {
    const ids = new Set();
    techObservations.forEach((t) => { if (t.aiFailed) ids.add(String(t.id)); });
    const known = new Set(techObservations.map((t) => String(t.id)));
    techApproveConflictRows.forEach((id) => { if (known.has(id)) ids.add(id); });
    return ids;
  }, [techObservations, techApproveConflictRows]);
  const techUnresolvedFailedIds = useMemo(
    () => Array.from(techFailedRowIds).filter((id) => {
      const o = techScores[id];
      return !(o && Number.isFinite(o.readinessScore) && Number.isFinite(o.expectedScore));
    }),
    [techFailedRowIds, techScores],
  );
  const techOverrideNoteMissingIds = useMemo(
    () => techOverrideRowsMissingNote({
      observations: techObservations.map((t) => ({ id: t.id, readinessScore: t.readinessScore, expectedScore: t.expectedScore, aiFailed: t.aiFailed === true || techFailedRowIds.has(String(t.id)) })),
      overrides: techScores,
    }),
    [techObservations, techFailedRowIds, techScores],
  );
  const techApproveBlockedReason = gapLocked
    ? techEvalBlockedReason({ techAssessmentDone, gapLocked })
    : techEvalRunning
    ? "The failed rows are being retried."
    : techUnresolvedFailedIds.length > 0
      ? `${techUnresolvedFailedIds.length} row${techUnresolvedFailedIds.length === 1 ? " has" : "s have"} no AI score. Retry the failed rows, or override both Readiness and Expected for each, before approving.`
      : techOverrideNoteMissingIds.length > 0
        ? `${techOverrideNoteMissingIds.length} overridden score${techOverrideNoteMissingIds.length === 1 ? " needs" : "s need"} a reason of at least ${OVERRIDE_NOTE_MIN_LENGTH} characters. Say why each score was changed before approving.`
        : "";
  /** Observation rows for the technical tables; failed rows require BOTH scores to be set by hand. */
  const buildTechTableItems = () => techObservations.map((t) => ({ id: t.id, label: t.observation, applicableControls: t.applicableControls, response: t.response, readinessScore: t.readinessScore, expectedScore: t.expectedScore, nextSteps: t.nextSteps, aiFailed: t.aiFailed === true || techFailedRowIds.has(String(t.id)), requireFullOverride: true, aiFailureReason: t.aiFailureReason, reasoningRefs: t.reasoningRefs, confidence: t.confidence, confidenceMethod: t.confidenceMethod, confidenceRuns: t.confidenceRuns, validSamples: t.validSamples, confidenceUnavailableReason: t.confidenceUnavailableReason, agreementNotes: t.agreementNotes, confidenceScope: t.confidenceScope, humanOverridden: t.humanOverridden, overrideNote: t.overrideNote, reviewedBy: t.reviewedBy, reviewedAsAdvisor: t.reviewedAsAdvisor, reviewedAt: t.reviewedAt }));
  /** Who approved the technical scores (named reviewer; "on behalf of" the Advisor when a Lead Advisor impersonated). */
  const renderTechApprovalAttribution = () => {
    if (!techScoresValidated) return null;
    const by = gw.techScoresValidatedBy || techApprovalInfo?.by || null;
    const asAdvisor = gw.techScoresValidatedAsAdvisor || techApprovalInfo?.asAdvisor || null;
    const impersonationId = gw.techScoresValidatedImpersonationId || techApprovalInfo?.impersonationId || null;
    const atRaw = gw.techScoresValidatedAt ?? techApprovalInfo?.at ?? null;
    const atIso = typeof atRaw === "number" ? new Date(atRaw).toISOString() : atRaw;
    const who = by?.email || by?.userId || "a named reviewer";
    const onBehalf = !!(asAdvisor || impersonationId);
    return /* @__PURE__ */ jsx("div", {
      "data-testid": "tech-approval-attribution",
      className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] px-5 py-3 text-[11px] font-semibold text-[color:var(--color-ok-fg)]",
      children: onBehalf
        ? `Approved by ${who} on behalf of ${asAdvisor?.email || "the Advisor"} (Lead Advisor acting under an audited impersonation session) on ${formatDdMmYyyy(atIso)}.`
        : `Approved by ${who} on ${formatDdMmYyyy(atIso)}.`,
    });
  };
  /**
   * Backlog Sheet-1 row 20 — "Review & Validate" — the explicit human-approval event
   * Commandment IV requires before `techScoresValidated` may become true. Sends the Advisor's
   * current in-table edits (`techScores`, the local override map above) as
   * `techScoreOverrides` — the backend applies them and stamps every observation
   * `approvalState: 'HUMAN_APPROVED'` + `reviewedBy`. This is a distinct call from
   * `runTechnicalAiEvaluation` — it never re-runs the AI, only records the human approval.
   */
  const approveTechnicalAiEvaluation = useCallback(async ({ impersonated = false } = {}) => {
    if (!project?.id || techScoresValidated || techApproving) return;
    if (techApproveBlockedReason) {
      message.warning(techApproveBlockedReason);
      return;
    }
    setTechApproving(true);
    try {
      const evaluationId = techEvaluationId || (gw.techAiEvalRunAt != null ? String(gw.techAiEvalRunAt) : undefined);
      const result = await approveGapAnalysisTechnicalEvaluation(project.id, techScores, { impersonated, evaluationId });
      if (result.ok) {
        setTechScoresValidated(true);
        setTechApproveConflictRows([]);
        setTechApprovalInfo({
          by: result.data?.techScoresValidatedBy || null,
          asAdvisor: result.data?.techScoresValidatedAsAdvisor || null,
          impersonationId: result.data?.techScoresValidatedImpersonationId || null,
          at: result.data?.techScoresValidatedAt ?? null,
        });
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
        message.success(
          impersonated
            ? "Technical scores validated (acting on behalf of Advisor)."
            : "Technical scores validated.",
        );
        const ignored = Array.isArray(result.data?.ignoredOverrideRowIds) ? result.data.ignoredOverrideRowIds.map(String) : [];
        if (ignored.length > 0) {
          const labels = ignored.map((id) => techObservations.find((o) => String(o.id) === id)?.observation || id).map((t) => String(t).slice(0, 60));
          message.warning(`${ignored.length} override${ignored.length === 1 ? " was" : "s were"} ignored because the row was re-scored after a retry: ${labels.join("; ")}. Reopen the evaluation if one still needs overriding.`);
        }
      } else {
        if (result.status === 409 && Array.isArray(result.failedRowIds)) setTechApproveConflictRows(result.failedRowIds);
        message.error(result.errorMessage || "Approval failed. Please try again.");
        if (result.status === 409 && fetchProjectProfile) await fetchProjectProfile(project.id);
      }
    } catch (e) {
      console.error("Technical evaluation approval failed:", e);
      message.error("Approval failed. Please try again.");
    }
    setTechApproving(false);
  }, [project?.id, techScoresValidated, techApproving, techApproveBlockedReason, approveGapAnalysisTechnicalEvaluation, techScores, techEvaluationId, gw.techAiEvalRunAt, techObservations, fetchProjectProfile]);
  /** LA "Act on behalf of Advisor" variant of approveTechnicalAiEvaluation — same session-validation shape as the trigger's on-behalf-of variant. */
  const approveTechnicalAiEvaluationOnBehalfOfAdvisor = useCallback(async () => {
    if (techScoresValidated) return;
    if (!techAiEvalRun) {
      message.warning("Run Technical AI Evaluation first.");
      return;
    }
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "advisor" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Advisor is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    if (!session.effectiveUserId) {
      message.warning(
        "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
      );
      return;
    }
    await approveTechnicalAiEvaluation({ impersonated: true });
  }, [techScoresValidated, techAiEvalRun, project?.id, approveTechnicalAiEvaluation]);
  const getScore = (store, id, field, fallback) => {
    return store[id]?.[field] ?? fallback;
  };
  const updateScore = (setter, id, field, value, original) => {
    setter((prev) => ({ ...prev, [id]: { ...original, ...prev[id], [field]: value } }));
  };
  /** Same shape the ai-scores endpoint expects — shared by generate, validate, and retry. */
  const buildAiScorePayload = useCallback(() => {
    const qPayload = scoredQuestions.map((q) => ({ id: q.id, question: q.question, domain: q.domain, response: q.response }));
    const issuePayload = Object.entries(realIssueResponses).flatMap(([cat, items]) => items.map((it) => ({ id: it.id, question: it.label, domain: cat, response: "-" })));
    return [...qPayload, ...issuePayload];
  }, [scoredQuestions, realIssueResponses]);
  // Derived straight from persisted gw.aiGeneratedScores (via scoredQuestions/realIssueResponses),
  // so it stays accurate across a page reload even though aiScoreFailedBatches (the raw last-response
  // batch metadata) is ephemeral. Lesson [[silent-zero-scoring-ai-fallback]].
  const failedAiScoreRowIds = useMemo(() => {
    const ids = new Set();
    scoredQuestions.forEach((q) => { if (q.aiFailed) ids.add(q.id); });
    Object.values(realIssueResponses).flat().forEach((it) => { if (it.aiFailed) ids.add(it.id); });
    return ids;
  }, [scoredQuestions, realIssueResponses]);
  // Task #74 (Gap A) — parallel to failedAiScoreRowIds above, for the Advisor's technical-scoring
  // path. techObservations is now real, backend-persisted data (populated by a successful
  // POST .../gap-analysis/technical-evaluation — see the techObservations useMemo above; fixed,
  // was previously a hardcoded [] stub — .claude/lessons/completion-flag-implies-ai-ran-with-no-real-call.md),
  // so this Set genuinely reflects any `aiFailed` observation once tech scoring has run.
  // Task #75: read-only aggregate for the printable/report surfaces (Lead Advisor's
  // "report" tab + SPOC's "report-review" tab). Deliberately has no onRetry wired to
  // GapAnalysisAiFailureBanner where this is consumed — retry only lives on the
  // interactive Review & Validate Scores panel (Task #74); SPOC must never gain an
  // action beyond what their persona is allowed at this read-only stage.
  const reportFailedRowCount = useMemo(
    () => failedAiScoreRowIds.size + techFailedRowIds.size,
    [failedAiScoreRowIds, techFailedRowIds]
  );
  const overallScore = useMemo(() => {
    const allIssueItems = Object.values(realIssueResponses).flat();
    const allItems = [
      ...scoredQuestions.filter((r) => isGapRowScored(nonTechScores, r)).map((r) => ({ readiness: getScore(nonTechScores, r.id, "readinessScore", r.readinessScore), expected: getScore(nonTechScores, r.id, "expectedScore", r.expectedScore) })),
      ...allIssueItems.filter((r) => isGapRowScored(issueScores, r)).map((r) => ({ readiness: getScore(issueScores, r.id, "readinessScore", r.readinessScore), expected: getScore(issueScores, r.id, "expectedScore", r.expectedScore) }))
    ];
    const totalReadiness = allItems.reduce((s, i) => s + i.readiness, 0);
    const totalExpected = allItems.reduce((s, i) => s + i.expected, 0);
    return totalExpected > 0 ? Math.round(totalReadiness / totalExpected * 100) : 0;
  }, [scoredQuestions, realIssueResponses, nonTechScores, issueScores]);
  const notScoredCount = useMemo(
    () =>
      scoredQuestions.filter((r) => !isGapRowScored(nonTechScores, r)).length +
      Object.values(realIssueResponses).flat().filter((r) => !isGapRowScored(issueScores, r)).length,
    [scoredQuestions, realIssueResponses, nonTechScores, issueScores],
  );
  const notScoredLabel = (n) => `${n} ${n === 1 ? "item" : "items"} not scored`;
  const displayReadinessPercent = useMemo(() => {
    if (scoresValidated && gw.validatedOverallScore != null && gw.validatedOverallScore !== "") {
      const n = Number(gw.validatedOverallScore);
      if (!Number.isNaN(n)) return Math.round(n);
    }
    return overallScore;
  }, [scoresValidated, gw.validatedOverallScore, overallScore]);
  // Mirrors the server's push gate (gapPushBlockedReason); the API refuses regardless.
  const pushBlockedReason = gapPushBlockedReason({
    status: step4Status,
    scoresValidated,
    techEvaluated: techAiEvalRun || (Array.isArray(gw.techObservations) && gw.techObservations.length > 0),
    techScoresValidated,
    techAssessmentDone,
    techWaived: !!techWaiver,
  });
  const showIso27001Heatmap = useMemo(() => {
    const c = `${project?.certification || ""} ${project?.frameworkType || ""}`;
    return /27001/i.test(c);
  }, [project?.certification, project?.frameworkType]);
  const annexReadinessByCtrl = useMemo(() => {
    const bucket = /* @__PURE__ */ new Map();
    for (const q of scoredQuestions) {
      if (!isGapRowScored(nonTechScores, q)) continue;
      const r = getScore(nonTechScores, q.id, "readinessScore", q.readinessScore);
      for (const m of normalizeGapControlMappings(q.controlMappings)) {
        const k = normalizeAnnexAControlRefForIso(m.number);
        if (!k) continue;
        if (!bucket.has(k)) bucket.set(k, []);
        bucket.get(k).push(r);
      }
    }
    for (const item of Object.values(realIssueResponses).flat()) {
      if (!isGapRowScored(issueScores, item)) continue;
      const r = getScore(issueScores, item.id, "readinessScore", item.readinessScore);
      for (const m of normalizeGapControlMappings(item.controlMappings)) {
        const k = normalizeAnnexAControlRefForIso(m.number);
        if (!k) continue;
        if (!bucket.has(k)) bucket.set(k, []);
        bucket.get(k).push(r);
      }
    }
    const out = /* @__PURE__ */ new Map();
    for (const [k, arr] of bucket) {
      const avg = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
      out.set(k, { avg, count: arr.length });
    }
    return out;
  }, [scoredQuestions, realIssueResponses, nonTechScores, issueScores]);
  useEffect(() => {
    if (laSubStep !== "push-dashboard" || !project?.id) return;
    let cancelled = false;
    (async () => {
      setPushTabIsoLoading(true);
      try {
        const [isoRes, ms] = await Promise.all([fetchIso27001Controls(), fetchMilestones(project.id)]);
        if (cancelled) return;
        setPushTabIsoControls(isoRes.controls || []);
        setPushTabIsoVersion(isoRes.versionLabel);
        const local = project?.profile?.milestones;
        const list = Array.isArray(ms) && ms.length > 0 ? ms : Array.isArray(local) ? local : [];
        setPushTabMilestones(list);
      } finally {
        if (!cancelled) setPushTabIsoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    laSubStep,
    project?.id,
    fetchIso27001Controls,
    fetchMilestones,
    project?.profile?.milestones,
    project?.profile?.scopingContractDraft?.scopingDocumentUpdatedAt,
  ]);
  const getRagColor = (score, max) => {
    return `${RAG_BG_CLASS[ragBandForScore(score, max) || "red"]} text-white`;
  };
  const refreshReportFromScores = () => {
    const scoredOnly = scoredQuestions.filter((r) => isGapRowScored(nonTechScores, r));
    const domainSet = [...new Set(scoredOnly.map((q) => q.domain).filter(Boolean))];
    const domainCounts = domainSet.map((d) => {
      const items = scoredOnly.filter((r) => r.domain === d);
      const total = items.length;
      const fulfilling = items.filter((r) => getScore(nonTechScores, r.id, "readinessScore", r.readinessScore) >= 3).length;
      return { domain: d, total, fulfilling };
    });
    // Real project data only; a section the data cannot supply stays empty (see utils/gapReportDraft.js).
    const built = buildReportDraft({ project, domainCounts, overallScore });
    if (!built.ok) {
      message.warning(built.reason);
      return;
    }
    setReportSections(built.sections);
    setIsReportEditMode(false);
  };
  const updateReportField = (key, value) => setReportSections((prev) => withReportField(prev, key, value));
  const updateReportFieldStatus = (key, status) => setReportSections((prev) => withReportFieldStatus(prev, key, status));
  const saveReportDraft = async () => {
    if (!reportCompanyName(project)) {
      message.warning("This project has no company name. Add it to the company profile before saving the report.");
      return;
    }
    // Each section the project data did not supply must be confirmed or marked not applicable first.
    const pending = unconfirmedReportSections(reportSections);
    if (pending.length > 0) {
      message.warning(`Before saving, fill in and confirm, or mark not applicable: ${pending.join(", ")}.`);
      return;
    }
    setReportDraftSaving(true);
    try {
      const saved = await persistGapWizard({ gapReportDraft: reportSections, reportDrafted: true });
      if (saved === false) {
        message.error(lastSaveFailureRef.current || "Failed to save report draft.");
        return;
      }
      setReportDrafted(true);
      setIsReportEditMode(false);
      message.success("Report draft saved.");
    } catch (e) {
      console.error("Failed to save report draft:", e);
      message.error("Failed to save report draft.");
    } finally {
      setReportDraftSaving(false);
    }
  };
  const lockedLaKeys = useMemo(() => {
    if (!isLeadAdvisor) return new Set();
    if (step4Status === "Finalized") return new Set();
    const locked = new Set();
    if (!questionnaireReleased) {
      ["responses", "schedule", "review-scores", "report", "push-dashboard"].forEach((k) => locked.add(k));
    } else if (!responsesReviewed) {
      ["schedule", "review-scores", "report", "push-dashboard"].forEach((k) => locked.add(k));
    } else if (!scoresValidated) {
      ["report", "push-dashboard"].forEach((k) => locked.add(k));
    } else if (!reportDrafted) {
      locked.add("push-dashboard");
    }
    return locked;
  }, [isLeadAdvisor, step4Status, questionnaireReleased, responsesReviewed, scoresValidated, reportDrafted]);
  const completedLaKeys = useMemo(() => {
    if (!isLeadAdvisor) return new Set();
    const c = new Set();
    if (questionnaireReleased) c.add("release");
    if (responsesReviewed) c.add("responses");
    if (scheduledMeetings.length > 0) c.add("schedule");
    if (scoresValidated) c.add("review-scores");
    if (reportDrafted) c.add("report");
    if (pushedToDashboard) c.add("push-dashboard");
    if (step4Status === "Finalized") {
      ["release", "responses", "schedule", "review-scores", "report"].forEach((k) => c.add(k));
    }
    return c;
  }, [
    isLeadAdvisor,
    questionnaireReleased,
    responsesReviewed,
    scheduledMeetings.length,
    scoresValidated,
    reportDrafted,
    pushedToDashboard,
    step4Status,
  ]);
  const laGapAttentionKey = useMemo(() => {
    if (!isLeadAdvisor || step4Status === "Finalized") return null;
    for (const s of LA_SUBSTEPS) {
      if (lockedLaKeys.has(s.key)) continue;
      if (completedLaKeys.has(s.key)) continue;
      return s.key;
    }
    return null;
  }, [isLeadAdvisor, step4Status, lockedLaKeys, completedLaKeys]);
  const laGapAttentionNavOpts = useMemo(
    () =>
      laGapAttentionKey
        ? { attentionTitleByKey: { [laGapAttentionKey]: "Continue here next" } }
        : {},
    [laGapAttentionKey],
  );
  const lockedAdvKeys = useMemo(() => {
    if (!isAdvisor) return new Set();
    const L = new Set();
    if (!toolSetup) {
      ["conduct-assessment", "ai-eval-tech", "review-scores-tech", "share-la"].forEach((k) => L.add(k));
    } else if (!techAssessmentDone) {
      ["ai-eval-tech", "review-scores-tech", "share-la"].forEach((k) => L.add(k));
    } else if (!techAiEvalRun) {
      ["review-scores-tech", "share-la"].forEach((k) => L.add(k));
    } else if (!techScoresValidated) {
      L.add("share-la");
    }
    return L;
  }, [isAdvisor, toolSetup, techAssessmentDone, techAiEvalRun, techScoresValidated]);
  const completedAdvKeys = useMemo(() => {
    if (!isAdvisor) return new Set();
    const c = new Set();
    if (toolSetup) c.add("setup-tool");
    if (techAssessmentDone) c.add("conduct-assessment");
    if (techAiEvalRun) c.add("ai-eval-tech");
    if (techScoresValidated) c.add("review-scores-tech");
    if (sharedWithLA) c.add("share-la");
    return c;
  }, [isAdvisor, toolSetup, techAssessmentDone, techAiEvalRun, techScoresValidated, sharedWithLA]);
  const delegationCommitComplete = useMemo(
    () =>
      delegated &&
      (unansweredQuestions.length === 0 || poolQuestions.length === 0),
    [delegated, unansweredQuestions.length, poolQuestions.length],
  );
  const lockedSpocKeys = useMemo(() => {
    if (!isSPOC) return new Set();
    const lock = new Set();
    if (!spocResponded) {
      ["delegate", "review-approve", "meetings", "report-review"].forEach((k) => lock.add(k));
    } else if (!delegationCommitComplete) {
      ["review-approve", "meetings", "report-review"].forEach((k) => lock.add(k));
    } else if (delegationFinalized !== true) {
      ["meetings", "report-review"].forEach((k) => lock.add(k));
    }
    const reportTabUnlocked = step4Status === "Under Review" || step4Status === "Approved by SPOC" || step4Status === "Finalized";
    if (!reportTabUnlocked) lock.add("report-review");
    return lock;
  }, [isSPOC, spocResponded, delegationCommitComplete, delegationFinalized, step4Status]);
  const ScoreBadge = ({ score, max, notScored }) => notScored ? /* @__PURE__ */ jsx("span", { "data-testid": "score-not-scored", className: "inline-flex items-center justify-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted)]", children: "Not scored" }) : /* @__PURE__ */ jsxs("span", {
    className: `inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-lg text-[10px] font-black ${getRagColor(score, max)}`, children: [
      score,
      "/",
      max
    ]
  });
  const ScoreInput = ({ value, max, onChange }) => /* @__PURE__ */ jsx(
    "input",
    {
      type: "number",
      min: 0,
      max,
      value,
      onChange: (e) => onChange(Math.max(0, Math.min(max, parseInt(e.target.value) || 0))),
      className: "w-14 bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-2 py-1 text-xs font-bold text-center text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20"
    }
  );
  const renderAssessmentTable = (title, items, scoreStore, scoreSetter, editable) => /* @__PURE__ */ jsxs("div", {
    className: "mb-6", children: [
    /* @__PURE__ */ jsx("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-3 pl-2", children: title }),
    /* @__PURE__ */ jsx("div", {
      className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", {
        className: "w-full text-left", children: [
      /* @__PURE__ */ jsx("thead", {
          children: /* @__PURE__ */ jsxs("tr", {
            className: "bg-[var(--color-surface-sunken)] text-[9px] font-black uppercase tracking-[0.12em] text-[color:var(--color-text-muted)]", children: [
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 w-[50px]", children: "#" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 min-w-[280px]", children: title.includes("Technical") || title.includes("Observation") ? "Observation" : "Question / Issue" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 w-[100px] text-center", children: "Controls" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 w-[100px] text-center", children: "Response" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 w-[90px] text-center", children: "Readiness" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 w-[90px] text-center", children: "Expected" }),
        /* @__PURE__ */ jsx("th", { className: "px-4 py-3 min-w-[180px]", children: "Next Steps" })
            ]
          })
        }),
      /* @__PURE__ */ jsx("tbody", {
          className: "divide-y divide-[color:var(--color-border)]", children: items.map((item, i) => {
            const rs = getScore(scoreStore, item.id, "readinessScore", item.readinessScore);
            const es = getScore(scoreStore, item.id, "expectedScore", item.expectedScore);
            return /* @__PURE__ */ jsxs("tr", {
              // Commandment IV / lesson [[silent-zero-scoring-ai-fallback]]: a fallback row is
              // structurally identical to a legitimate 0/0/0 score \u2014 item.aiFailed is the only
              // signal, so it gets a distinct amber row + an "AI failed" tag, never a silent zero.
              className: `transition-all ${item.aiFailed ? "bg-[var(--color-warn-bg)] hover:bg-[var(--color-warn-bg)]" : "hover:bg-[var(--color-info-bg)]"}`, children: [
          /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-xs font-bold text-[color:var(--color-text-muted)]", children: i + 1 }),
          /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-xs text-[color:var(--color-text-muted-dark)] leading-relaxed", children: item.label }),
          /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx(GapControlsCell, { item }) }),
          /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: /* @__PURE__ */ jsx("span", { className: `px-2 py-0.5 rounded-lg text-[10px] font-bold ${item.response === "Yes" || item.response === "Compliant" ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" : item.response === "Partial" ? "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)]" : item.response === "NA" ? "bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted)]" : item.response === "No" || item.response === "Non-compliant" ? "bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)]" : "bg-[var(--color-surface-sunken)] text-[color:var(--color-text-muted)]"}`, children: item.response || "-" }) }),
          /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: editable ? /* @__PURE__ */ jsx(ScoreInput, { value: item.requireFullOverride && item.aiFailed && scoreStore[item.id]?.readinessScore === undefined ? "" : rs, max: 5, onChange: (v) => updateScore(scoreSetter, item.id, "readinessScore", v, item.requireFullOverride && item.aiFailed ? {} : item) }) : /* @__PURE__ */ jsx(ScoreBadge, { score: rs, max: 5, notScored: !isGapRowScored(scoreStore, item) }) }),
          /* @__PURE__ */ jsx("td", { className: "px-4 py-3 text-center", children: editable ? /* @__PURE__ */ jsx(ScoreInput, { value: item.requireFullOverride && item.aiFailed && scoreStore[item.id]?.expectedScore === undefined ? "" : es, max: 5, onChange: (v) => updateScore(scoreSetter, item.id, "expectedScore", v, item.requireFullOverride && item.aiFailed ? {} : item) }) : /* @__PURE__ */ jsx(ScoreBadge, { score: es, max: 5, notScored: !isGapRowScored(scoreStore, item) }) }),
          /* @__PURE__ */ jsxs("td", { className: "px-4 py-3 text-[10px] text-[color:var(--color-text-muted)] font-medium", children: [
            item.aiFailed && /* @__PURE__ */ jsx(Tooltip, { title: describeAiFailureReason(item.aiFailureReason), children: /* @__PURE__ */ jsx(Tag, { color: "orange", className: "mr-1.5 align-middle", children: "AI failed" }) }),
            item.aiFailed && item.requireFullOverride && /* @__PURE__ */ jsx("div", { "data-testid": "row-ai-failed-state", className: "mb-1 font-bold text-[color:var(--color-warn-fg)]", children: `No AI score for this row (${describeAiFailureReason(item.aiFailureReason)}). Retry failed rows, or ${editable ? "enter BOTH Readiness and Expected yourself" : "an Advisor must override both scores"} before approval.` }),
            item.humanOverridden === true && /* @__PURE__ */ jsx(Tag, { color: "purple", className: "mr-1.5 align-middle", children: "Human-overridden" }),
            item.humanOverridden === true && item.overrideNote && /* @__PURE__ */ jsx("div", { "data-testid": `override-note-shown-${item.id}`, className: "mb-1 text-[color:var(--color-text-muted-dark)]", children: `Override reason: ${item.overrideNote}` }),
            editable && item.requireFullOverride && techRowIsOverridden(item, scoreStore[item.id]) && /* @__PURE__ */ jsx("textarea", {
              "data-testid": `override-note-${item.id}`, "aria-label": "Why are you changing this score?", rows: 2, maxLength: 1000,
              placeholder: `Why are you changing this score? (required, at least ${OVERRIDE_NOTE_MIN_LENGTH} characters)`,
              value: scoreStore[item.id]?.overrideNote ?? "",
              onChange: (e) => updateScore(scoreSetter, item.id, "overrideNote", e.target.value, item.requireFullOverride && item.aiFailed ? {} : item),
              className: "w-full mb-1 border border-[color:var(--color-warn-line)] rounded-lg p-1.5 text-[10px] text-[color:var(--color-text-muted-dark)] bg-[var(--color-bg-white)]",
            }),
            item.reviewedBy?.email && /* @__PURE__ */ jsx("div", { "data-testid": "row-reviewed-by", className: "mb-1 text-[color:var(--color-text-muted)]", children: `Reviewed by ${item.reviewedBy.email}${item.reviewedAsAdvisor ? ` on behalf of ${item.reviewedAsAdvisor.email || "Advisor"}` : ""}` }),
            // Commandment IV \u2014 explainability. `reasoningRefs` (top reasoning bullets the AI
            // gave for this score) shown via a tooltip icon when present; `confidence` is
            // always null in this codebase today (never fabricated \u2014 WRAPPER.md "Confidence \u2014
            // deferral status") so it renders as an explicit "not available" note, not a badge.
            Array.isArray(item.reasoningRefs) && item.reasoningRefs.length > 0 && /* @__PURE__ */ jsx(Tooltip, {
              title: /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("div", { className: "font-bold mb-1", children: "The model says" }),
                /* @__PURE__ */ jsx("ul", { className: "list-disc list-inside space-y-0.5", children: item.reasoningRefs.map((r, i) => /* @__PURE__ */ jsx("li", { children: r }, i)) }),
              ] }),
              children: /* @__PURE__ */ jsx(Tag, { color: "blue", className: "mr-1.5 align-middle", children: "Why?" }),
            }),
            item.nextSteps || "-",
            // Commandment IV - stored run-agreement for AI-scored rows. The score is computed per
            // BATCH of observations evaluated together (confidenceScope === "batch"), not per row,
            // and measures stability, never correctness. Absent values are stated as such by the
            // indicator ("not computed for this output") - never a fabricated figure; every non-failed row shows one of the
            // honest states (a row with nothing stored says so rather than rendering nothing).
            !item.aiFailed && /* @__PURE__ */ jsxs("details", {
              className: "mt-1.5", "data-testid": "row-agreement-details", children: [
                /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "AI agreement & reasoning" }),
                /* @__PURE__ */ jsx(AiAgreementIndicator, {
                  confidence: item.confidence,
                  runs: item.confidenceRuns,
                  validSamples: item.validSamples,
                  reasoningRefs: item.agreementNotes,
                  unavailableReason: item.confidenceUnavailableReason,
                  scopeLabel: item.confidenceScope === "batch" ? "This score covers the whole batch of observations evaluated together, not this row alone." : undefined,
                }),
              ]
            }),
          ] })
              ]
            }, item.id);
          })
        })
        ]
      })
    })
    ]
  });
  const domainGroups = useMemo(() => {
    const groups = {};
    scoredQuestions.forEach((r) => {
      const d = r.domain || UNCLASSIFIED_DOMAIN;
      if (!groups[d]) groups[d] = [];
      groups[d].push(r);
    });
    return groups;
  }, [scoredQuestions]);
  /**
   * Sections 3-6 of the printable Gap Analysis Report (Applicable Requirements
   * domain summary, Non-Technical Assessment, Issues — Threats — Opportunities,
   * Technical Assessment). Shared verbatim by the Lead Advisor's "report" tab and
   * the SPOC's read-only "report-review" tab — Task #75 Gap B found the SPOC view
   * was a hand-copied duplicate of this markup rather than a call through a shared
   * helper, which is how it silently missed the aiFailed row-marker treatment that
   * Task #72/#74 already added to the interactive review-scores tables
   * (renderAssessmentTable, above). Routing both surfaces through this one function
   * means a future fix here can never miss one of them again — same shape as the
   * indirect-reader risk called out in lesson [[chokepoint-bypassed-by-indirect-reader]].
   *
   * No retry affordance in this markup by design: retrying AI scoring only happens
   * on the Lead Advisor's "Review & Validate Scores" panel; this is a read-only
   * report render for both personas, so the amber row + "AI failed" tag is purely
   * informational here (lesson [[silent-zero-scoring-ai-fallback]] — a fallback
   * 0/0/0 must never render identically to a legitimate zero score, including
   * inside the printable/exportable report).
   */
  // Backlog #16 — Gap Analysis draft: sections 3-6 of the assembled report (shared verbatim
  // with the SPOC read-only review render below) are wrapped in the shared CollapsiblePane
  // primitive, keyed by a caller-supplied pageId so the two renders' collapse state never
  // cross-contaminate. Content and all reportDocEdit/step4Status gating is unchanged.
  // The pushed readiness number is questionnaire-based; validated technical scores do NOT feed it. The snapshot carries
  // a separate techFindings summary (null when waived or never validated) and technicalAssessmentWaived.
  const renderTechFindings = (prefix) => {
    const snap = gw.gapDashboardSnapshot && typeof gw.gapDashboardSnapshot === "object" ? gw.gapDashboardSnapshot : null;
    const waived = snap ? snap.technicalAssessmentWaived === true : !!techWaiver;
    if (waived) {
      return /* @__PURE__ */ jsx("p", { "data-testid": `${prefix}-tech-waived`, className: "text-[11px] font-bold text-[color:var(--color-warn-fg)]", children: "Technical assessment not applicable (waived): no technical findings are included." });
    }
    const f = snap?.techFindings;
    if (!f || typeof f.totalObservations !== "number") return null;
    const sev = f.bySeverity || {};
    const band = f.byScoreBand || {};
    return /* @__PURE__ */ jsxs("div", { "data-testid": `${prefix}-tech-findings`, className: "text-[11px] text-[color:var(--color-text-muted-dark)] space-y-0.5", children: [
      /* @__PURE__ */ jsx("p", { className: "font-bold text-[color:var(--color-text-muted-dark)]", children: `Technical findings (validated): ${f.totalObservations} observation${f.totalObservations === 1 ? "" : "s"}` }),
      /* @__PURE__ */ jsx("p", { children: `By severity: ${sev.high ?? 0} high, ${sev.medium ?? 0} medium, ${sev.low ?? 0} low${sev.unclassified ? `, ${sev.unclassified} unclassified` : ""}.` }),
      /* @__PURE__ */ jsx("p", { children: `By score band: ${band.red ?? 0} red, ${band.amber ?? 0} amber, ${band.green ?? 0} green.` }),
      /* @__PURE__ */ jsx("p", { className: "text-[color:var(--color-text-muted)]", children: "Shown separately; not part of the readiness figure." }),
    ] });
  };
  const renderReportAssessmentBody = (pageId) => /* @__PURE__ */ jsxs(Fragment, { children: [
    renderTechFindings("gap-report"),
    /* @__PURE__ */ jsx(CollapsiblePane, { id: "applicable-requirements", pageId, title: "3. Applicable Requirements", children: [
      /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-sm", children: [
        /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "bg-[var(--color-bg-alt)]", children: [
          /* @__PURE__ */ jsx("th", { className: "border p-2 text-left font-bold", children: "#" }),
          /* @__PURE__ */ jsx("th", { className: "border p-2 text-left font-bold", children: "Domain" }),
          /* @__PURE__ */ jsx("th", { className: "border p-2 text-center font-bold", children: "Requirements" }),
          /* @__PURE__ */ jsx("th", { className: "border p-2 text-center font-bold", children: "Fulfilling" }),
          /* @__PURE__ */ jsx("th", { className: "border p-2 text-center font-bold", children: "Score (RAG)" })
        ] }) }),
        /* @__PURE__ */ jsxs("tbody", { children: [
          Object.entries(domainGroups).filter(([domain]) => domain !== UNCLASSIFIED_DOMAIN).map(([domain, qs], i) => {
            const scoredQs = qs.filter((q) => isGapRowScored(nonTechScores, q));
            const total = scoredQs.length;
            const fulfilling = scoredQs.filter((q) => getScore(nonTechScores, q.id, "readinessScore", q.readinessScore) >= 3).length;
            const pct = total > 0 ? Math.round(fulfilling / total * 100) : 0;
            const domainAvg = total > 0 ? scoredQs.reduce((sum, q) => sum + getScore(nonTechScores, q.id, "readinessScore", q.readinessScore), 0) / total : 0;
            return /* @__PURE__ */ jsxs("tr", { children: [
              /* @__PURE__ */ jsx("td", { className: "border p-2", children: i + 1 }),
              /* @__PURE__ */ jsx("td", { className: "border p-2", children: domain }),
              /* @__PURE__ */ jsx("td", { className: "border p-2 text-center", children: total }),
              /* @__PURE__ */ jsx("td", { className: "border p-2 text-center", children: fulfilling }),
              /* @__PURE__ */ jsx("td", { className: "border p-2 text-center", children: /* @__PURE__ */ jsxs("span", { className: `px-2 py-0.5 rounded text-xs font-bold text-white ${RAG_BG_CLASS[ragBandForAvg(domainAvg) || "red"]}`, children: total > 0 ? [pct, "%"] : "Not scored" }) })
            ] }, domain);
          }),
          /* @__PURE__ */ jsxs("tr", { className: "bg-[var(--color-surface-sunken)] font-bold", children: [
            /* @__PURE__ */ jsx("td", { className: "border p-2", colSpan: 2, children: "Overall" }),
            /* @__PURE__ */ jsx("td", { className: "border p-2 text-center", children: scoredQuestions.filter((r) => isGapRowScored(nonTechScores, r)).length }),
            /* @__PURE__ */ jsx("td", { className: "border p-2 text-center", children: scoredQuestions.filter((r) => isGapRowScored(nonTechScores, r) && getScore(nonTechScores, r.id, "readinessScore", r.readinessScore) >= 3).length }),
            /* @__PURE__ */ jsx("td", { className: "border p-2 text-center", children: /* @__PURE__ */ jsxs("span", { className: `px-2 py-0.5 rounded text-xs font-bold text-white ${RAG_BG_CLASS[ragBandForPercent(overallScore) || "red"]}`, children: [overallScore, "%"] }) })
          ] })
        ] })
      ] }),
      notScoredCount > 0 && /* @__PURE__ */ jsx("p", { "data-testid": "report-not-scored-note", className: "mt-2 text-[11px] font-bold text-[color:var(--color-warn-fg)]", children: `${notScoredLabel(notScoredCount)} - excluded from the counts and the readiness figure above.` })
    ]
    }),
    /* @__PURE__ */ jsx(CollapsiblePane, { id: "non-technical-assessment", pageId, title: "4. Non-Technical Assessment", children:
      Object.entries(domainGroups).map(([domain, qs]) => /* @__PURE__ */ jsxs("div", { className: "mb-4", children: [
        /* @__PURE__ */ jsx("h3", { className: "text-base font-bold mb-2", children: domain }),
        /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-[11px] mb-4", children: [
          /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "bg-[var(--color-bg-alt)]", children: [
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left", children: "Question" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[60px]", children: "Controls" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[70px]", children: "Response" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[65px]", children: "Readiness" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[65px]", children: "Expected" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 w-[120px]", children: "Next Steps" })
          ] }) }),
          /* @__PURE__ */ jsx("tbody", { children: qs.map((q) => /* @__PURE__ */ jsxs("tr", {
            // lesson [[silent-zero-scoring-ai-fallback]]: a fallback 0/0/0 must never render
            // identically to a legitimate zero score, including in the printable report.
            className: q.aiFailed ? "bg-[var(--color-warn-bg)]" : undefined,
            children: [
            /* @__PURE__ */ jsx("td", { className: "border p-1.5", children: q.question }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: fmtControlCount(q.applicableControls) }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: q.response }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: isGapRowScored(nonTechScores, q) ? getScore(nonTechScores, q.id, "readinessScore", q.readinessScore) : "Not scored" }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: isGapRowScored(nonTechScores, q) ? getScore(nonTechScores, q.id, "expectedScore", q.expectedScore) : "Not scored" }),
            /* @__PURE__ */ jsxs("td", { className: "border p-1.5", children: [
              q.aiFailed && /* @__PURE__ */ jsx(Tooltip, { title: describeAiFailureReason(q.aiFailureReason), children: /* @__PURE__ */ jsx(Tag, { color: "orange", className: "mr-1.5 align-middle", children: "AI failed" }) }),
              q.nextSteps
            ] })
          ] }, q.id)) })
        ] })
      ] }, domain))
    }),
    /* @__PURE__ */ jsx(CollapsiblePane, { id: "issues-threats-opportunities", pageId, title: "5. Issues - Threats - Opportunities", children:
      Object.entries(realIssueResponses).map(([cat, items]) => /* @__PURE__ */ jsxs("div", { className: "mb-4", children: [
        /* @__PURE__ */ jsx("h3", { className: "text-base font-bold mb-2", children: cat }),
        /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-[11px] mb-4", children: [
          /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "bg-[var(--color-bg-alt)]", children: [
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left", children: "Issue" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[60px]", children: "Controls" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[70px]", children: "Response" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[65px]", children: "Readiness" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[65px]", children: "Expected" }),
            /* @__PURE__ */ jsx("th", { className: "border p-1.5 w-[120px]", children: "Next Steps" })
          ] }) }),
          /* @__PURE__ */ jsx("tbody", { children: items.map((it) => /* @__PURE__ */ jsxs("tr", {
            className: it.aiFailed ? "bg-[var(--color-warn-bg)]" : undefined,
            children: [
            /* @__PURE__ */ jsx("td", { className: "border p-1.5", children: it.label }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: fmtControlCount(it.applicableControls) }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: it.response }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: isGapRowScored(issueScores, it) ? getScore(issueScores, it.id, "readinessScore", it.readinessScore) : "Not scored" }),
            /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: isGapRowScored(issueScores, it) ? getScore(issueScores, it.id, "expectedScore", it.expectedScore) : "Not scored" }),
            /* @__PURE__ */ jsxs("td", { className: "border p-1.5", children: [
              it.aiFailed && /* @__PURE__ */ jsx(Tooltip, { title: describeAiFailureReason(it.aiFailureReason), children: /* @__PURE__ */ jsx(Tag, { color: "orange", className: "mr-1.5 align-middle", children: "AI failed" }) }),
              it.nextSteps
            ] })
          ] }, it.id)) })
        ] })
      ] }, cat))
    }),
    /* @__PURE__ */ jsx(CollapsiblePane, { id: "technical-assessment", pageId, title: "6. Technical Assessment", children:
      /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-[11px]", children: [
        /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "bg-[var(--color-bg-alt)]", children: [
          /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left", children: "Observation" }),
          /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[60px]", children: "Controls" }),
          /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[70px]", children: "Response" }),
          /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[65px]", children: "Readiness" }),
          /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[65px]", children: "Expected" }),
          /* @__PURE__ */ jsx("th", { className: "border p-1.5 w-[120px]", children: "Next Steps" })
        ] }) }),
        /* @__PURE__ */ jsx("tbody", { children: techObservations.map((t) => /* @__PURE__ */ jsxs("tr", {
          className: t.aiFailed ? "bg-[var(--color-warn-bg)]" : undefined,
          children: [
          /* @__PURE__ */ jsx("td", { className: "border p-1.5", children: t.observation }),
          /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: fmtControlCount(t.applicableControls) }),
          /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: t.response }),
          /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: getScore(techScores, t.id, "readinessScore", t.readinessScore) }),
          /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: getScore(techScores, t.id, "expectedScore", t.expectedScore) }),
          /* @__PURE__ */ jsxs("td", { className: "border p-1.5", children: [
            t.aiFailed && /* @__PURE__ */ jsx(Tooltip, { title: describeAiFailureReason(t.aiFailureReason), children: /* @__PURE__ */ jsx(Tag, { color: "orange", className: "mr-1.5 align-middle", children: "AI failed" }) }),
            t.nextSteps
          ] })
        ] }, t.id)) })
      ] })
    }),
    // Backlog #29, R040 (Director REQUEST-CHANGES follow-up): an approved tool-test summary
    // must actually land in the report the "Approve — add to report" button promises, not just
    // stay local to the Advisor's Conduct Assessment tab. Renders from `toolTestSummaries`
    // (Commandment IV: only Advisor-approved entries ever reach this array — see the state
    // declaration + acceptToolTestSummary above).
    // Backlog #29, R040: only server-APPROVED summaries (Advisor approval recorded server-side) reach the
    // report. Company roles cannot read raw tool-test evidence (API 403), so the section is not offered to them.
    canViewToolTestEvidence && !(isToolTestSpocView && (toolTestEvidence.status === "denied" || toolTestEvidence.status === "error")) && /* @__PURE__ */ jsx(CollapsiblePane, { id: "tool-test-summaries", pageId, title: "7. Tool-Test Assessment Summaries", children:
      toolTestEvidence.status === "unavailable"
        ? /* @__PURE__ */ jsx("p", { "data-testid": "tool-test-not-presented", className: "text-sm text-[color:var(--color-text-muted)] italic", children: SPOC_TOOL_TESTS_NOT_PRESENTED_TEXT })
        : toolTestEvidence.status === "error" || toolTestEvidence.status === "denied"
        ? /* @__PURE__ */ jsx("p", { className: "text-sm text-[color:var(--color-danger-fg)]", children: `Tool-test summaries could not be loaded (${toolTestEvidence.error}).` })
        : toolTestSummaries.length === 0 && !toolTestNextCursor
        ? /* @__PURE__ */ jsx("p", { className: "text-sm text-[color:var(--color-text-muted)] italic", children: toolTestEvidence.status === "loading" ? "Loading tool-test summaries…" : "No tool-test summaries approved yet." })
        : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("p", {
              className: "text-[10px] text-[color:var(--color-text-muted)] mb-2",
              children: "AI drafted, Advisor-approved. Run-agreement (where computed) is shown under each summary; it measures stability, not correctness.",
            }),
            !isToolTestSpocView && toolTestCounts && /* @__PURE__ */ jsx("p", {
              "data-testid": "tool-test-counts", className: "text-[10px] text-[color:var(--color-text-muted)] mb-2",
              children: `Approved ${toolTestCounts.APPROVED ?? 0} · Unresolved drafts ${toolTestCounts.AI_DRAFT ?? 0} · Rejected ${toolTestCounts.REJECTED ?? 0}`,
            }),
            /* @__PURE__ */ jsxs("table", { className: "w-full border-collapse text-[11px]", children: [
              /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { className: "bg-[var(--color-bg-alt)]", children: [
                /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left w-[90px]", children: "Tool" }),
                /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-center w-[70px]", children: "Severity" }),
                /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left", children: "Summary" }),
                /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left w-[160px]", children: "Mapped Controls" }),
                /* @__PURE__ */ jsx("th", { className: "border p-1.5 text-left w-[150px]", children: isToolTestSpocView ? "Approval" : "Approved By" })
              ] }) }),
              /* @__PURE__ */ jsx("tbody", { children: toolTestSummaries.map((t) => /* @__PURE__ */ jsxs("tr", { children: [
                /* @__PURE__ */ jsx("td", { className: "border p-1.5 capitalize", children: t.toolType }),
                /* @__PURE__ */ jsx("td", { className: "border p-1.5 text-center", children: /* @__PURE__ */ jsx("span", {
                  className: `px-2 py-0.5 rounded text-[9px] font-bold text-white ${t.severity === "high" ? "bg-rose-500" : t.severity === "medium" ? "bg-amber-500" : "bg-emerald-500"}`,
                  children: t.severity,
                }) }),
                /* @__PURE__ */ jsxs("td", { className: "border p-1.5", children: [
                  /* @__PURE__ */ jsx("ul", {
                    className: "list-disc list-inside space-y-0.5",
                    children: (t.summary || []).map((b, i) => /* @__PURE__ */ jsx("li", { children: b }, i)),
                  }),
                  // SPOC projection carries only `confidence`; Advisor DTO carries the full meta.
                  /* @__PURE__ */ jsx(AiAgreementIndicator, isToolTestSpocView
                    ? { confidence: t.confidence, approved: true }
                    : {
                        approved: true,
                        confidence: t.meta?.confidence,
                        runs: t.meta?.confidenceRuns,
                        validSamples: t.meta?.validSamples,
                        reasoningRefs: t.meta?.reasoningRefs,
                        unavailableReason: t.meta?.confidenceUnavailableReason,
                      }),
                ] }),
                /* @__PURE__ */ jsx("td", { className: "border p-1.5", children: (t.mapped_controls || []).map((c) => c.id).join(", ") || "-" }),
                /* @__PURE__ */ jsxs("td", { className: "border p-1.5", children: [
                  isToolTestSpocView ? (t.approvedByRole ? `Approved by ${String(t.approvedByRole).replace(/_/g, " ")}` : "Advisor-approved") : (t.approvedBy?.email || "-"),
                  !isToolTestSpocView && t.approvedBy?.onBehalfOfUserId ? " (approved via Lead Advisor on behalf)" : "",
                  t.approvedAt ? /* @__PURE__ */ jsx("div", { className: "text-[color:var(--color-text-muted)]", children: formatDdMmYyyy(t.approvedAt) }) : null,
                  t.selfApproved ? /* @__PURE__ */ jsx("div", { "data-testid": "self-approved-marker", className: "mt-1 text-[color:var(--color-warn-fg)] font-semibold", children: SELF_APPROVED_TEXT }) : null,
                  !isToolTestSpocView && t.sourceRetained ? /* @__PURE__ */ jsx("button", {
                    type: "button", disabled: sourceDownloadBusyId === t.id,
                    onClick: () => void downloadToolTestSource(t.id, t.sourceFileName),
                    className: "mt-1 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] disabled:opacity-50",
                    children: sourceDownloadBusyId === t.id ? "Downloading…" : "Download original",
                  }) : null,
                ] })
              ] }, t.id)) })
            ] }),
            toolTestNextCursor && /* @__PURE__ */ jsx("button", {
              type: "button", "data-testid": "tool-test-report-load-more", onClick: () => void loadMoreToolTestSummaries(), disabled: toolTestLoadingMore,
              className: "mt-2 px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] disabled:opacity-50",
              children: toolTestLoadingMore ? "Loading…" : "Load more",
            }),
          ] }),
    })
  ] });
  const releasedDomainGroups = useMemo(() => {
    const groups = {};
    releasedQuestions.forEach((q) => {
      const d = q.domain || UNCLASSIFIED_DOMAIN;
      if (!groups[d]) groups[d] = [];
      groups[d].push(q);
    });
    return groups;
  }, [releasedQuestions]);
  const releasedDomainCount = Object.keys(releasedDomainGroups).length;
  const statusBadge = (status) =>
    /* @__PURE__ */ jsx("span", {
      className: `px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
        status === "Draft"
          ? "bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]"
          : status === "Under Review"
            ? "bg-[var(--color-info-bg)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)] animate-pulse"
            : status === "Approved by SPOC"
              ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]"
              : "bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]"
      }`,
      children: status,
    });
  const refreshSpocAwaitingCount = useCallback(async () => {
    if (!isSPOC || !project?.id || !fetchMeetings) return;
    const all = await fetchMeetings();
    const n = (all || []).filter(
      (m) =>
        m.projectId === project.id &&
        m.status === "awaiting_delegates" &&
        isMeetingFromGapAnalysis(m) &&
        normalizeMeetingAttendees(m.attendees).length === 0,
    ).length;
    setSpocAwaitingDelegatesCount(n);
  }, [isSPOC, project?.id, fetchMeetings]);
  const loadSpocProjectMeetings = useCallback(async () => {
    if (!project?.id || !fetchMeetings) return;
    setSpocMeetingsLoading(true);
    try {
      const all = await fetchMeetings();
      const list = (all || []).filter((m) => m.projectId === project.id && isMeetingFromGapAnalysis(m));
      setSpocProjectMeetings(list);
      setSpocAwaitingDelegatesCount(
        list.filter(
          (m) => m.status === "awaiting_delegates" && normalizeMeetingAttendees(m.attendees).length === 0,
        ).length,
      );
    } finally {
      setSpocMeetingsLoading(false);
      setSpocMeetingsHydrated(true);
    }
  }, [fetchMeetings, project?.id]);
  const toggleSpocMeetingDelegatePick = useCallback((meetingId, userId) => {
    setSpocDelegatePicksByMeeting((prev) => {
      const cur = Array.isArray(prev[meetingId]) ? [...prev[meetingId]] : [];
      const idx = cur.indexOf(userId);
      if (idx >= 0) cur.splice(idx, 1);
      else cur.push(userId);
      return { ...prev, [meetingId]: cur };
    });
  }, []);
  const confirmSpocDelegatesForMeeting = useCallback(async (meetingId, pickIds) => {
    const ids = Array.isArray(pickIds) ? pickIds : [];
    if (ids.length === 0) return;
    setSpocSavingMeetingId(meetingId);
    const attendees = spocTabProjectMembers.filter((m) => ids.includes(m.id)).map((m) => ({
      userId: m.id,
      name: m.name || m.email,
      email: m.email,
      role: m.role
    }));
    const result = await updateMeetingAttendees(meetingId, attendees);
    setSpocSavingMeetingId(null);
    if (result) {
      setSpocDelegatePicksByMeeting((prev) => {
        const next = { ...prev };
        delete next[meetingId];
        return next;
      });
      await loadSpocProjectMeetings();
      await refreshSpocAwaitingCount();
    }
  }, [spocTabProjectMembers, updateMeetingAttendees, loadSpocProjectMeetings, refreshSpocAwaitingCount]);
  const spocMeetingsAwaiting = useMemo(
    () =>
      spocProjectMeetings.filter(
        (m) => m.status === "awaiting_delegates" && normalizeMeetingAttendees(m.attendees).length === 0,
      ),
    [spocProjectMeetings],
  );
  const spocMeetingsScheduled = useMemo(() => {
    return spocProjectMeetings.filter((m) => {
      const st = m.status || "";
      if (st === "cancelled" || st === "completed") return false;
      if (st === "scheduled" || st === "rescheduled" || st === "confirmed") return true;
      if (st === "awaiting_delegates" && normalizeMeetingAttendees(m.attendees).length > 0) return true;
      return false;
    });
  }, [spocProjectMeetings]);
  const completedSpocKeys = useMemo(() => {
    if (!isSPOC) return new Set();
    const c = new Set();
    if (spocResponded) c.add("respond");
    if (delegationCommitComplete && delegated) c.add("delegate");
    /** Review & Finalize Delegation completes when delegation has been sent to delegates */
    if (delegationFinalized === true) {
      c.add("review-approve");
    }
    /**
     * Meeting requests: separate from finalize — green only after meetings are loaded and no row
     * still needs SPOC delegate assignment (not when only delegationFinalized flips).
     */
    if (
      delegationFinalized === true &&
      spocMeetingsHydrated &&
      !spocMeetingsLoading &&
      spocMeetingsAwaiting.length === 0
    ) {
      c.add("meetings");
    }
    if (step4Status === "Approved by SPOC" || step4Status === "Finalized") c.add("report-review");
    return c;
  }, [
    isSPOC,
    spocResponded,
    delegationCommitComplete,
    delegated,
    delegationFinalized,
    step4Status,
    spocMeetingsHydrated,
    spocMeetingsLoading,
    spocMeetingsAwaiting.length,
  ]);
  useEffect(() => {
    if (!delegationFinalized) setSpocMeetingsHydrated(false);
  }, [delegationFinalized]);
  useEffect(() => {
    if (!isSPOC || !project?.id || delegationFinalized !== true) return;
    setSpocMeetingsHydrated(false);
    loadSpocProjectMeetings();
  }, [isSPOC, project?.id, delegationFinalized, loadSpocProjectMeetings]);
  useEffect(() => {
    if (!isSPOC || spocSubStep !== "meetings" || !project?.id) return;
    loadSpocProjectMeetings();
  }, [isSPOC, spocSubStep, project?.id, loadSpocProjectMeetings]);
  useEffect(() => {
    if (!isSPOC || !project?.id) return;
    refreshSpocAwaitingCount();
  }, [isSPOC, project?.id, refreshSpocAwaitingCount]);
  useEffect(() => {
    setSpocDelegatePicksByMeeting({});
    setSpocMeetingsHydrated(false);
  }, [project?.id]);
  useEffect(() => {
    if (!isSPOC || spocSubStep !== "meetings" || !project?.id) return;
    let cancelled = false;
    setSpocRosterLoading(true);
    (async () => {
      const mem = await fetchProjectMembers(project.id);
      if (!cancelled) {
        setSpocTabProjectMembers(Array.isArray(mem) ? mem : []);
        setSpocRosterLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSPOC, spocSubStep, project?.id, fetchProjectMembers]);
  useEffect(() => {
    if (!isLeadAdvisor || !project?.id || laSubStep !== "responses" || !fetchProjectProfile) return;
    fetchProjectProfile(project.id);
  }, [isLeadAdvisor, project?.id, laSubStep, fetchProjectProfile]);
  useEffect(() => {
    if (!isLeadAdvisor || !project?.id || laSubStep !== "schedule") return;
    syncScheduledMeetingsFromServer();
  }, [isLeadAdvisor, project?.id, laSubStep, syncScheduledMeetingsFromServer]);
  useEffect(() => {
    if (!isLeadAdvisor || !project?.id) return;
    const onVisibleOrFocus = () => {
      if (document.visibilityState !== "visible" || laSubStep !== "schedule") return;
      syncScheduledMeetingsFromServer();
    };
    window.addEventListener("focus", onVisibleOrFocus);
    document.addEventListener("visibilitychange", onVisibleOrFocus);
    return () => {
      window.removeEventListener("focus", onVisibleOrFocus);
      document.removeEventListener("visibilitychange", onVisibleOrFocus);
    };
  }, [isLeadAdvisor, project?.id, laSubStep, syncScheduledMeetingsFromServer]);
  /** Report stays editable while step4 is Draft (e.g. after Save draft) until Send for Review; do not treat as read-only. Schedule can host multiple kickoffs/interviews; do not treat as read-only in Draft just because at least one meeting exists (see completedLaKeys). When Finalized, all substeps except Push are view-only. */
  const laSubStepReadOnly =
    isLeadAdvisor &&
    ((completedLaKeys.has(laSubStep) &&
      !(laSubStep === "report" && step4Status === "Draft") &&
      !(laSubStep === "schedule" && step4Status === "Draft")) ||
      (step4Status === "Finalized" && laSubStep !== "push-dashboard"));
  /**
   * Retries only the rows that failed AI scoring. Primary path: map each
   * failedBatches[] {startIndex,size} range back onto the exact payload array
   * last sent to /gap-analysis/ai-scores (lastAiScorePayloadRef). Fallback:
   * any row still flagged aiFailed in persisted state but not covered by that
   * mapping (e.g. after a reload, when the ref is empty) is retried directly
   * off its own current data — so there is never a perpetual AI-failed state
   * with no way out (Commandment IV).
   */
  const handleRetryFailedAiScoreBatches = useCallback(async () => {
    // Defense in depth: the retry button already passes disabled={laSubStepReadOnly} to
    // match every sibling control on this panel, but a Finalized gap-analysis step must
    // stay locked even if invoked another way.
    if (!project?.id || laSubStepReadOnly) return;
    const basis = lastAiScorePayloadRef.current || [];
    const rangeQuestions = [];
    for (const fb of aiScoreFailedBatches) {
      const start = Number(fb?.startIndex) || 0;
      const size = Number(fb?.size) || 0;
      for (let i = start; i < start + size; i++) {
        if (basis[i]) rangeQuestions.push(basis[i]);
      }
    }
    const seen = new Set(rangeQuestions.map((q) => q.id));
    const extra = buildAiScorePayload().filter((q) => failedAiScoreRowIds.has(q.id) && !seen.has(q.id));
    const retryQuestions = [...rangeQuestions, ...extra];
    if (retryQuestions.length === 0) return;

    const aiCompany = buildGapAiCompanyContext(project);
    if (!aiCompany.ok) {
      message.warning(aiCompany.reason);
      return;
    }
    setRetryingAiScores(true);
    try {
      const companyCtx = aiCompany.companyContext;
      lastAiScorePayloadRef.current = retryQuestions;
      const result = await generateAiScores(project.id, { questions: retryQuestions, companyContext: companyCtx });
      if (result?.scores) {
        const mergedScores = { ...(gw.aiGeneratedScores || {}) };
        result.scores.forEach((s) => { mergedScores[s.id] = s; });
        await persistGapWizard({ aiGeneratedScores: mergedScores });
        setAiScoreWarnings(result.warnings || []);
        setAiScoreFailedBatches(result.failedBatches || []);
        if ((result.failedBatches || []).length === 0 && (result.warnings || []).length === 0) {
          message.success("Retry succeeded - AI scores updated for the previously failed rows.");
        } else {
          message.warning("Some rows still failed after retry. You can retry again.");
        }
      } else {
        message.error("Retry failed. Please try again.");
      }
    } catch (e) {
      console.error("Retry of failed AI score batches failed:", e);
      message.error("Retry failed. Please try again.");
    }
    setRetryingAiScores(false);
  }, [project, laSubStepReadOnly, aiScoreFailedBatches, generateAiScores, gw.aiGeneratedScores, persistGapWizard, buildAiScorePayload, failedAiScoreRowIds]);
  const advSubStepReadOnly = isAdvisor && completedAdvKeys.has(advSubStep);
  const spocSubStepReadOnly = isSPOC && completedSpocKeys.has(spocSubStep);
  const reportDocEdit = isLeadAdvisor && step4Status === "Draft" && isReportEditMode && !laSubStepReadOnly;
  const reportCanConfirm = isLeadAdvisor && step4Status === "Draft" && !laSubStepReadOnly;
  const reportFieldProps = (key, editable = true) => ({
    fieldKey: key,
    value: reportSections[key],
    status: reportSections.sectionStatus?.[key],
    origin: reportSections.sectionOrigin?.[key],
    editing: editable && reportDocEdit,
    canConfirm: editable && reportCanConfirm,
    onChange: updateReportField,
    onStatus: updateReportFieldStatus,
  });
  const showLdGapReportSidebar = isLeadAdvisor && laSubStep === "report" && step4Status !== "Finalized";
  const gapReportComposerShown =
    ((isSPOC && spocSubStep === "report-review" && step4Status === "Under Review") ||
      (isLeadAdvisor && laSubStep === "report" && step4Status !== "Finalized")) &&
    !(isLeadAdvisor && laSubStepReadOnly) &&
    !(isSPOC && spocSubStepReadOnly);

  const flushSpocRespondDraft = useCallback(() => {
    if (!isSPOC || spocSubStep !== "respond" || spocSubStepReadOnly || spocResponded) return;
    const base = projectProfileRef.current || {};
    const baseWiz =
      base.gapAnalysisWizard && typeof base.gapAnalysisWizard === "object" ? base.gapAnalysisWizard : {};
    const mergedA = { ...(baseWiz.spocAnswers || {}), ...spocAnswersRef.current };
    const mergedN = { ...(baseWiz.spocNotes || {}), ...spocNotesRef.current };
    const mergedD = { ...(baseWiz.spocDocs || {}), ...spocDocsRef.current };
    void persistGapWizard({ spocAnswers: mergedA, spocNotes: mergedN, spocDocs: mergedD });
  }, [isSPOC, spocSubStep, spocSubStepReadOnly, spocResponded, persistGapWizard]);

  const flushSpocRespondDraftRef = useRef(flushSpocRespondDraft);
  flushSpocRespondDraftRef.current = flushSpocRespondDraft;

  /** Debounced autosave so draft answers survive view switches (e.g. Evidence) that unmount ProjectWorkspace. */
  useEffect(() => {
    if (!isSPOC || spocSubStep !== "respond" || spocSubStepReadOnly || spocResponded) return;
    const t = window.setTimeout(() => {
      flushSpocRespondDraft();
    }, 500);
    return () => window.clearTimeout(t);
  }, [
    isSPOC,
    spocSubStep,
    spocSubStepReadOnly,
    spocResponded,
    spocAnswers,
    spocNotes,
    spocDocs,
    flushSpocRespondDraft,
  ]);

  const prevSpocSubStepForDraftRef = useRef(spocSubStep);
  useEffect(() => {
    const prev = prevSpocSubStepForDraftRef.current;
    prevSpocSubStepForDraftRef.current = spocSubStep;
    if (prev === "respond" && spocSubStep !== "respond" && isSPOC) {
      flushSpocRespondDraft();
    }
  }, [spocSubStep, isSPOC, flushSpocRespondDraft]);

  useEffect(() => {
    return () => {
      void flushSpocRespondDraftRef.current();
    };
  }, []);

  return /* @__PURE__ */ jsxs("section", {
    className: "space-y-6 animate-in fade-in slide-in-from-bottom-6 duration-700", children: [
    /* @__PURE__ */ jsxs("div", {
      className: "flex items-center justify-between flex-wrap gap-4", children: [
      /* @__PURE__ */ jsxs("div", {
        className: "flex items-center gap-4", children: [
        /* @__PURE__ */ jsx(StageHeader, { title: "Gap Analysis" }),
          statusBadge(step4Status)
        ]
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "flex items-center gap-3", children: [
          isSPOC && step4Status === "Under Review" && /* @__PURE__ */ jsxs(Fragment, {
            children: [
          /* @__PURE__ */ jsx("button", {
              "data-testid": "send-back-open",
              onClick: () => setSendBackDialog({ reason: "", busy: false, error: null, serverRequired: false }), className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] transition-all shadow-sm", children: "Send for Re-review"
            }),
          /* @__PURE__ */ jsx(Modal, {
            open: !!sendBackDialog, title: "Send the gap analysis back for re-review?", maskClosable: false, closable: !sendBackDialog?.busy,
            onCancel: () => { if (!sendBackDialog?.busy) setSendBackDialog(null); },
            footer: /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
              /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "send-back-cancel", disabled: !!sendBackDialog?.busy, onClick: () => setSendBackDialog(null), className: "px-4 py-2 border border-[color:var(--color-border)] rounded-lg text-xs font-bold disabled:opacity-50", children: "Cancel" }),
              /* @__PURE__ */ jsx("button", {
                type: "button", "data-testid": "send-back-confirm", onClick: () => void sendBackToDraft(),
                disabled: !!sendBackDialog?.busy || (sendBackReasonRequired && !(sendBackDialog?.reason || "").trim()),
                className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed",
                children: sendBackDialog?.busy ? "Sending\u2026" : "Send back",
              }),
            ] }),
            children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 text-sm text-[color:var(--color-text-muted-dark)]", children: [
              /* @__PURE__ */ jsx("p", { "data-testid": "send-back-notice", children: sendBackReasonRequired
                ? "Sending this back replaces the validated technical scores or the pushed dashboard snapshot. Say why; it is kept in the history."
                : "The report returns to the Lead Advisor as a Draft. A reason is optional." }),
              sendBackDialog?.error ? /* @__PURE__ */ jsx("p", { "data-testid": "send-back-error", role: "alert", className: "text-[color:var(--color-danger-fg)] font-medium", children: sendBackDialog.error }) : null,
              /* @__PURE__ */ jsx("label", { htmlFor: "send-back-reason", className: "block text-xs font-bold", children: sendBackReasonRequired ? "Reason (required)" : "Reason (optional)" }),
              /* @__PURE__ */ jsx("textarea", {
                id: "send-back-reason", "data-testid": "send-back-reason", rows: 3, maxLength: 1000, value: sendBackDialog?.reason || "", disabled: !!sendBackDialog?.busy,
                onChange: (e) => setSendBackDialog((d) => (d ? { ...d, reason: e.target.value } : d)),
                className: "w-full border border-[color:var(--color-border)] rounded-lg p-2 text-sm",
              }),
            ] }),
          }),
          /* @__PURE__ */ jsx("button", { onClick: () => setGapAnalysisReviewStatus("Approved by SPOC"), className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg", children: "Approve Report" })
            ]
          }),
          isLeadAdvisor && step4Status === "Approved by SPOC" && /* @__PURE__ */ jsx("button", { onClick: () => {
            // Task #74 (Gap B) / lesson [[silent-zero-scoring-ai-fallback]]: the branded/printable
            // report preview (out of scope for Task #72) renders whatever is in aiGeneratedScores
            // verbatim — an aiFailed row's fallback 0/0/0 would reach the client looking legitimate.
            // Refuse a silent finalize; require an explicit acknowledgement of the failure count.
            const failedRowCount = failedAiScoreRowIds.size + techFailedRowIds.size;
            if (failedRowCount === 0) {
              finalizeLeadGapReport();
              return;
            }
            Modal.confirm({
              title: "Finalize with AI failures?",
              content: `${failedRowCount} row(s) show an AI-scoring failure. Those rows have no score. They are left out of the readiness figure (they are not counted as zero), and the push to the dashboard will state how many items were excluded. Retry failed batches first, or confirm to finalize anyway.`,
              okText: "Finalize anyway",
              okType: "danger",
              cancelText: "Retry failures first",
              onOk: () => finalizeLeadGapReport(),
            });
          }, className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg", children: "Finalize Report" })
        ]
      })
      ]
    }),
      renderGapAnalysisStatusBar(step4Status),
      isLeadAdvisor && /* @__PURE__ */ jsxs("div", {
        className: "space-y-6", children: [
          /* @__PURE__ */ jsx(SubStepNav, { className: "mb-8", steps: buildSubSteps(LA_SUBSTEPS, { lockedKeys: lockedLaKeys, completedKeys: completedLaKeys, badgeByKey: undefined, attentionTitleByKey: laGapAttentionNavOpts.attentionTitleByKey }), activeKey: laSubStep, onSelect: setLaSubStep }),
          laSubStepReadOnly && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] px-4 py-3 text-[11px] font-semibold text-[color:var(--color-ok-fg)]",
            children: "You are viewing a completed step. Actions are disabled; switch to the latest step to continue working."
          }),
          questionnaireReleased && spocWorkComplete !== true && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-warn-bg)] border border-[color:var(--color-warn-line)] rounded-2xl p-6 mb-6 flex items-start gap-4", children: [
        /* @__PURE__ */ jsx("div", { className: "w-10 h-10 rounded-xl bg-[var(--color-warn-bg)] flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsx("span", { className: "text-lg animate-pulse", children: "\u23F3" }) }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black text-[color:var(--color-warn-fg)]", children: "Awaiting SPOC Responses" }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-warn-fg)] mt-1 leading-relaxed", children: "The questionnaire has been released to the Company SPOC. Sub-tabs are locked until the SPOC completes their responses and delegates remaining questions. You will be able to proceed once the SPOC marks their work as complete." }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 mt-3", children: [
            /* @__PURE__ */ jsx("div", { className: "w-2 h-2 rounded-full bg-amber-500 animate-pulse" }),
            /* @__PURE__ */ jsx("span", { className: "text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-warn-fg)]", children: "Waiting for SPOC" })
          ] })
        ] })
            ]
          }),
          spocWorkComplete === true && questionnaireReleased && (laSubStep === "release" || laSubStep === "responses") && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-ok-bg)] border border-[color:var(--color-ok-line)] rounded-2xl p-6 mb-6 flex items-start gap-4", children: [
        /* @__PURE__ */ jsx("div", { className: "w-10 h-10 rounded-xl bg-[var(--color-ok-bg)] flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsx("span", { className: "text-lg", children: "\u2713" }) }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: "SPOC Work Complete" }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-ok-fg)] mt-1 leading-relaxed", children: "The Company SPOC has finalized delegation. Delegates are now responding to their assigned questions. Check the Responses tab to track live progress." })
        ] })
            ]
          }),
          laSubStep === "release" && /* @__PURE__ */ jsxs("div", {
            className: `bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden${laSubStepReadOnly ? " opacity-70" : ""}`, children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Release Gap Analysis Questionnaire" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Release the finalized questionnaire to Company SPOC during the kick-off meeting" })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [
            /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-info-bg)]   rounded-2xl p-5 border border-[color:var(--color-info-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Questionnaire Status" }),
              /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1", children: questionnaireReleased ? "Released" : "Pending" })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-ok-bg)]   rounded-2xl p-5 border border-[color:var(--color-ok-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "Total Questions" }),
              /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1", children: releasedQuestions.length })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-info-bg)]   rounded-2xl p-5 border border-[color:var(--color-info-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Domains" }),
              /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1", children: releasedDomainCount })
                  ]
                })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-surface-sunken)] rounded-2xl p-6 border border-[color:var(--color-border)]", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-4", children: "Questionnaire Summary" }),
                  Object.entries(releasedDomainGroups).map(([domain, qs]) => /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center justify-between py-2 border-b border-[color:var(--color-border)] last:border-0", children: [
              /* @__PURE__ */ jsx("span", { className: "text-xs font-semibold text-[color:var(--color-text-muted-dark)]", children: domain }),
              /* @__PURE__ */ jsxs("span", {
                      className: "px-3 py-1 bg-[var(--color-bg-white)] rounded-lg text-[10px] font-bold text-[color:var(--color-text-muted-dark)] border", children: [
                        qs.length,
                        " questions"
                      ]
                    })
                    ]
                  }, domain))
                ]
              }),
          /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => {
                    setQuestionnaireReleased(true);
                    persistGapWizard({ questionnaireReleased: true, laSubStep: "release" });
                  },
                  disabled: questionnaireReleased || laSubStepReadOnly,
                  className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${questionnaireReleased ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: questionnaireReleased ? "\u2713 Questionnaire Released to SPOC" : "Release Questionnaire"
                }
              )
              ]
            })
            ]
          }),
          laSubStep === "responses" && (() => {
            const delAssignments = gw.delegateAssignments || [];
            const assignedIdSet = new Set(delAssignments.flatMap((a) => a.questionIds || []));
            const uniqueAssignedIds = [...new Set(delAssignments.flatMap((a) => a.questionIds || []))];
            const totalQ = releasedQuestions.length;
            const assignedCount = assignedIdSet.size;
            const respondedCount = releasedQuestions.reduce((n, q) => n + (resolveQuestionResponse(q.id) ? 1 : 0), 0);
            const allAssignedAnswered = uniqueAssignedIds.length > 0 && uniqueAssignedIds.every((id) => resolveQuestionResponse(id));
            const assignedAnsweredCount = uniqueAssignedIds.filter((id) => resolveQuestionResponse(id)).length;
            const sortedQs = sortReleasedQuestionsForLa(releasedQuestions);
            const pctResponded = totalQ > 0 ? respondedCount / totalQ * 100 : 0;
            const canProceed = spocWorkComplete === true && allAssignedAnswered;
            const laAttByQ = laGapAttachmentIndex.byQuestion;
            const laTotalFiles = laGapAttachmentIndex.totalFiles;
            const laQuestionsWithFiles = laGapAttachmentIndex.questionsWithFiles;
            if (!questionnaireReleased) {
              return /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "px-8 py-6 border-b border-[color:var(--color-border)]",
                    children: [
                      /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Questionnaire Responses" }),
                      /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Track assignment and answers across every released question." })
                    ]
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "p-10 text-center",
                    children: /* @__PURE__ */ jsx("p", { className: "text-sm text-[color:var(--color-text-muted)] font-medium max-w-md mx-auto leading-relaxed", children: "Release the questionnaire from the Release Questionnaire step to unlock the full response list and live progress here." })
                  })
                ]
              });
            }
            return /* @__PURE__ */ jsxs("div", {
              className: `bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden${laSubStepReadOnly ? " opacity-70" : ""}`,
              children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "px-8 py-6 border-b border-[color:var(--color-border)]",
                  children: [
                    /* @__PURE__ */ jsxs("div", {
                      className: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4",
                      children: [
                        /* @__PURE__ */ jsxs("div", {
                          children: [
                            /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Questionnaire Responses - Live Progress" }),
                            /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: [
                              respondedCount, " of ", totalQ, " questions answered \u00b7 ", assignedCount, " assigned to delegates",
                              laTotalFiles > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
                                " \u00b7 ",
                                laTotalFiles,
                                " evidence file",
                                laTotalFiles !== 1 ? "s" : "",
                                " on ",
                                laQuestionsWithFiles,
                                " question",
                                laQuestionsWithFiles !== 1 ? "s" : ""
                              ] })
                            ] })
                          ]
                        }),
                        allAssignedAnswered && uniqueAssignedIds.length > 0 && /* @__PURE__ */ jsx("span", { className: "px-3 py-1 bg-[var(--color-ok-bg)] border border-[color:var(--color-ok-line)] rounded-lg text-[9px] font-black text-[color:var(--color-ok-fg)] uppercase shrink-0", children: "All assigned complete" })
                      ]
                    }),
                    /* @__PURE__ */ jsxs("div", {
                      className: "mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4",
                      children: [
                        /* @__PURE__ */ jsxs("div", {
                          className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)]   px-5 py-4 shadow-sm",
                          children: [
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Assigned" }),
                            /* @__PURE__ */ jsxs("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1 tabular-nums", children: [
                              assignedCount,
                              /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)] font-bold text-lg", children: " / " }),
                              totalQ
                            ] }),
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-2 font-medium", children: "Questions routed to delegates by the SPOC" })
                          ]
                        }),
                        /* @__PURE__ */ jsxs("div", {
                          className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)]   px-5 py-4 shadow-sm",
                          children: [
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "Responded" }),
                            /* @__PURE__ */ jsxs("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1 tabular-nums", children: [
                              respondedCount,
                              /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)] font-bold text-lg", children: " / " }),
                              totalQ
                            ] }),
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-2 font-medium", children: "SPOC or delegate answers received" })
                          ]
                        }),
                        /* @__PURE__ */ jsxs("div", {
                          className: "rounded-2xl border border-[color:var(--color-info-line)] bg-[var(--color-info-bg)]   px-5 py-4 shadow-sm",
                          children: [
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Evidence files" }),
                            /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1 tabular-nums", children: laTotalFiles }),
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-2 font-medium", children: laQuestionsWithFiles > 0 ? `Across ${laQuestionsWithFiles} question${laQuestionsWithFiles !== 1 ? "s" : ""}` : "No evidence uploaded yet" }),
                            laTotalFiles === 0 && /* @__PURE__ */ jsx("p", { className: "text-[9px] text-[color:var(--color-text-muted)] mt-1.5 font-medium leading-snug", children: "Files added by the SPOC or delegates in Respond to Questions appear here." })
                          ]
                        })
                      ]
                    }),
                    totalQ > 0 && /* @__PURE__ */ jsx("div", {
                      className: "mt-5 w-full h-2.5 bg-[var(--color-bg-alt)] rounded-full overflow-hidden",
                      children: /* @__PURE__ */ jsx("div", {
                        className: `h-full rounded-full transition-all duration-500 ${respondedCount >= totalQ ? "bg-emerald-500" : "bg-[var(--color-accent-fill)]"}`,
                        style: { width: `${pctResponded}%` }
                      })
                    })
                  ]
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "p-8 space-y-6",
                  children: [
                    spocWorkComplete !== true && /* @__PURE__ */ jsxs("div", {
                      className: "bg-[var(--color-warn-bg)] rounded-2xl p-4 border border-[color:var(--color-warn-line)] flex items-start gap-3",
                      children: [
                        /* @__PURE__ */ jsx("span", { className: "text-lg shrink-0", children: "\u23F3" }),
                        /* @__PURE__ */ jsx("p", { className: "text-xs font-medium text-[color:var(--color-warn-fg)] leading-relaxed", children: "Awaiting the Company SPOC to finish setup. Questions not routed to a delegate show as Unassigned; SPOC answers still appear in the green answered rows below." })
                      ]
                    }),
                    /* @__PURE__ */ jsx("div", {
                      className: "space-y-2 max-h-[min(70vh,560px)] overflow-y-auto pr-1",
                      children: sortedQs.map((q) => {
                        const isAssigned = assignedIdSet.has(q.id);
                        const rs = resolveQuestionResponse(q.id);
                        const answered = !!rs;
                        const responseSource = answered ? getResponseSourceForLa(q.id) : null;
                        const qFiles = laAttByQ[q.id] || [];
                        const rowAnswered = answered;
                        const rowUnassignedNoAnswer = !answered && !isAssigned;
                        const rowPending = !answered && isAssigned;
                        const rowClass = rowAnswered
                          ? "bg-[var(--color-ok-bg)] border-[color:var(--color-ok-line)]"
                          : rowUnassignedNoAnswer
                            ? "opacity-55 cursor-not-allowed bg-[var(--color-surface-sunken)] border-[color:var(--color-border)]"
                            : "bg-[var(--color-bg-white)] border-[color:var(--color-border)]";
                        const questionMuted = rowUnassignedNoAnswer;
                        return /* @__PURE__ */ jsxs(
                          "div",
                          {
                            title: rowUnassignedNoAnswer
                              ? "The SPOC has not yet assigned this question to a delegate."
                              : rowAnswered && !isAssigned
                                ? "Answered by the Company SPOC (not delegated)."
                                : void 0,
                            className: `flex items-start gap-3 px-4 py-3 rounded-xl border transition-all ${rowClass}`,
                            children: [
                              /* @__PURE__ */ jsx("span", { className: "text-[9px] font-black text-[color:var(--color-text-muted)] shrink-0 w-9 pt-0.5", children: q.qNo || q.id }),
                              /* @__PURE__ */ jsxs("div", {
                                className: "flex-1 min-w-0",
                                children: [
                                  /* @__PURE__ */ jsx("p", { className: `text-[11px] leading-snug ${questionMuted ? "text-[color:var(--color-text-muted)]" : "text-[color:var(--color-text-hero)] font-medium"}`, children: q.question }),
                                  q.domain && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-black uppercase tracking-widest text-[color:var(--color-accent)] mt-1 inline-block", children: q.domain }),
                                  qFiles.length > 0 && /* @__PURE__ */ jsx("div", {
                                    className: "mt-2 flex flex-wrap gap-1.5",
                                    children: qFiles.map((att) => /* @__PURE__ */ jsxs(
                                      "button",
                                      {
                                        type: "button",
                                        onClick: () => {
                                          // openBlobUrl resolves false (never a raw/unsigned URL)
                                          // when re-signing fails or throws — surface that.
                                          void Promise.resolve(openBlobUrl(att.url)).then((ok) => {
                                            if (ok === false) {
                                              message.error("A secure link could not be generated. Please retry.");
                                            }
                                          });
                                        },
                                        title: att.fileName,
                                        className: `inline-flex items-center gap-1 max-w-[min(100%,220px)] rounded-md border px-2 py-0.5 text-[8px] font-semibold transition-colors hover:opacity-90 cursor-pointer ${att.role === "spoc" ? "border-[color:var(--color-border)] bg-[var(--color-bg-white)] text-[color:var(--color-text-muted-dark)]" : "border-[color:var(--color-info-line)] bg-[var(--color-info-bg)] text-[color:var(--color-accent)]"}`,
                                        children: [
                                          /* @__PURE__ */ jsx("svg", { className: "w-2.5 h-2.5 shrink-0 opacity-60", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" }) }),
                                          /* @__PURE__ */ jsx("span", { className: "font-black uppercase tracking-tighter opacity-80 shrink-0", children: att.role === "spoc" ? "SPOC" : "Delegate" }),
                                          /* @__PURE__ */ jsx("span", { className: "truncate", children: att.fileName || "File" })
                                        ]
                                      },
                                      att.id
                                    ))
                                  })
                                ]
                              }),
                              /* @__PURE__ */ jsxs("div", {
                                className: "shrink-0 flex flex-col items-end gap-1 pt-0.5",
                                children: [
                                  rowUnassignedNoAnswer && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Unassigned" }),
                                  rowPending && /* @__PURE__ */ jsx("span", { className: "text-[9px] text-[color:var(--color-text-muted)] italic", children: "Pending" }),
                                  answered && /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-end gap-0.5", children: [
                                    /* @__PURE__ */ jsx("span", { className: `px-2.5 py-0.5 rounded-md text-[8px] font-black uppercase ${rs === "Yes" ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" : rs === "No" ? "bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)]" : rs === "Partial" ? "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)]" : "bg-[var(--color-bg-alt)] text-[color:var(--color-text-muted-dark)]"}`, children: rs }),
                                    responseSource === "spoc" && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "SPOC" }),
                                    responseSource === "delegate" && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Delegate" })
                                  ] })
                                ]
                              })
                            ]
                          },
                          q.id
                        );
                      })
                    }),
                    spocWorkComplete === true && (responsesReviewed ? /* @__PURE__ */ jsx("div", { className: "bg-[var(--color-ok-bg)] rounded-2xl p-5 border border-[color:var(--color-ok-line)] text-center", children: /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: "\u2713 Responses reviewed - Proceed to Schedule Interviews" }) }) : /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: () => {
                          setResponsesReviewed(true);
                          persistGapWizard({ responsesReviewed: true, laSubStep: "schedule" });
                          setLaSubStep("schedule");
                        },
                        disabled: !canProceed || laSubStepReadOnly,
                        className: `w-full px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${!canProceed ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                        children: canProceed ? "Submit & Continue to Schedule Interview" : uniqueAssignedIds.length === 0 ? "Waiting for the SPOC to assign questions to delegates" : `Waiting for all assigned responses (${assignedAnsweredCount}/${uniqueAssignedIds.length})`
                      }
                    ))
                  ]
                })
              ]
            });
          })(),
          laSubStep === "schedule" && (() => {
            const TYPE_LABELS = { in_person: "In-person Assessment", remote_video: "Virtual Assessment (Teams/Zoom)", hybrid: "Hybrid Assessment" };
            const resolveScheduleDepartment = () =>
              meetingDepartmentSelect === "Other"
                ? String(meetingDepartmentCustom || "").trim()
                : String(meetingDepartmentSelect || "").trim();
            const handleSchedule = async () => {
              const dept = resolveScheduleDepartment();
              if (!interviewDate || !interviewTitle || !dept) return;
              const meeting = await createMeeting(project.id, {
                title: interviewTitle,
                meetingDate: interviewDate,
                meetingType: interviewType,
                notes: interviewNotes || "",
                attendees: [],
                department: dept,
                meetingLink: meetingLink || undefined,
                meetingLocation: meetingLocation || undefined,
                scheduleSource: MEETING_SCHEDULE_SOURCE_GAP_ANALYSIS,
              });
              if (meeting) {
                setInterviewTitle("");
                setInterviewDate("");
                setInterviewNotes("");
                setInterviewType("in_person");
                setMeetingDepartmentSelect("");
                setMeetingDepartmentCustom("");
                setMeetingLink("");
                setMeetingLocation("");
                await syncScheduledMeetingsFromServer();
                if (fetchNotifications) fetchNotifications();
              }
            };
            const openReschedule = (mt) => {
              setRescheduleId(mt.id);
              setRescheduleData({ meetingDate: mt.date || "", meetingType: mt.type || "in_person", meetingLink: mt.meetingLink || "", meetingLocation: mt.meetingLocation || "", notes: mt.notes || "" });
            };
            const handleReschedule = async () => {
              if (!rescheduleId || !rescheduleData.meetingDate) return;
              const result = await updateMeeting(rescheduleId, rescheduleData);
              if (result) {
                setRescheduleId(null);
                setRescheduleData({});
                await syncScheduledMeetingsFromServer();
                if (fetchNotifications) fetchNotifications();
              }
            };
            return /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Schedule Non-Technical Interviews" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Enter meeting details and department. The Company SPOC will be notified to add delegates; once they do, the meeting appears for everyone in Meetings." })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: `p-8 space-y-6${laSubStepReadOnly ? " pointer-events-none opacity-60" : ""}`, children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Meeting Title" }),
            /* @__PURE__ */ jsx("input", { type: "text", value: interviewTitle, onChange: (e) => setInterviewTitle(e.target.value), placeholder: "e.g. Gap Analysis Non-Technical Interview", disabled: laSubStepReadOnly, className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
          ] }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Department" }),
            /* @__PURE__ */ jsxs("select", {
              value: meetingDepartmentSelect,
              onChange: (e) => {
                const v = e.target.value;
                setMeetingDepartmentSelect(v);
                if (v !== "Other") setMeetingDepartmentCustom("");
              },
              disabled: laSubStepReadOnly,
              className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20",
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "- Select department -" }),
                ...GAP_MEETING_DEPARTMENT_OPTIONS.map((d) =>
                  /* @__PURE__ */ jsx("option", { value: d, children: d }, d),
                ),
                /* @__PURE__ */ jsx("option", { value: "Other", children: "Other (specify below)" }),
              ],
            }),
            meetingDepartmentSelect === "Other" &&
              /* @__PURE__ */ jsx("input", {
                type: "text",
                value: meetingDepartmentCustom,
                onChange: (e) => setMeetingDepartmentCustom(e.target.value),
                placeholder: "Enter department name",
                disabled: laSubStepReadOnly,
                className: "mt-2 w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20",
              }),
          ] }),
          /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Interview Date" }),
              /* @__PURE__ */ jsx("input", { type: "date", value: interviewDate, onChange: (e) => setInterviewDate(e.target.value), className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
            ] }),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Interview Type" }),
              /* @__PURE__ */ jsxs("select", { value: interviewType, onChange: (e) => setInterviewType(e.target.value), className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20", children: [
                /* @__PURE__ */ jsx("option", { value: "in_person", children: "In-person Assessment" }),
                /* @__PURE__ */ jsx("option", { value: "remote_video", children: "Virtual Assessment (Teams/Zoom)" }),
                /* @__PURE__ */ jsx("option", { value: "hybrid", children: "Hybrid Assessment" })
              ] })
            ] })
                ]
              }),
          (interviewType === "in_person" || interviewType === "hybrid") && /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Meeting Location" }),
            /* @__PURE__ */ jsx("input", { type: "text", value: meetingLocation, onChange: (e) => setMeetingLocation(e.target.value), placeholder: "e.g. Conference Room A, 5th Floor, Resilinc HQ", className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
          ] }),
          (interviewType === "remote_video" || interviewType === "hybrid") && /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Meeting Link" }),
            /* @__PURE__ */ jsx("input", { type: "url", value: meetingLink, onChange: (e) => setMeetingLink(e.target.value), placeholder: "e.g. https://teams.microsoft.com/l/meetup-join/...", className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3", children: /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted-dark)] leading-relaxed", children: "Delegates are not selected here. After you submit, the Company SPOC is notified to add attendees from the project roster in the Meetings area." }) }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("label", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-2 block", children: "Notes & Agenda" }),
            /* @__PURE__ */ jsx("textarea", { value: interviewNotes, onChange: (e) => setInterviewNotes(e.target.value), placeholder: "Agenda items, areas to cover, preparation notes...", rows: 3, className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-xl px-4 py-3 text-sm font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20 resize-none" })
          ] }),
          /* @__PURE__ */ jsx("button", {
            onClick: handleSchedule,
            disabled: laSubStepReadOnly || !interviewDate || !interviewTitle || !resolveScheduleDepartment(),
            className: `w-full px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${laSubStepReadOnly || !interviewDate || !interviewTitle || !resolveScheduleDepartment() ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
            children: "Notify SPOC & submit meeting request"
          }),
          scheduledMeetings.length > 0 && /* @__PURE__ */ jsxs("div", { className: "bg-[var(--color-surface-sunken)] rounded-2xl p-5 border border-[color:var(--color-border)]", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-3", children: "Scheduled Meetings" }),
            /* @__PURE__ */ jsx("div", { className: "space-y-3", children: scheduledMeetings.map((mt, i) => {
              const isEditing = rescheduleId === mt.id;
              const st = mt.status || "scheduled";
              const statusLabel = st === "rescheduled" ? "Rescheduled" : st === "awaiting_delegates" ? "Awaiting SPOC" : "Scheduled";
              const statusStyle = st === "rescheduled" ? "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)]" : st === "awaiting_delegates" ? "bg-[var(--color-info-bg)] text-[color:var(--color-accent)]" : "bg-[var(--color-info-bg)] text-[color:var(--color-accent)]";
              const attCount = (mt.attendees || []).length;
              const deptLine = mt.department ? ` \u2022 Dept: ${mt.department}` : "";
              return /* @__PURE__ */ jsxs("div", { className: `bg-[var(--color-bg-white)] rounded-xl border transition-all ${isEditing ? "border-[color:var(--color-info-line)] shadow-md" : "border-[color:var(--color-border)]"}`, children: [
                /* @__PURE__ */ jsxs("div", { className: "px-4 py-3", children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                      /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-[color:var(--color-text-muted-dark)] block", children: mt.title }),
                      /* @__PURE__ */ jsxs("span", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: [mt.date, " \u2022 ", TYPE_LABELS[mt.type] || mt.type, deptLine, st === "awaiting_delegates" ? " \u2022 SPOC will add delegates" : ` \u2022 ${attCount} attendee${attCount !== 1 ? "s" : ""}`] })
                    ] }),
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 shrink-0", children: [
                      !isEditing && /* @__PURE__ */ jsx("button", { onClick: () => openReschedule(mt), className: "px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)] hover:text-[color:var(--color-accent)] hover:bg-[var(--color-info-bg)] border border-transparent hover:border-[color:var(--color-info-line)] transition-all", children: "Reschedule" }),
                      /* @__PURE__ */ jsx("span", { className: `px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase ${statusStyle}`, children: statusLabel })
                    ] })
                  ] }),
                  !isEditing && (mt.meetingLocation || mt.meetingLink) && /* @__PURE__ */ jsxs("div", { className: "mt-2 pt-2 border-t border-[color:var(--color-border)] flex flex-wrap gap-3", children: [
                    mt.meetingLocation && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 text-[10px] text-[color:var(--color-text-muted)]", children: ["\uD83D\uDCCD", " ", mt.meetingLocation] }),
                    mt.meetingLink && /* @__PURE__ */ jsx("a", { href: mt.meetingLink, target: "_blank", rel: "noreferrer", className: "inline-flex items-center gap-1.5 text-[10px] text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] hover:underline font-semibold", children: ["\uD83D\uDD17", " Join Meeting"].join("") })
                  ] })
                ] }),
                isEditing && /* @__PURE__ */ jsxs("div", { className: "px-4 pb-4 pt-1 border-t border-[color:var(--color-info-line)] space-y-3", children: [
                  /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
                    /* @__PURE__ */ jsxs("div", { children: [
                      /* @__PURE__ */ jsx("label", { className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-1 block", children: "New Date" }),
                      /* @__PURE__ */ jsx("input", { type: "date", value: rescheduleData.meetingDate || "", onChange: (e) => setRescheduleData((p) => ({ ...p, meetingDate: e.target.value })), className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
                    ] }),
                    /* @__PURE__ */ jsxs("div", { children: [
                      /* @__PURE__ */ jsx("label", { className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-1 block", children: "Interview Type" }),
                      /* @__PURE__ */ jsxs("select", { value: rescheduleData.meetingType || "in_person", onChange: (e) => setRescheduleData((p) => ({ ...p, meetingType: e.target.value, ...(e.target.value === "in_person" ? { meetingLink: "" } : e.target.value === "remote_video" ? { meetingLocation: "" } : {}) })), className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20", children: [
                        /* @__PURE__ */ jsx("option", { value: "in_person", children: "In-person Assessment" }),
                        /* @__PURE__ */ jsx("option", { value: "remote_video", children: "Virtual Assessment (Teams/Zoom)" }),
                        /* @__PURE__ */ jsx("option", { value: "hybrid", children: "Hybrid Assessment" })
                      ] })
                    ] })
                  ] }),
                  (rescheduleData.meetingType === "in_person" || rescheduleData.meetingType === "hybrid") && /* @__PURE__ */ jsxs("div", { children: [
                    /* @__PURE__ */ jsx("label", { className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-1 block", children: "Location" }),
                    /* @__PURE__ */ jsx("input", { type: "text", value: rescheduleData.meetingLocation || "", onChange: (e) => setRescheduleData((p) => ({ ...p, meetingLocation: e.target.value })), placeholder: "e.g. Conference Room A, 5th Floor", className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
                  ] }),
                  (rescheduleData.meetingType === "remote_video" || rescheduleData.meetingType === "hybrid") && /* @__PURE__ */ jsxs("div", { children: [
                    /* @__PURE__ */ jsx("label", { className: "text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-1 block", children: "Meeting Link" }),
                    /* @__PURE__ */ jsx("input", { type: "url", value: rescheduleData.meetingLink || "", onChange: (e) => setRescheduleData((p) => ({ ...p, meetingLink: e.target.value })), placeholder: "e.g. https://teams.microsoft.com/l/meetup-join/...", className: "w-full bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-xs font-medium text-[color:var(--color-text-muted-dark)] outline-none focus:ring-2 focus:ring-blue-500/20" })
                  ] }),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 pt-1", children: [
                    /* @__PURE__ */ jsx("button", { onClick: handleReschedule, disabled: !rescheduleData.meetingDate, className: "px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest bg-[var(--color-accent-fill)] text-white hover:brightness-110 transition-all shadow-sm disabled:opacity-40", children: "Confirm Reschedule" }),
                    /* @__PURE__ */ jsx("button", { onClick: () => { setRescheduleId(null); setRescheduleData({}); }, className: "px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] hover:bg-[var(--color-bg-alt)] transition-all", children: "Cancel" })
                  ] })
                ] })
              ] }, mt.id || i);
            }) })
          ] }),
          interviewsScheduled && /* @__PURE__ */ jsx("div", { className: "bg-[var(--color-ok-bg)] rounded-2xl p-5 border border-[color:var(--color-ok-line)] text-center", children: /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: "\u2713 Interviews Scheduled" }) })
              ]
            })
            ]
          });
          })(),
          laSubStep === "review-scores" && /* @__PURE__ */ jsxs("div", {
            className: `bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden${laSubStepReadOnly ? " opacity-70" : ""}`, children: [
        isLeadAdvisor && showTechEvaluationHistory && /* @__PURE__ */ jsx("div", { className: "px-8 pt-6", children: renderTechEvaluationHistory() }),
        (aiScoreWarnings.length > 0 || aiScoreFailedBatches.length > 0 || failedAiScoreRowIds.size > 0) && /* @__PURE__ */ jsx("div", {
              className: "px-8 pt-6", children: /* @__PURE__ */ jsx(GapAnalysisAiFailureBanner, {
                warnings: aiScoreWarnings,
                failedBatches: aiScoreFailedBatches,
                failedRowCount: failedAiScoreRowIds.size,
                retrying: retryingAiScores,
                onRetry: handleRetryFailedAiScoreBatches,
                disabled: laSubStepReadOnly
              })
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)] flex flex-wrap items-center justify-between gap-4", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Review & Validate Scores" }),
            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: scoresValidated ? "Scores validated with revised AI evaluation" : scoresSaved ? "Scores saved. Click Validate to run a final AI revision." : aiScoresGenerated ? "Review the scores below. Regenerate if needed, then save to lock them in." : "Generate AI scores to evaluate readiness across all domains" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 flex-wrap", children: [
            !scoresSaved && !aiScoresGenerating && /* @__PURE__ */ jsx("button", { disabled: laSubStepReadOnly, onClick: async () => {
              const aiCompany = buildGapAiCompanyContext(project);
              if (!aiCompany.ok) {
                message.warning(aiCompany.reason);
                return;
              }
              setAiScoresGenerating(true);
              setAiScoreWarnings([]);
              setAiScoreFailedBatches([]);
              try {
                const companyCtx = aiCompany.companyContext;
                const payload = buildAiScorePayload();
                lastAiScorePayloadRef.current = payload;
                const result = await generateAiScores(project.id, { questions: payload, companyContext: companyCtx });
                if (result?.scores) {
                  const scoresMap = {};
                  result.scores.forEach((s) => { scoresMap[s.id] = s; });
                  persistGapWizard({ aiGeneratedScores: scoresMap, aiScoresGenerated: true });
                  setAiScoresGenerated(true);
                  setAiScoreWarnings(result.warnings || []);
                  setAiScoreFailedBatches(result.failedBatches || []);
                }
              } catch (e) { console.error("AI score generation failed:", e); }
              setAiScoresGenerating(false);
            }, className: `px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-lg ${aiScoresGenerated ? "bg-[var(--color-bg-white)] border-2 border-[color:var(--color-accent)] text-[color:var(--color-accent)] hover:bg-[var(--color-info-bg)]" : "bg-[var(--color-accent-fill)]   text-white  "}`, children: aiScoresGenerated ? "Regenerate Scores" : "Generate AI Scores" }),
            (aiScoresGenerating || validationGenerating) && /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2 px-4 py-2 bg-[var(--color-info-bg)] rounded-xl text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: [
              /* @__PURE__ */ jsx("span", { className: "w-3 h-3 border-2 border-[color:var(--color-accent)] border-t-transparent rounded-full animate-spin" }),
              validationGenerating ? "Validating\u2026" : "Analyzing\u2026"
            ] }),
            aiScoresGenerated && !scoresSaved && !aiScoresGenerating && /* @__PURE__ */ jsx("button", { disabled: laSubStepReadOnly, onClick: () => {
              setScoresSaved(true);
              persistGapWizard({ scoresSaved: true, scoresSavedAt: Date.now(), nonTechScoreOverrides: nonTechScores, issueScoreOverrides: issueScores });
              message.success("Scores saved.");
            }, className: "px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg", children: "Save Scores" }),
            scoresSaved && !scoresValidated && !validationGenerating && /* @__PURE__ */ jsx("button", { disabled: laSubStepReadOnly, onClick: async () => {
              const aiCompany = buildGapAiCompanyContext(project);
              if (!aiCompany.ok) {
                message.warning(aiCompany.reason);
                return;
              }
              setValidationGenerating(true);
              setAiScoreWarnings([]);
              setAiScoreFailedBatches([]);
              try {
                const companyCtx = aiCompany.companyContext;
                const payload = buildAiScorePayload();
                lastAiScorePayloadRef.current = payload;
                const result = await generateAiScores(project.id, { questions: payload, companyContext: companyCtx });
                if (result?.scores) {
                  const scoresMap = {};
                  result.scores.forEach((s) => { scoresMap[s.id] = s; });
                  persistGapWizard({ aiGeneratedScores: scoresMap, scoresValidated: true, scoresValidatedAt: Date.now(), validatedOverallScore: computeOverallPercent(scoresMap, [{ rows: scoredQuestions, overrides: nonTechScores }, { rows: Object.values(realIssueResponses).flat(), overrides: issueScores }]) });
                  setScoresValidated(true);
                  setAiScoreWarnings(result.warnings || []);
                  setAiScoreFailedBatches(result.failedBatches || []);
                  message.success("Scores validated with revised AI evaluation.");
                }
              } catch (e) {
                console.error("Validation scoring failed:", e);
                message.error("Validation failed. Please try again.");
              }
              setValidationGenerating(false);
            }, className: "px-5 py-2.5 bg-[var(--color-accent-fill)]   text-white rounded-xl text-[9px] font-black uppercase tracking-widest   transition-all shadow-lg", children: "Validate" }),
            scoresSaved && !scoresValidated && /* @__PURE__ */ jsx("span", { className: "px-4 py-2 bg-[var(--color-ok-bg)] rounded-xl text-[9px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "\u2713 Saved" }),
            scoresValidated && /* @__PURE__ */ jsx("span", { className: "px-4 py-2 bg-[var(--color-info-bg)] rounded-xl text-[9px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "\u2713 Validated" })
          ] })
              ]
            }),
        (aiScoresGenerating || validationGenerating) && !aiScoresGenerated && /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-10 text-center", children: [
          /* @__PURE__ */ jsx("span", { className: "inline-block w-6 h-6 border-3 border-[color:var(--color-accent)] border-t-transparent rounded-full animate-spin mb-3" }),
          /* @__PURE__ */ jsx("p", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Analyzing with AI\u2026" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Evaluating controls, readiness, and generating recommendations" })
              ]
            }),
        !aiScoresGenerated && !aiScoresGenerating && /* @__PURE__ */ jsx("div", {
              className: "px-8 py-16 text-center", children:
          /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] font-medium", children: "Click \u201cGenerate AI Scores\u201d above to begin the evaluation" })
            }),
        aiScoresGenerated && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs("div", {
                className: "mx-6 mt-6 mb-4 bg-[var(--color-ok-bg)] border border-[color:var(--color-ok-line)] rounded-2xl p-5", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 mb-4", children: [
              /* @__PURE__ */ jsx("div", { className: "w-10 h-10 rounded-full bg-[var(--color-ok-bg)] ring-1 ring-inset ring-[color:var(--color-ok-line)] flex items-center justify-center shrink-0", children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5 text-[color:var(--color-ok-fg)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 3, d: "M5 13l4 4L19 7" }) }) }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("p", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: scoresValidated ? "Validated - Revised Scores" : scoresSaved ? "Scores Saved" : "AI Evaluation Complete" }),
                /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-ok-fg)] font-medium", children: [
                  scoredQuestions.length - scoredQuestions.filter((q) => !isGapRowScored(nonTechScores, q)).length, " questions analyzed \u00b7 Overall score ",
                  overallScore, "%",
                  notScoredCount > 0 ? ` \u00b7 ${notScoredLabel(notScoredCount)}` : "",
                  scoresValidated ? " \u00b7 Validated on " + formatDdMmYyyy(gw.scoresValidatedAt || Date.now()) : scoresSaved ? " \u00b7 Saved" : ""
                ] })
              ] })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-3", children: Object.entries(domainGroups).filter(([domain]) => domain !== UNCLASSIFIED_DOMAIN).map(([domain, qs]) => {
              const scoredQs = qs.filter((q) => isGapRowScored(nonTechScores, q));
              const avg = scoredQs.length > 0 ? Math.round(scoredQs.reduce((s, q) => s + getScore(nonTechScores, q.id, "readinessScore", q.readinessScore), 0) / scoredQs.length * 20) : null;
              return /* @__PURE__ */ jsxs("div", { className: "bg-[var(--color-bg-white)] rounded-xl px-4 py-3 border border-[color:var(--color-ok-line)] text-center", children: [
                /* @__PURE__ */ jsx("span", { className: "text-[8px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] block mb-1", children: domain }),
                avg === null
                  ? /* @__PURE__ */ jsx("span", { "data-testid": "domain-not-scored", className: "text-sm font-bold text-[color:var(--color-text-muted)]", children: "Not scored" })
                  : /* @__PURE__ */ jsxs("span", { className: `text-2xl font-black ${RAG_TEXT_CLASS[ragBandForPercent(avg) || "red"]}`, children: [avg, "%"] })
              ] }, domain);
            }) })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "p-6 space-y-6", children: [
                  Object.entries(domainGroups).map(([domain, qs]) => /* @__PURE__ */ jsx("div", {
                    children: renderAssessmentTable(
                      domain,
                      qs.map((q) => ({ id: q.id, label: q.question, applicableControls: q.applicableControls, controlMappings: q.controlMappings, response: q.response, readinessScore: q.readinessScore, expectedScore: q.expectedScore, scored: q.scored, nextSteps: q.nextSteps, aiFailed: q.aiFailed, aiFailureReason: q.aiFailureReason })),
                      nonTechScores,
                      setNonTechScores,
                      false
                    )
                  }, domain)),
            Object.keys(realIssueResponses).length > 0 && /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)] pt-4 border-t", children: "Issues - Threats - Opportunities" }),
                  Object.entries(realIssueResponses).map(([cat, items]) => /* @__PURE__ */ jsx("div", { children: renderAssessmentTable(cat, items.map((it) => ({ ...it, label: it.label })), issueScores, setIssueScores, false) }, cat))
                ]
              })
        ] })
            ]
          }),
          laSubStep === "report" && /* @__PURE__ */ jsxs("div", {
            className: `bg-[var(--color-bg-white)] rounded-2xl border border-[color:var(--color-border)] shadow-sm overflow-hidden flex flex-col${laSubStepReadOnly ? " opacity-70" : ""}`,
            style: { maxHeight: "min(92vh, 1280px)" },
            children: [
              /* @__PURE__ */ jsxs("div", {
                className: "px-6 py-5 border-b border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] text-[color:var(--color-text-hero)] flex flex-col gap-4",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4",
                    children: [
                      /* @__PURE__ */ jsxs("div", { children: [
                        /* @__PURE__ */ jsx("h3", { className: "text-lg font-black uppercase tracking-widest", children: "Gap Analysis Report" }),
                        /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-accent)] font-medium mt-1", children: [
                          [project?.certification, reportCompanyName(project)].filter(Boolean).join(" \u00b7 ")
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs("div", {
                        className: "flex flex-wrap items-center gap-2",
                        children: [
                          /* @__PURE__ */ jsx("button", {
                            type: "button",
                            disabled: reportDraftSaving || step4Status !== "Draft" || laSubStepReadOnly,
                            onClick: refreshReportFromScores,
                            className: "px-4 py-2 bg-[var(--color-bg-white)] text-[color:var(--color-text-hero)] border border-[color:var(--color-border-strong)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed",
                            children: "Refresh from scores"
                          }),
                          step4Status === "Draft" && (!isReportEditMode ? /* @__PURE__ */ jsx("button", {
                            type: "button",
                            disabled: reportDraftSaving || laSubStepReadOnly,
                            onClick: () => setIsReportEditMode(true),
                            className: "px-4 py-2 bg-[var(--color-bg-white)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-info-bg)] transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed",
                            children: "Edit"
                          }) : /* @__PURE__ */ jsx("button", {
                            type: "button",
                            disabled: reportDraftSaving || laSubStepReadOnly,
                            onClick: () => setIsReportEditMode(false),
                            className: "px-4 py-2 bg-[var(--color-bg-white)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-info-bg)] transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed",
                            children: "Preview"
                          })),
                          /* @__PURE__ */ jsx("button", {
                            type: "button",
                            disabled: reportDraftSaving || step4Status !== "Draft" || laSubStepReadOnly,
                            onClick: saveReportDraft,
                            className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2",
                            children: reportDraftSaving ? "Saving\u2026" : "Save draft"
                          }),
                          reportDrafted && step4Status === "Draft" && /* @__PURE__ */ jsx("button", {
                            type: "button",
                            "data-testid": "send-for-review",
                            title: !techPrereqMet ? TECH_ASSESSMENT_REQUIRED_TEXT : undefined,
                            disabled: reportDraftSaving || laSubStepReadOnly || !techPrereqMet,
                            onClick: () => {
                              setGapAnalysisReviewStatus("Under Review");
                              message.success("Report sent to SPOC for review.");
                            },
                            className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed",
                            children: "Send for Review"
                          })
                        ]
                      })
                    ]
                  }),
                  /* @__PURE__ */ jsx(SubStepNav, {
                    variant: "onDark",
                    steps: buildSubSteps(LA_SUBSTEPS, { lockedKeys: lockedLaKeys, completedKeys: completedLaKeys, attentionTitleByKey: laGapAttentionNavOpts.attentionTitleByKey }),
                    activeKey: laSubStep,
                    onSelect: setLaSubStep,
                  })
                ]
              }),
              reportDrafted && !reportDraftSaving && /* @__PURE__ */ jsx("div", { className: "px-6 py-2 bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] text-xs font-semibold border-b border-[color:var(--color-ok-line)]", children: "Report draft saved." }),
              reportDrafted && step4Status === "Draft" && !techPrereqMet && /* @__PURE__ */ jsx("div", { "data-testid": "send-review-blocked-reason", className: "px-6 py-2 bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)] text-xs font-semibold border-b border-[color:var(--color-warn-line)]", children: `Send for Review is unavailable: ${TECH_ASSESSMENT_REQUIRED_TEXT}` }),
              // Task #75 Gap A: the printable report previously rendered aiFailed rows as
              // unmarked zeros (lesson [[silent-zero-scoring-ai-fallback]]) — informational
              // only, no retry (that lives on the Review & Validate Scores panel, Task #74).
              reportFailedRowCount > 0 && /* @__PURE__ */ jsx("div", { className: "px-6 pt-4", children: /* @__PURE__ */ jsx(GapAnalysisAiFailureBanner, { failedRowCount: reportFailedRowCount }) }),
              /* @__PURE__ */ jsxs("div", {
                className: "flex flex-col lg:flex-row flex-1 min-h-0 min-w-0 overflow-hidden",
                children: [
              /* @__PURE__ */ jsx("div", {
                "data-contract-anchor": "gapReport",
                className: "flex-1 min-w-0 min-h-[52vh] overflow-y-auto p-10 paper-surface bg-white text-slate-900",
                style: { fontFamily: "'Times New Roman', Georgia, serif" },
                children: !reportSections.executiveSummary ? /* @__PURE__ */ jsxs("div", {
                  className: "text-center py-20 space-y-4",
                  children: [
                    /* @__PURE__ */ jsx("p", { className: "text-slate-400 text-sm", children: "No report generated yet." }),
                    /* @__PURE__ */ jsx("button", {
                      onClick: refreshReportFromScores,
                      disabled: laSubStepReadOnly,
                      className: "px-6 py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed",
                      children: "Generate Report from Scores"
                    })
                  ]
                }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "text-center border-b-2 border-slate-200 pb-6 mb-8", children: [
                      /* @__PURE__ */ jsxs("div", {
                        className: "flex justify-between items-start mb-6", children: [
                          /* @__PURE__ */ jsxs("div", { className: "text-left flex items-center gap-3", children: [
                            gapReportCompanyLogoUrl && /* @__PURE__ */ jsx("img", {
                              src: gapReportCompanyLogoUrl,
                              alt: "Company Logo",
                              className: "h-9 w-auto max-w-[140px] object-contain",
                            }),
                            /* @__PURE__ */ jsxs("div", { children: [
                              /* @__PURE__ */ jsx("span", { className: "text-[10px] text-slate-400 uppercase", children: "Company" }),
                              /* @__PURE__ */ jsx("p", { className: "text-sm font-bold", children: reportCompanyName(project) || "-" })
                            ] })
                          ] }),
                          /* @__PURE__ */ jsxs("div", { className: "text-right", children: [
                            /* @__PURE__ */ jsx("span", { className: "text-[10px] text-slate-400 uppercase", children: "Advisor" }),
                            reportSections.affirmationName ? /* @__PURE__ */ jsx("p", { className: "text-sm font-bold", children: reportSections.affirmationName }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 italic", children: "Not provided" })
                          ] })
                        ]
                      }),
                      /* @__PURE__ */ jsx("h1", { className: "text-3xl font-bold tracking-tight mb-1", children: "Gap Analysis Report" }),
                      /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500", children: project.certification || "" })
                    ]
                  }),
                  /* Backlog #16 \u2014 Gap Analysis draft: sections 1/2/8 wrapped in CollapsiblePane;
                     pageId matches renderReportAssessmentBody's below so all 8 body sections
                     (1/2 here + 3-7 from renderReportAssessmentBody + 8 here) share one
                     collapse-state namespace for this draft. reportDocEdit / step4Status
                     gating is untouched \u2014 only presentational wrapping changed. Section 7
                     (Tool-Test Assessment Summaries, backlog #29) added inside
                     renderReportAssessmentBody \u2014 see that function. */
                  /* @__PURE__ */ jsx(CollapsiblePane, { id: "executive-summary", pageId: "gap-analysis-report-draft", title: "1. Executive Summary", children:
                    reportDocEdit ? /* @__PURE__ */ jsx("textarea", { value: reportSections.executiveSummary, onChange: (e) => setReportSections((p) => ({ ...p, executiveSummary: e.target.value })), rows: 6, className: "w-full border rounded-lg p-3 text-sm leading-relaxed resize-none outline-none focus:ring-2 focus:ring-blue-500/20" }) : /* @__PURE__ */ jsx("p", { className: "text-sm leading-relaxed text-slate-700", children: reportSections.executiveSummary })
                  }),
                  /* @__PURE__ */ jsx(CollapsiblePane, { id: "scope", pageId: "gap-analysis-report-draft", title: "2. Scope", children:
                    /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
                      /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("legalEntities"), label: "Legal Entities + Locations" }),
                      /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("productsServices"), label: "Products and Services" }),
                      /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("activitiesDepts"), label: "Activities / Departments" })
                    ] })
                  }),
                  renderReportAssessmentBody("gap-analysis-report-draft"),
                  /* @__PURE__ */ jsx(CollapsiblePane, { id: "affirmation-statement", pageId: "gap-analysis-report-draft", title: "8. Affirmation Statement", children:
                    /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsxs("p", { className: "text-sm leading-relaxed text-slate-700 italic mb-6", children: [
                      '"In my opinion the responses submitted present fairly the compliance readiness position of ',
                      /* @__PURE__ */ jsx("strong", { children: reportCompanyName(project) || "the company" }),
                      '."'
                    ] }),
                    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-4", children: [
                      /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("affirmationName"), label: "Name" }),
                      /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("affirmationDate"), label: "Date", inputType: "date" })
                    ] }),
                    /* @__PURE__ */ jsx("p", { className: "text-[10px] text-slate-400 mt-4 text-center font-bold uppercase tracking-widest", children: "- Confidential -" })
                    ] })
                  })
                ] })
              }),
              showLdGapReportSidebar && /* @__PURE__ */ jsxs("div", {
                className: "flex flex-col min-h-0 border-t lg:border-t-0 lg:border-l border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] w-full lg:max-w-[360px] lg:w-[360px] lg:shrink-0",
                children: [
                  gapReportComposerShown && /* @__PURE__ */ jsxs("div", {
                    className: "p-3 border-b border-[color:var(--color-border)] space-y-2 shrink-0",
                    children: [
                      /* @__PURE__ */ jsx("textarea", {
                        value: gapReportNewCommentDraft,
                        onChange: (e) => setGapReportNewCommentDraft(e.target.value),
                        placeholder: "Add a comment\u2026",
                        rows: 3,
                        className: "w-full text-xs border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/20"
                      }),
                      /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => void handleGapReportAnnotationPost(),
                        className: "w-full px-3 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110",
                        children: "Post comment"
                      })
                    ]
                  }),
                  /* @__PURE__ */ jsx("div", {
                    className: "flex-1 min-h-0 flex flex-col overflow-hidden p-3 lg:p-4",
                    children: /* @__PURE__ */ jsx(ContractAnnotationsSidebar, {
                      fillAvailableHeight: true,
                      annotations: gapReportAnnotations,
                      selectedAnchorKey: gapReportAnnotationFocusKey,
                      onSelectAnchor: setGapReportAnnotationFocusKey,
                      onReply: handleGapReportAnnotationReply,
                      onResolve: handleGapReportAnnotationResolve,
                      canReply: !laSubStepReadOnly,
                      canResolve: step4Status === "Draft" && !laSubStepReadOnly,
                      title: "Report comments",
                      emptyHint: "No comments yet."
                    })
                  })
                ]
              })
                ]
              })
            ]
          }),
          laSubStep === "push-dashboard" && /* @__PURE__ */ jsxs("div", {
            className: `bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden${laSubStepReadOnly ? " opacity-70" : ""}`, children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Push Report Data to Dashboard" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Sync the finalized gap analysis data with the project dashboard" })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-10", children: [
          gw.gapDashboardSnapshot?.pushedAt &&
            /* @__PURE__ */ jsxs("div", {
              "data-testid": "gap-push-last-snapshot",
              className: "rounded-xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] px-4 py-3 text-[10px] text-[color:var(--color-ok-fg)] font-medium",
              children: [
                "Last pushed snapshot: ",
                formatDdMmYyyyTime(gw.gapDashboardSnapshot.pushedAt),
                gw.gapDashboardSnapshot.readinessPercent != null ? ` · ${gw.gapDashboardSnapshot.readinessPercent}% readiness` : "",
                gw.gapDashboardSnapshot.version != null ? ` · version ${gw.gapDashboardSnapshot.version}` : "",
                Array.isArray(gw.gapDashboardSnapshotHistory) && gw.gapDashboardSnapshotHistory.length > 0
                  ? ` · ${gw.gapDashboardSnapshotHistory.length} earlier ${gw.gapDashboardSnapshotHistory.length === 1 ? "push" : "pushes"} kept in history`
                  : "",
                " (calculated by the server from the validated scores)",
                typeof gw.gapDashboardSnapshot.excludedItemCount === "number" && gw.gapDashboardSnapshot.excludedItemCount > 0
                  ? /* @__PURE__ */ jsx("span", { "data-testid": "gap-push-excluded", className: "block mt-1 font-bold text-[color:var(--color-warn-fg)]", children: `${gw.gapDashboardSnapshot.excludedItemCount} ${gw.gapDashboardSnapshot.excludedItemCount === 1 ? "item" : "items"} excluded (AI scoring failed): left out of the readiness figure, neither counted as zero nor as passed.` })
                  : null,
              ],
            }),
          /* @__PURE__ */ jsxs("section", {
            className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)]   p-6 shadow-sm",
            children: [
              /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-4", children: "Readiness score" }),
              /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-end gap-4", children: [
                /* @__PURE__ */ jsxs("span", { className: "text-5xl font-black text-[color:var(--color-text-hero)] tracking-tight", children: [
                  displayReadinessPercent,
                  /* @__PURE__ */ jsx("span", { className: "text-2xl text-[color:var(--color-text-muted)] font-bold ml-1", children: "%" }),
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "text-[11px] text-[color:var(--color-text-muted)] max-w-md pb-1", children: [
                  scoresValidated
                    ? /* @__PURE__ */ jsxs(Fragment, {
                        children: [
                          "From Review & Validate Scores (validated",
                          gw.scoresValidatedAt ? ` ${formatDdMmYyyy(gw.scoresValidatedAt)}` : "",
                          ").",
                        ],
                      })
                    : "Live calculation from current readiness and expected scores. Validate scores in the previous step to lock a definitive percentage.",
                  notScoredCount > 0 && /* @__PURE__ */ jsx("span", { "data-testid": "gap-push-not-scored", className: "block mt-1 font-bold text-[color:var(--color-warn-fg)]", children: `${notScoredLabel(notScoredCount)} - not counted in this percentage.` }),
                ] }),
              ] }),
              renderTechFindings("gap-push"),
            ],
          }),
          /* @__PURE__ */ jsxs("section", {
            children: [
              /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-4", children: "RAG rating - Annex A heatmap" }),
              /* @__PURE__ */ jsx(PushIsoHeatmapSection, {
                isoControls: pushTabIsoControls,
                readinessMap: annexReadinessByCtrl,
                loading: pushTabIsoLoading,
                versionLabel: pushTabIsoVersion,
                showIso: showIso27001Heatmap,
              }),
            ],
          }),
          /* @__PURE__ */ jsxs("section", {
            children: [
              /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-4", children: "Remediation roadmap - contract milestones" }),
              /* @__PURE__ */ jsx(PushMilestoneGantt, { milestones: pushTabMilestones }),
            ],
          }),
          /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
          pushResultOpen && pushedToDashboard
            ? /* @__PURE__ */ jsxs("div", {
                "data-testid": "gap-push-result", role: "status",
                className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] p-5 space-y-3",
                children: [
                  /* @__PURE__ */ jsx("p", { "data-testid": "gap-push-result-text", className: "text-sm font-semibold text-[color:var(--color-ok-fg)]", children: gw.gapDashboardSnapshot?.pushedAt
                    ? `Pushed on ${formatDdMmYyyy(gw.gapDashboardSnapshot.pushedAt)}${gw.gapDashboardSnapshot.version != null ? `, version ${gw.gapDashboardSnapshot.version}` : ""}; the Gap readiness tile will appear after the dashboard refresh.`
                    : "Pushed to the dashboard; the Gap readiness tile will appear after the dashboard refresh." }),
                  /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-3", children: [
                    setCurrentView && /* @__PURE__ */ jsx("button", {
                      type: "button", "data-testid": "gap-push-view-dashboard", onClick: () => setCurrentView(View.PROJECT_DASHBOARD),
                      className: "px-5 py-2.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-hero)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]",
                      children: "View dashboard",
                    }),
                    /* @__PURE__ */ jsx("button", {
                      type: "button", "data-testid": "gap-push-continue", onClick: () => { setPushResultOpen(false); onAfterGapDashboardPush?.(); },
                      className: "px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110",
                      children: "Continue to Organizational Setup",
                    }),
                  ] }),
                ],
              })
            : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
                "button",
                {
                  "data-testid": "gap-push-to-dashboard",
                  title: !pushedToDashboard && pushBlockedReason ? pushBlockedReason : void 0,
                  onClick: () => {
                    // The server computes and stores the snapshot (readiness, heatmap, milestone summary) from the
                    // validated scores; the client only asks for the push and reads the result back with the profile.
                    void (async () => {
                      const ok = await persistGapWizard({ pushedToDashboard: true });
                      if (ok) {
                        setPushedToDashboard(true);
                        setPushResultOpen(true);
                        message.success("Data pushed to the dashboard.");
                      } else {
                        setPushedToDashboard(false);
                        message.error(takeSaveFailureText("The server did not accept the push to the dashboard. Please try again."));
                      }
                    })();
                  },
                  disabled: !reportDrafted || pushedToDashboard || laSubStepReadOnly || !!pushBlockedReason,
                  className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${pushedToDashboard ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : (!reportDrafted || pushBlockedReason) ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: pushedToDashboard ? "\u2713 Data Pushed to Dashboard" : "Push to Dashboard"
                }
              ),
          !pushedToDashboard && pushBlockedReason && /* @__PURE__ */ jsx("p", { "data-testid": "gap-push-blocked-reason", className: "text-[11px] text-[color:var(--color-text-muted)] font-medium max-w-xl", children: pushBlockedReason }),
          !pushedToDashboard && !pushBlockedReason && reportFailedRowCount > 0 && /* @__PURE__ */ jsx("p", { "data-testid": "gap-push-failed-rows-note", className: "text-[11px] text-[color:var(--color-warn-fg)] font-medium max-w-xl", children: `${reportFailedRowCount} item(s) have a failed AI score. Items with no usable score (and no reviewer override) are left out of the readiness figure; the push records how many were excluded.` }),
          isLeadAdvisor && pushedToDashboard && !pushBlockedReason && /* @__PURE__ */ jsxs("div", { "data-testid": "gap-repush", className: "rounded-xl border border-[color:var(--color-border)] p-4 space-y-2 max-w-xl", children: [
            gw.scoresValidatedAt && gw.gapDashboardSnapshot?.scoresValidatedAt && String(gw.scoresValidatedAt) !== String(gw.gapDashboardSnapshot.scoresValidatedAt) && /* @__PURE__ */ jsx("p", { "data-testid": "gap-repush-changed", className: "text-[11px] text-[color:var(--color-warn-fg)] font-medium", children: "The scores were validated again after the last push, so the dashboard may be out of date." }),
            /* @__PURE__ */ jsx("button", {
              type: "button", "data-testid": "gap-push-updated", onClick: () => setRepushDialog({ busy: false, error: null }),
              className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border-strong)] text-[color:var(--color-text-hero)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]",
              children: "Push updated data",
            }),
          ] }),
            ] }),
          /* @__PURE__ */ jsx(Modal, {
            open: !!repushDialog, title: "Push updated data to the dashboard?", maskClosable: false, closable: !repushDialog?.busy,
            onCancel: () => { if (!repushDialog?.busy) setRepushDialog(null); },
            footer: /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
              /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "repush-cancel", disabled: !!repushDialog?.busy, onClick: () => setRepushDialog(null), className: "px-4 py-2 border border-[color:var(--color-border)] rounded-lg text-xs font-bold disabled:opacity-50", children: "Cancel" }),
              /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "repush-confirm", disabled: !!repushDialog?.busy, onClick: () => void repushToDashboard(), className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-xs font-bold disabled:opacity-50", children: repushDialog?.busy ? "Pushing\u2026" : "Push updated data" }),
            ] }),
            children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 text-sm text-[color:var(--color-text-muted-dark)]", children: [
              /* @__PURE__ */ jsx("p", { children: "A new snapshot is calculated from the current validated scores. The previous snapshot is kept in the push history. The Gap readiness tile is briefly unavailable while the push completes." }),
              repushDialog?.error ? /* @__PURE__ */ jsx("p", { "data-testid": "repush-error", role: "alert", className: "text-[color:var(--color-danger-fg)] font-medium", children: repushDialog.error }) : null,
            ] }),
          }),
          ] })
              ]
            })
            ]
          })
        ]
      }),
      canActOnBehalfOfAdvisor && !toolSetup && advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] px-6 py-4 text-sm text-[color:var(--color-danger-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
          }),
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => setAdvisorActOnBehalfOpen(true),
            className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] underline underline-offset-2 hover:text-[color:var(--color-danger-fg)]",
            children: "Start a new session →",
          }),
        ],
      }),
      canActOnBehalfOfAdvisor && !toolSetup && !advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-6 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "Technical assessment tooling has not been configured by the Advisor yet (Setup Tool step).",
          }),
          advisorImpersonationActive
            ? /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => void markToolsAsReadyOnBehalfOfAdvisor(),
                className: "shrink-0 px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
                children: "Mark Tools as Ready (as Advisor) →",
              })
            : /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => setAdvisorActOnBehalfOpen(true),
                className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] underline underline-offset-2 hover:text-[color:var(--color-warn-fg)]",
                children: "Act on behalf of Advisor →",
              }),
        ],
      }),
      canActOnBehalfOfAdvisor && /* @__PURE__ */ jsx(ImpersonationDialog, {
        open: advisorActOnBehalfOpen,
        onClose: () => setAdvisorActOnBehalfOpen(false),
        projectId: project?.id,
        projectName: project?.companyName || project?.name,
        members: advisorMembersForActOnBehalf,
        initialEffectiveRole: "advisor",
        initialReason: "Blocked on: Advisor to configure and mark the technical assessment tooling as ready (Gap Analysis Setup Tool). Select the specific Advisor below - the server requires a named Advisor, not a role-only session, before it will accept this write.",
      }),
      canActOnBehalfOfAdvisor && !techAssessmentDone && !toolSetup && /* @__PURE__ */ jsx("div", {
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] px-6 py-4 text-sm text-[color:var(--color-text-muted-dark)] font-medium",
        children: "Setup Tool must be marked ready by the Advisor before Conduct Assessment can be completed on their behalf.",
      }),
      canActOnBehalfOfAdvisor && !techAssessmentDone && toolSetup && advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] px-6 py-4 text-sm text-[color:var(--color-danger-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
          }),
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => setAdvisorActOnBehalfAssessmentOpen(true),
            className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] underline underline-offset-2 hover:text-[color:var(--color-danger-fg)]",
            children: "Start a new session →",
          }),
        ],
      }),
      canActOnBehalfOfAdvisor && !techAssessmentDone && toolSetup && !advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-6 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "The technical assessment has not been completed by the Advisor yet (Conduct Assessment step).",
          }),
          advisorImpersonationActive
            ? /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => void completeAssessmentOnBehalfOfAdvisor(),
                className: "shrink-0 px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
                children: "Complete Assessment (as Advisor) →",
              })
            : /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => setAdvisorActOnBehalfAssessmentOpen(true),
                className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] underline underline-offset-2 hover:text-[color:var(--color-warn-fg)]",
                children: "Act on behalf of Advisor →",
              }),
        ],
      }),
      canActOnBehalfOfAdvisor && /* @__PURE__ */ jsx(ImpersonationDialog, {
        open: advisorActOnBehalfAssessmentOpen,
        onClose: () => setAdvisorActOnBehalfAssessmentOpen(false),
        projectId: project?.id,
        projectName: project?.companyName || project?.name,
        members: advisorMembersForActOnBehalf,
        initialEffectiveRole: "advisor",
        initialReason: "Blocked on: Advisor to conduct and complete the technical assessment (Gap Analysis Conduct Assessment step). Select the specific Advisor below - the server requires a named Advisor, not a role-only session, before it will accept this write.",
      }),
      canActOnBehalfOfAdvisor && !sharedWithLA && !techAssessmentDone && /* @__PURE__ */ jsx("div", {
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] px-6 py-4 text-sm text-[color:var(--color-text-muted-dark)] font-medium",
        children: "Conduct Assessment must be completed by the Advisor before sharing technical scores with the Lead Advisor on their behalf.",
      }),
      canActOnBehalfOfAdvisor && !sharedWithLA && techAssessmentDone && advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] px-6 py-4 text-sm text-[color:var(--color-danger-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
          }),
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => setAdvisorActOnBehalfShareOpen(true),
            className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] underline underline-offset-2 hover:text-[color:var(--color-danger-fg)]",
            children: "Start a new session →",
          }),
        ],
      }),
      canActOnBehalfOfAdvisor && !sharedWithLA && techAssessmentDone && !advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-6 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: techScoresValidated
              ? "Technical scores have not been shared with the Lead Advisor yet (Share with Lead Advisor step)."
              : `Technical scores have not been shared with the Lead Advisor yet. ${SHARE_NEEDS_VALIDATED_SCORES}`,
          }),
          advisorImpersonationActive
            ? /* @__PURE__ */ jsx("button", {
                type: "button",
                disabled: !techScoresValidated,
                title: techScoresValidated ? undefined : SHARE_NEEDS_VALIDATED_SCORES,
                onClick: () => void shareTechnicalScoresOnBehalfOfAdvisor(),
                className: "shrink-0 px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
                children: "Mark Shared with Lead Advisor (as Advisor) →",
              })
            : /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => setAdvisorActOnBehalfShareOpen(true),
                className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] underline underline-offset-2 hover:text-[color:var(--color-warn-fg)]",
                children: "Act on behalf of Advisor →",
              }),
        ],
      }),
      canActOnBehalfOfAdvisor && /* @__PURE__ */ jsx(ImpersonationDialog, {
        open: advisorActOnBehalfShareOpen,
        onClose: () => setAdvisorActOnBehalfShareOpen(false),
        projectId: project?.id,
        projectName: project?.companyName || project?.name,
        members: advisorMembersForActOnBehalf,
        initialEffectiveRole: "advisor",
        initialReason: "Blocked on: Advisor to share validated technical scores with the Lead Advisor (Gap Analysis Share with Lead Advisor step). Select the specific Advisor below - the server requires a named Advisor, not a role-only session, before it will accept this write.",
      }),
      // Backlog Sheet-1 row 19 — LA "Act on behalf of Advisor", Run Technical AI Evaluation step.
      // Same shape as the techAssessmentDone/sharedWithLA banners above — now attachable because
      // a real backend persistence design exists (.claude/lessons/completion-flag-implies-ai-ran-with-no-real-call.md).
      canActOnBehalfOfAdvisor && !techAiEvalRun && !techAssessmentDone && /* @__PURE__ */ jsx("div", {
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] px-6 py-4 text-sm text-[color:var(--color-text-muted-dark)] font-medium",
        children: "Conduct Assessment must be completed by the Advisor before running the technical AI evaluation on their behalf.",
      }),
      canActOnBehalfOfAdvisor && !techAiEvalRun && techAssessmentDone && advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] px-6 py-4 text-sm text-[color:var(--color-danger-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
          }),
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => setAdvisorActOnBehalfTechEvalOpen(true),
            className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] underline underline-offset-2 hover:text-[color:var(--color-danger-fg)]",
            children: "Start a new session →",
          }),
        ],
      }),
      canActOnBehalfOfAdvisor && !techAiEvalRun && techAssessmentDone && !advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-6 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "The technical AI evaluation has not been run yet (AI Evaluation step)." + (techEvalBlocked ? ` Run is disabled: ${techEvalBlocked}` : ""),
          }),
          advisorImpersonationActive
            ? /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => void runTechnicalAiEvaluationOnBehalfOfAdvisor(),
                disabled: techEvalRunning || !!techEvalBlocked,
                title: techEvalBlocked || undefined,
                className: "shrink-0 px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg disabled:opacity-50",
                children: techEvalRunning ? "Running…" : "Run Technical AI Evaluation (as Advisor) →",
              })
            : /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => setAdvisorActOnBehalfTechEvalOpen(true),
                className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] underline underline-offset-2 hover:text-[color:var(--color-warn-fg)]",
                children: "Act on behalf of Advisor →",
              }),
        ],
      }),
      canActOnBehalfOfAdvisor && !techAiEvalRun && techRunError && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: "Technical AI evaluation did not run. Nothing was saved; you can run it again.", description: techRunError }),
      canActOnBehalfOfAdvisor && /* @__PURE__ */ jsx(ImpersonationDialog, {
        open: advisorActOnBehalfTechEvalOpen,
        onClose: () => setAdvisorActOnBehalfTechEvalOpen(false),
        projectId: project?.id,
        projectName: project?.companyName || project?.name,
        members: advisorMembersForActOnBehalf,
        initialEffectiveRole: "advisor",
        initialReason: "Blocked on: Advisor to run the technical AI evaluation (Gap Analysis AI Evaluation step). Select the specific Advisor below - the server requires a named Advisor, not a role-only session, before it will accept this write.",
      }),
      // Backlog Sheet-1 row 20 — LA "Act on behalf of Advisor", Review & Validate (the explicit
      // human-approval event, Commandment IV — never triggerable from AI output alone).
      canActOnBehalfOfAdvisor && !techScoresValidated && !techAiEvalRun && /* @__PURE__ */ jsx("div", {
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] px-6 py-4 text-sm text-[color:var(--color-text-muted-dark)] font-medium",
        children: "Run Technical AI Evaluation must complete before the technical scores can be validated on the Advisor's behalf.",
      }),
      // The Lead Advisor must see exactly what is being approved: the same observations, AI scores,
      // stored agreement indicator and (with a named-Advisor session) the same override inputs as
      // the Advisor's own Review & Validate table, so an approval is never made blind.
      canActOnBehalfOfAdvisor && !techScoresValidated && techAiEvalRun && /* @__PURE__ */ jsxs("div", {
        "data-testid": "la-tech-review-panel",
        className: "rounded-2xl border border-[color:var(--color-border)] bg-[var(--color-bg-white)] px-6 py-5 space-y-4",
        children: [
          /* @__PURE__ */ jsx("h4", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "AI-drafted technical scores awaiting the Advisor's approval" }),
          (techFailedRowIds.size > 0 || techAiWarnings.length > 0 || techFailedBatches.length > 0) && /* @__PURE__ */ jsx(GapAnalysisAiFailureBanner, {
            warnings: techAiWarnings,
            failedBatches: techFailedBatches,
            failedRowCount: techFailedRowIds.size,
            retrying: techEvalRunning,
            retryLabel: "Retry failed rows",
            onRetry: advisorImpersonationActive ? () => void retryFailedTechRowsOnBehalfOfAdvisor() : undefined,
            disabled: techApproving
          }),
          techRunError && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: "Retry did not complete. Nothing was changed.", description: techRunError }),
          !advisorImpersonationActive && /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[color:var(--color-text-muted)] font-medium", children: "Read-only until you start an Advisor session; the scores can then be overridden here exactly as the Advisor would." }),
          renderAssessmentTable(
            "Technical Observations",
            buildTechTableItems(),
            techScores,
            setTechScores,
            advisorImpersonationActive
          ),
        ],
      }),
      canActOnBehalfOfAdvisor && techScoresValidated && renderTechApprovalAttribution(),
      canActOnBehalfOfAdvisor && !techScoresValidated && techAiEvalRun && advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] px-6 py-4 text-sm text-[color:var(--color-danger-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "This session doesn't name a specific Advisor - the server will reject any save. Start a new session and select a named Advisor.",
          }),
          /* @__PURE__ */ jsx("button", {
            type: "button",
            onClick: () => setAdvisorActOnBehalfTechApproveOpen(true),
            className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] underline underline-offset-2 hover:text-[color:var(--color-danger-fg)]",
            children: "Start a new session →",
          }),
        ],
      }),
      canActOnBehalfOfAdvisor && !techScoresValidated && techAiEvalRun && !advisorImpersonationNeedsNamedUser && /* @__PURE__ */ jsxs("div", {
        className: "rounded-2xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-6 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        children: [
          /* @__PURE__ */ jsx("span", {
            children: "The AI-drafted technical scores have not been reviewed and approved yet (Review & Validate step)." + (advisorImpersonationActive && techApproveBlockedReason ? ` Approve is disabled: ${techApproveBlockedReason}` : ""),
          }),
          advisorImpersonationActive
            ? /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => void approveTechnicalAiEvaluationOnBehalfOfAdvisor(),
                disabled: techApproving || !!techApproveBlockedReason,
                title: techApproveBlockedReason || undefined,
                className: "shrink-0 px-5 py-2.5 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg disabled:opacity-50",
                children: techApproving ? "Approving…" : "Approve & Validate (as Advisor) →",
              })
            : /* @__PURE__ */ jsx("button", {
                type: "button",
                onClick: () => setAdvisorActOnBehalfTechApproveOpen(true),
                className: "shrink-0 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] underline underline-offset-2 hover:text-[color:var(--color-warn-fg)]",
                children: "Act on behalf of Advisor →",
              }),
        ],
      }),
      canActOnBehalfOfAdvisor && /* @__PURE__ */ jsx(ImpersonationDialog, {
        open: advisorActOnBehalfTechApproveOpen,
        onClose: () => setAdvisorActOnBehalfTechApproveOpen(false),
        projectId: project?.id,
        projectName: project?.companyName || project?.name,
        members: advisorMembersForActOnBehalf,
        initialEffectiveRole: "advisor",
        initialReason: "Blocked on: Advisor to review and approve the AI-drafted technical scores (Gap Analysis Review & Validate step). Select the specific Advisor below - the server requires a named Advisor, not a role-only session, before it will accept this write.",
      }),
      isAdvisor && /* @__PURE__ */ jsxs("div", {
        className: "space-y-6", children: [
          /* @__PURE__ */ jsx(SubStepNav, { className: "mb-8", steps: buildSubSteps(ADV_SUBSTEPS, { lockedKeys: lockedAdvKeys, completedKeys: completedAdvKeys, badgeByKey: undefined, attentionTitleByKey: undefined }), activeKey: advSubStep, onSelect: setAdvSubStep }),
          (advSubStep === "conduct-assessment" || advSubStep === "share-la") && renderTechWaiverPanel(),
          advSubStepReadOnly && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] px-4 py-3 text-[11px] font-semibold text-[color:var(--color-ok-fg)]",
            children: "You are viewing a completed step (read-only)."
          }),
          advSubStep === "setup-tool" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Setup Technical Assessment Tool" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Record every assessment tool actually in scope for this engagement - real tool name, scope/target, and configuration notes." })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          toolSetupRecords.length === 0
                ? /* @__PURE__ */ jsx("div", {
                    className: "rounded-2xl border border-dashed border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] px-6 py-8 text-center",
                    children: /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] font-medium", children: "No assessment tools recorded yet. Add the tools you'll actually use below before marking this step ready." }),
                  })
                : /* @__PURE__ */ jsx("div", {
                    className: "space-y-3",
                    children: toolSetupRecords.map((rec) => /* @__PURE__ */ jsxs("div", {
                      className: "bg-[var(--color-surface-sunken)] rounded-2xl p-5 border border-[color:var(--color-border)] flex items-start gap-4", children: [
            /* @__PURE__ */ jsx("div", {
                        className: "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-[var(--color-ok-bg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]",
                        children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5 text-[color:var(--color-ok-fg)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "3", d: "M5 13l4 4L19 7" }) }),
                      }),
            /* @__PURE__ */ jsxs("div", {
                        className: "flex-1 min-w-0", children: [
              /* @__PURE__ */ jsx("span", { className: "text-xs font-black text-[color:var(--color-text-muted-dark)]", children: rec.toolName }),
              rec.scopeTarget && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-0.5", children: rec.scopeTarget }),
              rec.configNotes && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-0.5", children: rec.configNotes }),
              /* @__PURE__ */ jsxs("p", {
                          className: "text-[9px] text-[color:var(--color-text-muted)] mt-1", children: [
                            "Set up by ",
                            rec.setupBy || "-",
                            rec.setupAt ? ` \u00b7 ${formatDdMmYyyy(rec.setupAt)}` : "",
                          ],
                        }),
                      ],
                      }),
            !toolSetup && !advSubStepReadOnly && /* @__PURE__ */ jsx("button", {
                        type: "button",
                        onClick: () => void removeToolSetupRecord(rec.id),
                        disabled: removingToolRecordId === rec.id || !!evidenceWriteBlocked,
                        title: evidenceWriteBlocked || undefined,
                        className: "shrink-0 text-[9px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] hover:text-[color:var(--color-danger-fg)] disabled:opacity-50",
                        children: "Remove",
                      }),
                      ],
                    }, rec.id)),
                  }),
          !toolSetup && !advSubStepReadOnly && /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-surface-sunken)] rounded-2xl p-5 border border-[color:var(--color-border)] space-y-3", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Add an assessment tool" }),
            /* @__PURE__ */ jsxs("div", {
                  className: "grid grid-cols-1 md:grid-cols-2 gap-3", children: [
              /* @__PURE__ */ jsx("input", {
                      type: "text",
                      value: newToolName,
                      onChange: (e) => setNewToolName(e.target.value),
                      placeholder: "Tool name (e.g. Nessus, Wireshark, CIS-CAT)",
                      className: "px-3 py-2 rounded-lg border border-[color:var(--color-border)] text-xs",
                    }),
              /* @__PURE__ */ jsx("input", {
                      type: "text",
                      value: newToolScope,
                      onChange: (e) => setNewToolScope(e.target.value),
                      placeholder: "Scope / target (e.g. Prod network 10.0.0.0/16, AD tenant)",
                      className: "px-3 py-2 rounded-lg border border-[color:var(--color-border)] text-xs",
                    }),
                  ],
                }),
            /* @__PURE__ */ jsx("textarea", {
                  value: newToolNotes,
                  onChange: (e) => setNewToolNotes(e.target.value),
                  placeholder: "Configuration notes (scan profile, credential scope, exclusions...)",
                  rows: 2,
                  className: "w-full px-3 py-2 rounded-lg border border-[color:var(--color-border)] text-xs",
                }),
            /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: () => void addToolSetupRecord(),
                  disabled: !newToolName.trim() || addingToolRecord || !!evidenceWriteBlocked,
                  title: evidenceWriteBlocked || undefined,
                  className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all disabled:opacity-50",
                  children: addingToolRecord ? "Adding\u2026" : "Add Tool",
                }),
                ],
              }),
          /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: async () => {
                    setToolSetup(true);
                    const ok = await persistGapWizard({ toolSetup: true });
                    if (!ok) { setToolSetup(false); message.error(takeSaveFailureText("The server did not accept this step as complete. Check that at least one tool is recorded and try again.")); }
                  },
                  disabled: toolSetup || advSubStepReadOnly || toolSetupRecords.length === 0,
                  className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${toolSetup ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : toolSetupRecords.length === 0 ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: toolSetup ? "\u2713 Tools Configured" : "Mark Tools as Ready"
                }
              ),
          !toolSetup && toolSetupRecords.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: "Record at least one assessment tool above before marking this step ready." }),
              ]
            })
            ]
          }),
          advSubStep === "conduct-assessment" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Conduct Technical Assessment" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Execute the technical assessment and record observations" })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-surface-sunken)] rounded-2xl p-6 border", children: [
            /* @__PURE__ */ jsxs("h5", {
                  className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-4", children: [
                    "Technical Observations (",
                    techObservations.length,
                    ")"
                  ]
                }),
            /* @__PURE__ */ jsx("div", {
                  className: "space-y-3", children: techObservations.map((obs, i) => /* @__PURE__ */ jsxs("div", {
                    className: "bg-[var(--color-bg-white)] rounded-xl px-5 py-3 border flex items-start gap-3", children: [
              /* @__PURE__ */ jsxs("span", {
                      className: "text-xs font-black text-[color:var(--color-text-muted)] mt-0.5", children: [
                        "T",
                        i + 1
                      ]
                    }),
              /* @__PURE__ */ jsxs("div", {
                      className: "flex-1", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted-dark)] leading-relaxed", children: obs.observation }),
                /* @__PURE__ */ jsxs("div", {
                        className: "flex items-center gap-3 mt-2", children: [
                  /* @__PURE__ */ jsx("span", { className: `px-2 py-0.5 rounded text-[9px] font-bold ${obs.response === "Non-compliant" ? "bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)]" : "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)]"}`, children: obs.response }),
                  /* @__PURE__ */ jsxs("span", {
                          className: "text-[9px] text-[color:var(--color-text-muted)]", children: [
                            fmtControlCount(obs.applicableControls),
                            " controls"
                          ]
                        })
                        ]
                      })
                      ]
                    })
                    ]
                  }, obs.id))
                })
                ]
              }),
          /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => setCompleteDialogOpen(true),
                  disabled: !toolSetup || techAssessmentDone || advSubStepReadOnly || !canCompleteAssessment,
                  className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${techAssessmentDone ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : !toolSetup || !canCompleteAssessment ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: techAssessmentDone ? "\u2713 Assessment Complete" : "Complete Assessment"
                }
              ),
              /* @__PURE__ */ jsx(Modal, {
                open: completeDialogOpen, title: "Complete the assessment?", maskClosable: false,
                onCancel: () => setCompleteDialogOpen(false),
                footer: /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
                  /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "complete-cancel", onClick: () => setCompleteDialogOpen(false), className: "px-4 py-2 border border-[color:var(--color-border)] rounded-lg text-xs font-bold", children: "Cancel" }),
                  /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "complete-confirm", onClick: () => void completeAssessment(), className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-xs font-bold", children: "Complete assessment" }),
                ] }),
                children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 text-sm text-[color:var(--color-text-muted-dark)]", "data-testid": "complete-freeze-notice", children: [
                  /* @__PURE__ */ jsx("p", { children: "Completing the assessment freezes the tool-test evidence. You will not be able to upload, approve, reject, link or mark tools as having no output until you reopen it." }),
                  /* @__PURE__ */ jsx("p", { children: techScoresValidated
                    ? "The technical scores are already validated, so reopening later needs a written reason and archives the validated evaluation."
                    : "Once the technical scores have been validated, reopening later needs a written reason and archives the validated evaluation." }),
                ] }),
              }),
              techAssessmentDone && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
                /* @__PURE__ */ jsx("button", {
                  type: "button", "data-testid": "reopen-assessment", onClick: openReopenDialog,
                  disabled: !!reopenBlocked || reopening, title: reopenBlocked || undefined,
                  className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] disabled:opacity-50 disabled:cursor-not-allowed",
                  children: reopening ? "Reopening…" : "Reopen assessment",
                }),
                reopenBlocked && /* @__PURE__ */ jsx("p", { "data-testid": "reopen-blocked-reason", className: "text-[11px] text-[color:var(--color-warn-fg)]", children: `Reopen is unavailable: ${reopenBlocked}` }),
                /* @__PURE__ */ jsx(Modal, {
                  open: !!reopenDialog, title: "Reopen the assessment?", closable: !reopening, maskClosable: false,
                  onCancel: () => { if (!reopening) setReopenDialog(null); },
                  footer: /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
                    /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "reopen-cancel", disabled: reopening, onClick: () => setReopenDialog(null), className: "px-4 py-2 border border-[color:var(--color-border)] rounded-lg text-xs font-bold disabled:opacity-50", children: "Cancel" }),
                    /* @__PURE__ */ jsx("button", {
                      type: "button", "data-testid": "reopen-confirm", onClick: () => void reopenAssessment(reopenDialog?.reason),
                      disabled: reopening || (reopenNeedsReason && (reopenDialog?.reason || "").trim().length < REOPEN_REASON_MIN_LENGTH),
                      className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed",
                      children: reopening ? "Reopening…" : "Reopen",
                    }),
                  ] }),
                  children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 text-sm text-[color:var(--color-text-muted-dark)]", children: [
                    reopenNeedsReason
                      ? /* @__PURE__ */ jsx("p", { "data-testid": "reopen-archive-notice", children: "The validated technical evaluation will be archived to history and the technical evaluation must be run and approved again. Give a reason; it is recorded in the archive." })
                      : /* @__PURE__ */ jsx("p", { "data-testid": "reopen-plain-notice", children: "The assessment will be reopened. Complete it again once the evidence is updated. A reason is optional." }),
                    reopenDialog?.error ? /* @__PURE__ */ jsx("p", { "data-testid": "reopen-reason-error", role: "alert", className: "text-[color:var(--color-danger-fg)] font-medium", children: reopenDialog.error }) : null,
                    /* @__PURE__ */ jsx("label", { htmlFor: "reopen-reason-input", className: "block text-xs font-bold", children: reopenNeedsReason ? "Reason (required)" : "Reason (optional)" }),
                    /* @__PURE__ */ jsx("textarea", {
                      id: "reopen-reason-input", "data-testid": "reopen-reason-input", rows: 3, maxLength: REOPEN_REASON_MAX_LENGTH,
                      value: reopenDialog?.reason || "", disabled: reopening,
                      onChange: (e) => setReopenDialog((prev) => (prev ? { ...prev, reason: e.target.value } : prev)),
                      className: "w-full border border-[color:var(--color-border)] rounded-lg p-2 text-sm",
                    }),
                    /* @__PURE__ */ jsx("p", { "data-testid": "reopen-reason-counter", className: "text-[11px] text-[color:var(--color-text-muted)] text-right", children: `${(reopenDialog?.reason || "").length}/${REOPEN_REASON_MAX_LENGTH}` }),
                  ] }),
                }),
              ] }),
              techAssessmentDone && project?.legacyCompletedWithoutEvidence === true && /* @__PURE__ */ jsx("p", {
                "data-testid": "legacy-completed-disclosure", role: "note",
                className: "rounded-xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-4 py-3 text-[11px] font-medium text-[color:var(--color-warn-fg)]", children: `${LEGACY_COMPLETED_TEXT}.`,
              }),
              renderTechEvaluationHistory(),
              // Completion rule (Commandment V — the UI says exactly what blocks): >= 1 approved summary, zero
              // unresolved drafts, every recorded tool linked to an approved summary or marked no-output.
              !techAssessmentDone && toolSetup && !canCompleteAssessment && /* @__PURE__ */ jsxs("div", {
                "data-testid": "completion-blockers",
                className: "rounded-xl border border-[color:var(--color-warn-line)] bg-[var(--color-warn-bg)] px-4 py-3 space-y-1",
                children: [
                  /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)]", children: "Complete Assessment is blocked" }),
                  /* @__PURE__ */ jsx("ul", {
                    className: "list-disc list-inside text-[11px] text-[color:var(--color-warn-fg)] space-y-0.5",
                    children: toolEvidenceCompletion.blockers.map((b, i) => /* @__PURE__ */ jsx("li", { children: b }, i)),
                  }),
                  /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-warn-fg)]", children: "Rule: at least one approved summary, no unresolved AI drafts, and every recorded tool linked to an approved summary or marked as having no output." }),
                ],
              }),
              // Per-recorded-tool status, with the Advisor's "no output" mark (reason required, one per tool).
              toolSetupRecords.length > 0 && /* @__PURE__ */ jsxs("div", {
                "data-testid": "tool-evidence-status", className: "bg-[var(--color-surface-sunken)] rounded-2xl p-6 border space-y-3",
                children: [
                  /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Evidence per recorded tool" }),
                  ...toolEvidenceCompletion.toolStates.map((t) => /* @__PURE__ */ jsxs("div", {
                    "data-testid": `tool-evidence-${t.id}`, className: "bg-[var(--color-bg-white)] rounded-xl px-4 py-3 border space-y-2",
                    children: [
                      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
                        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
                          /* @__PURE__ */ jsx("span", { className: "text-xs font-black text-[color:var(--color-text-muted-dark)]", children: t.toolName }),
                          /* @__PURE__ */ jsx(Tag, {
                            color: t.state === "APPROVED" ? "green" : t.state === "NO_OUTPUT" ? "blue" : t.state === "DRAFT" ? "orange" : "red",
                            children: t.state === "APPROVED" ? "Approved summary linked" : t.state === "NO_OUTPUT" ? "Marked no output" : t.state === "DRAFT" ? "Draft awaiting approve or reject" : "Needs evidence",
                          }),
                        ] }),
                        t.state === "NEEDS_EVIDENCE" && !evidenceWriteBlocked && !advSubStepReadOnly && !noOutputForm && /* @__PURE__ */ jsx("button", {
                          type: "button", onClick: () => setNoOutputForm({ toolRecordId: t.id, reason: "", busy: false, error: null }),
                          className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]",
                          children: "Mark no output",
                        }),
                      ] }),
                      t.state === "NEEDS_EVIDENCE" && toolEvidenceCompletion.legacyUnlinkedCount > 0 && /* @__PURE__ */ jsx("p", {
                        "data-testid": `legacy-hint-${t.id}`, className: "text-[10px] text-[color:var(--color-warn-fg)]",
                        children: "An approved summary exists that is not linked to any recorded tool, so it does not count for this tool. Use \"Link to a recorded tool\" on that summary below, or mark this tool as having no output.",
                      }),
                      t.state === "NO_OUTPUT" && t.mark && /* @__PURE__ */ jsx("p", {
                        className: "text-[10px] text-[color:var(--color-text-muted)]",
                        children: `Reason: ${t.mark.reason}${t.mark.markedByEmail ? ` · ${t.mark.markedByEmail}` : ""}${t.mark.markedAt ? ` · ${formatDdMmYyyy(t.mark.markedAt)}` : ""}`,
                      }),
                      noOutputForm && noOutputForm.toolRecordId === t.id && /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
                        /* @__PURE__ */ jsx("textarea", {
                          "aria-label": "Reason this tool produced no output", value: noOutputForm.reason, maxLength: 1000, rows: 2,
                          onChange: (e) => setNoOutputForm((prev) => (prev ? { ...prev, reason: e.target.value } : prev)),
                          placeholder: "Why did this tool produce no output? (required, recorded in the audit log; cannot be edited)",
                          className: "w-full px-3 py-2 rounded-lg border border-[color:var(--color-border)] text-xs",
                        }),
                        noOutputForm.error && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: noOutputForm.error }),
                        /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
                          /* @__PURE__ */ jsx("button", {
                            type: "button", onClick: () => void markToolNoOutput(),
                            disabled: !noOutputForm.reason.trim() || noOutputForm.busy,
                            className: "px-3 py-1.5 bg-[var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest disabled:opacity-50",
                            children: noOutputForm.busy ? "Saving…" : "Confirm: no output",
                          }),
                          /* @__PURE__ */ jsx("button", {
                            type: "button", onClick: () => setNoOutputForm(null), disabled: noOutputForm.busy,
                            className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest",
                            children: "Cancel",
                          }),
                        ] }),
                      ] }),
                    ],
                  }, t.id)),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-surface-sunken)] rounded-2xl p-6 border space-y-4",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex flex-wrap items-center justify-between gap-3",
                    children: [
                      /* @__PURE__ */ jsxs("div", {
                        children: [
                          /* @__PURE__ */ jsx("h5", {
                            className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]",
                            children: "AI Tool-Test Summariser",
                          }),
                          /* @__PURE__ */ jsx("p", {
                            className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 max-w-md",
                            children:
                              "Upload a Wazuh / Nmap / plain-text tool output for an AI-drafted plain-English summary and control mapping. Nothing is added to the report until you review and approve it.",
                          }),
                        ],
                      }),
                      /* @__PURE__ */ jsxs("div", {
                        className: "flex flex-wrap items-center gap-2",
                        children: [
                          /* @__PURE__ */ jsx(Select, {
                            "aria-label": "Recorded tool this output belongs to",
                            "data-testid": "tool-test-record-select",
                            value: toolTestSelectedRecordId,
                            onChange: setToolTestSelectedRecordId,
                            allowClear: true,
                            placeholder: "Which recorded tool? (required)",
                            disabled: advSubStepReadOnly || (toolSetupRecords.length > 0 && linkTargets.length === 0),
                            style: { width: 220 },
                            options: linkTargets.map((r) => ({ value: r.id, label: r.toolName })),
                          }),
                          /* @__PURE__ */ jsx(Select, {
                            value: toolTestSelectedType,
                            onChange: setToolTestSelectedType,
                            disabled: advSubStepReadOnly,
                            style: { width: 150 },
                            options: [
                              { value: "wazuh", label: "Wazuh alerts" },
                              { value: "nmap", label: "Nmap XML" },
                              { value: "generic", label: "Generic text" },
                            ],
                          }),
                          /* @__PURE__ */ jsx("input", {
                            ref: toolTestFileInputRef,
                            type: "file",
                            accept: ".json,.ndjson,.xml,.txt",
                            className: "hidden",
                            onChange: handleToolTestFileSelected,
                          }),
                          /* @__PURE__ */ jsx("button", {
                            type: "button",
                            onClick: () => toolTestFileInputRef.current?.click(),
                            disabled: advSubStepReadOnly || !!evidenceWriteBlocked || toolUploadNeedsTool,
                            title: evidenceWriteBlocked || (toolUploadNeedsTool ? toolUploadNeedsToolText : undefined),
                            className:
                              "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed",
                            children: "Upload tool output",
                          }),
                        ],
                      }),
                    ],
                  }),
                  toolTestEvidence.status === "error" && /* @__PURE__ */ jsx(Alert, {
                    type: "error",
                    showIcon: true,
                    message: `Could not load tool-test summaries: ${toolTestEvidence.error}`,
                    action: /* @__PURE__ */ jsx("button", { type: "button", onClick: () => void loadToolTestSummaries(), className: "text-[10px] font-black uppercase", children: "Retry" }),
                  }),
                  toolTestEvidence.status === "denied" && /* @__PURE__ */ jsx(Alert, { type: "warning", showIcon: true, message: toolTestEvidence.error }),
                  evidenceWriteBlocked && /* @__PURE__ */ jsx("p", { "data-testid": "evidence-write-blocked", className: "text-[11px] font-medium text-[color:var(--color-warn-fg)]", children: evidenceWriteBlocked }),
                  !advSubStepReadOnly && toolUploadNeedsTool && /* @__PURE__ */ jsx("p", {
                    "data-testid": "tool-test-unlinked-hint", className: "text-[10px] text-[color:var(--color-warn-fg)]",
                    children: toolUploadNeedsToolText,
                  }),
                  !advSubStepReadOnly && toolSetupRecords.length === 0 && /* @__PURE__ */ jsx("p", {
                    "data-testid": "tool-test-unlinked-hint", className: "text-[10px] text-[color:var(--color-warn-fg)]",
                    children: "No recorded tools yet. A summary not linked to a recorded tool does not count toward completing the assessment.",
                  }),
                  toolTestCounts && /* @__PURE__ */ jsx("p", {
                    "data-testid": "tool-test-counts", className: "text-[10px] text-[color:var(--color-text-muted)]",
                    children: `Approved ${toolTestCounts.APPROVED ?? 0} · Unresolved drafts ${toolTestCounts.AI_DRAFT ?? 0} · Rejected ${toolTestCounts.REJECTED ?? 0}`,
                  }),
                  toolTestEvidence.status === "loading" && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: "Loading tool-test summaries…" }),
                  toolTestEvidence.status === "ready" && toolTestSummaries.length === 0 && toolTestDrafts.length === 0 && !toolTestNextCursor && /* @__PURE__ */ jsx("p", {
                    className: "text-[10px] text-[color:var(--color-text-muted)]",
                    children: "No tool-test summaries yet. Upload a tool output to generate an AI draft for review.",
                  }),
                  // Order matches the API: approved evidence first, then unresolved drafts (rejected on request below).
                  toolTestSummaries.length > 0 && /* @__PURE__ */ jsx("div", {
                    className: "space-y-2", "data-testid": "tool-test-approved",
                    children: toolTestSummaries.map((t) => /* @__PURE__ */ jsxs(
                      "div",
                      {
                        className: "bg-[var(--color-bg-white)] rounded-xl px-5 py-3 border",
                        children: [
                          /* @__PURE__ */ jsxs("div", {
                            className: "flex items-center gap-2 flex-wrap",
                            children: [
                              /* @__PURE__ */ jsx(Tag, { color: "purple", children: "AI drafted - approved" }),
                              /* @__PURE__ */ jsx(Tag, {
                                color: t.severity === "high" ? "red" : t.severity === "medium" ? "orange" : "green",
                                children: t.severity,
                              }),
                              /* @__PURE__ */ jsxs("span", {
                                className: "text-[9px] text-[color:var(--color-text-muted)]",
                                children: [t.toolType, " · ", t.mapped_controls?.length || 0, " mapped controls", t.toolRecordId ? ` · for ${toolSetupRecords.find((r) => r.id === t.toolRecordId)?.toolName || "a recorded tool"}` : " · not linked to a recorded tool", t.approvedBy?.email ? ` · approved by ${t.approvedBy.email}` : "", t.approvedAt ? ` · ${formatDdMmYyyy(t.approvedAt)}` : ""],
                              }),
                            ],
                          }),
                          Array.isArray(t.linkHistory) && t.linkHistory.length > 0 && /* @__PURE__ */ jsx("ul", {
                            "data-testid": `link-history-${t.id}`, className: "mt-1 text-[10px] text-[color:var(--color-text-muted)] space-y-0.5",
                            children: t.linkHistory.map((l, i) => /* @__PURE__ */ jsx("li", { key: i, children: `Linked to ${toolSetupRecords.find((r) => r.id === l.toolRecordId)?.toolName || "a recorded tool"} by ${l.linkedByEmail || "an unknown user"}${l.linkedAt ? ` on ${formatDdMmYyyy(l.linkedAt)}` : ""}. Reason: ${l.reason}` }, i)),
                          }),
                          !t.toolRecordId && !isToolTestSpocView && !advSubStepReadOnly && !(linkForm && linkForm.summaryId === t.id) && /* @__PURE__ */ jsx("button", {
                            type: "button", "data-testid": `link-open-${t.id}`, onClick: () => setLinkForm({ summaryId: t.id, toolRecordId: undefined, reason: "", busy: false, error: null }),
                            disabled: !!evidenceWriteBlocked || linkTargets.length === 0,
                            title: evidenceWriteBlocked || (linkTargets.length === 0 ? "Every recorded tool already has an approved summary or is marked as having no output." : undefined),
                            className: "mt-2 px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] disabled:opacity-50 disabled:cursor-not-allowed",
                            children: "Link to a recorded tool",
                          }),
                          linkForm && linkForm.summaryId === t.id && /* @__PURE__ */ jsxs("div", { "data-testid": "link-form", className: "mt-2 space-y-2 rounded-lg border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] p-3", children: [
                            /* @__PURE__ */ jsx(Select, {
                              "aria-label": "Recorded tool to link this summary to", "data-testid": "link-tool-select",
                              value: linkForm.toolRecordId, onChange: (v) => setLinkForm((prev) => (prev ? { ...prev, toolRecordId: v } : prev)),
                              placeholder: "Which recorded tool?", style: { width: 240 },
                              options: linkTargets.map((r) => ({ value: r.id, label: r.toolName })),
                            }),
                            /* @__PURE__ */ jsx("textarea", {
                              "aria-label": "Reason for linking this summary", value: linkForm.reason, maxLength: 1000, rows: 2,
                              onChange: (e) => setLinkForm((prev) => (prev ? { ...prev, reason: e.target.value } : prev)),
                              placeholder: "Why does this summary evidence that tool? (required, kept in the audit log; a summary can be linked only once)",
                              className: "w-full px-3 py-2 rounded-lg border border-[color:var(--color-border)] text-xs bg-[var(--color-bg-white)]",
                            }),
                            linkForm.error && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: linkForm.error }),
                            /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
                              /* @__PURE__ */ jsx("button", {
                                type: "button", "data-testid": "link-confirm", onClick: () => void linkToolTestSummary(),
                                disabled: !linkForm.toolRecordId || !linkForm.reason.trim() || linkForm.busy || !!evidenceWriteBlocked,
                                className: "px-3 py-1.5 bg-[var(--color-accent-fill)] text-white rounded-lg text-[10px] font-black uppercase tracking-widest disabled:opacity-50",
                                children: linkForm.busy ? "Linking…" : "Confirm link",
                              }),
                              /* @__PURE__ */ jsx("button", {
                                type: "button", onClick: () => setLinkForm(null), disabled: linkForm.busy,
                                className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest",
                                children: "Cancel",
                              }),
                            ] }),
                          ] }),
                          t.selfApproved && /* @__PURE__ */ jsx("p", { "data-testid": "self-approved-marker", className: "mt-1 text-[10px] font-semibold text-[color:var(--color-warn-fg)]", children: SELF_APPROVED_TEXT }),
                          /* @__PURE__ */ jsx("ul", {
                            className: "list-disc list-inside text-xs text-[color:var(--color-text-muted-dark)] mt-2 space-y-0.5",
                            children: (t.summary || []).map((b, i) => /* @__PURE__ */ jsx("li", { children: b }, i)),
                          }),
                          renderToolTestAuditLine(t),
                        ],
                      },
                      t.id,
                    )),
                  }),
                  toolTestDrafts.length > 0 && /* @__PURE__ */ jsx("div", {
                    className: "space-y-2", "data-testid": "tool-test-drafts",
                    children: toolTestDrafts.map((d) => /* @__PURE__ */ jsxs("div", {
                      className: "bg-[var(--color-warn-bg)] rounded-xl px-5 py-3 border border-[color:var(--color-warn-line)] space-y-1",
                      children: [
                        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
                          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
                            /* @__PURE__ */ jsx(Tag, { color: "purple", children: "AI drafted - review" }),
                            /* @__PURE__ */ jsxs("span", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: [d.toolType, d.sourceFileName ? ` · ${d.sourceFileName}` : "", d.toolRecordId ? ` · for ${toolSetupRecords.find((r) => r.id === d.toolRecordId)?.toolName || "a recorded tool"}` : " · not linked to a recorded tool", " · not yet approved, not counted as evidence"] }),
                          ] }),
                          !advSubStepReadOnly && /* @__PURE__ */ jsx("button", {
                            type: "button", onClick: () => openToolTestDraft(d),
                            className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]",
                            children: "Review",
                          }),
                        ] }),
                        renderToolTestAuditLine(d),
                      ],
                    }, d.id)),
                  }),
                  toolTestNextCursor && (toolTestSummaries.length < (toolTestCounts?.APPROVED ?? 0) || toolTestDrafts.length < (toolTestCounts?.AI_DRAFT ?? 0) || !toolTestCounts) && /* @__PURE__ */ jsx("button", {
                    type: "button", "data-testid": "tool-test-load-more", onClick: () => void loadMoreToolTestSummaries(), disabled: toolTestLoadingMore,
                    className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] disabled:opacity-50",
                    children: toolTestLoadingMore ? "Loading…" : "Load more",
                  }),
                  // Rejected summaries: terminal, never evidence. Fetched with ?status=REJECTED only when asked for.
                  toolTestCounts && (toolTestCounts.REJECTED ?? 0) > 0 && !toolTestRejected.open && /* @__PURE__ */ jsx("button", {
                    type: "button", "data-testid": "tool-test-show-rejected", onClick: () => void loadRejectedToolTestSummaries(),
                    className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]",
                    children: `Show rejected (${toolTestCounts.REJECTED})`,
                  }),
                  toolTestRejected.open && /* @__PURE__ */ jsxs("div", {
                    className: "space-y-2", "data-testid": "tool-test-rejected",
                    children: [
                      /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Rejected summaries (not evidence)" }),
                      toolTestRejected.status === "loading" && toolTestRejected.items.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: "Loading rejected summaries…" }),
                      toolTestRejected.status === "error" && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: toolTestRejected.error }),
                      toolTestRejected.status === "ready" && toolTestRejected.items.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: "No rejected summaries." }),
                      ...toolTestRejected.items.map((r) => /* @__PURE__ */ jsxs("div", {
                        className: "bg-[var(--color-bg-alt)] rounded-xl px-5 py-3 border border-[color:var(--color-border)] space-y-1",
                        children: [
                          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 flex-wrap", children: [
                            /* @__PURE__ */ jsx(Tag, { color: "red", children: "Rejected" }),
                            /* @__PURE__ */ jsx("span", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: [r.toolType, r.sourceFileName ? ` · ${r.sourceFileName}` : "", r.toolRecordId ? ` · for ${toolSetupRecords.find((x) => x.id === r.toolRecordId)?.toolName || "a recorded tool"}` : ""].join("") }),
                          ] }),
                          /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[color:var(--color-text-muted-dark)]", children: `Reason: ${r.rejectionReason || "-"}` }),
                          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: `Rejected by ${r.rejectedBy?.email || "-"}${r.rejectedAt ? ` on ${formatDdMmYyyy(r.rejectedAt)}` : ""}` }),
                          renderToolTestAuditLine(r),
                        ],
                      }, r.id)),
                      toolTestRejected.nextCursor && /* @__PURE__ */ jsx("button", {
                        type: "button", "data-testid": "tool-test-rejected-load-more", onClick: () => void loadRejectedToolTestSummaries({ more: true }), disabled: toolTestRejected.status === "loading",
                        className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] disabled:opacity-50",
                        children: "Load more",
                      }),
                    ],
                  }),
                ],
              }),
              toolTestModal && /* @__PURE__ */ jsx(Modal, {
                open: true,
                title: "AI-drafted tool-test summary",
                onCancel: closeToolTestModal,
                width: 640,
                footer: /* @__PURE__ */ jsxs("div", {
                  className: "flex justify-end gap-2",
                  children: [
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: closeToolTestModal,
                      className:
                        "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)] transition-all",
                      children: "Discard",
                    }),
                    toolTestModal.summary && toolTestModal.draftId && !toolTestModal.rejectOpen && /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => setToolTestModal((prev) => (prev ? { ...prev, rejectOpen: true } : prev)),
                      disabled: !!toolTestModal.approving || !!toolTestModal.rejecting || !!evidenceWriteBlocked,
                      title: evidenceWriteBlocked || undefined,
                      className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-danger-line)] text-[color:var(--color-danger-fg)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-danger-bg)] transition-all disabled:opacity-50",
                      children: "Reject\u2026",
                    }),
                    /* @__PURE__ */ jsx("button", {
                      type: "button",
                      onClick: () => void acceptToolTestSummary(),
                      disabled: !toolTestModal.summary || !toolTestModal.draftId || !!toolTestModal.approving || !!toolTestModal.rejecting || !!evidenceWriteBlocked,
                      title: evidenceWriteBlocked || undefined,
                      className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${toolTestModal.summary && toolTestModal.draftId && !toolTestModal.approving && !toolTestModal.rejecting && !evidenceWriteBlocked ? "bg-[var(--color-accent-fill)] text-white hover:brightness-110" : "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed"}`,
                      children: toolTestModal.approving ? "Approving\u2026" : "Approve - add to report",
                    }),
                  ],
                }),
                children: /* @__PURE__ */ jsxs("div", {
                  className: "space-y-4 pt-1",
                  children: [
                    /* @__PURE__ */ jsx(Tag, { color: "purple", children: "AI drafted - review before use" }),
                    toolTestModal.loading && /* @__PURE__ */ jsxs("div", {
                      className: "flex items-center gap-2 text-sm text-[color:var(--color-text-muted)] py-8 justify-center",
                      children: [/* @__PURE__ */ jsx(Spin, { size: "small" }), "Summarising tool output\u2026"],
                    }),
                    toolTestModal.error && !toolTestModal.loading && /* @__PURE__ */ jsx(Alert, {
                      type: "error",
                      showIcon: true,
                      message: toolTestModal.error,
                    }),
                    !toolTestModal.loading && !toolTestModal.error && toolTestModal.summary && /* @__PURE__ */ jsxs(Fragment, {
                      children: [
                        evidenceWriteBlocked && /* @__PURE__ */ jsx(Alert, { type: "warning", showIcon: true, "data-testid": "modal-write-blocked", message: evidenceWriteBlocked }),
                        toolTestModal.approveError && /* @__PURE__ */ jsx(Alert, {
                          type: "error",
                          showIcon: true,
                          message: toolTestModal.approveError,
                        }),
                        toolTestModal.toolRecordId && /* @__PURE__ */ jsx("p", {
                          className: "text-[10px] text-[color:var(--color-text-muted)]",
                          children: `Evidence for recorded tool: ${toolSetupRecords.find((r) => r.id === toolTestModal.toolRecordId)?.toolName || "a recorded tool"}`,
                        }),
                        toolTestModal.rejectOpen && /* @__PURE__ */ jsxs("div", {
                          "data-testid": "reject-form", className: "rounded-xl border border-[color:var(--color-danger-line)] bg-[var(--color-danger-bg)] p-3 space-y-2",
                          children: [
                            /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)]", children: "Reject this draft (final: it can never be approved afterwards)" }),
                            /* @__PURE__ */ jsx("textarea", {
                              "aria-label": "Reason for rejecting this summary", value: toolTestModal.rejectReason, maxLength: 1000, rows: 3,
                              onChange: (e) => setToolTestModal((prev) => (prev ? { ...prev, rejectReason: e.target.value } : prev)),
                              placeholder: "Why is this summary being rejected? (required, kept in the audit log)",
                              className: "w-full px-3 py-2 rounded-lg border border-[color:var(--color-danger-line)] text-xs bg-[var(--color-bg-white)]",
                            }),
                            /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
                              /* @__PURE__ */ jsx("button", {
                                type: "button", onClick: () => void rejectToolTestSummary(),
                                disabled: !(toolTestModal.rejectReason || "").trim() || !!toolTestModal.rejecting || !!toolTestModal.approving,
                                className: "px-3 py-1.5 bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)] ring-1 ring-inset ring-[color:var(--color-danger-line)] rounded-lg text-[10px] font-black uppercase tracking-widest disabled:opacity-50",
                                children: toolTestModal.rejecting ? "Rejecting\u2026" : "Confirm reject",
                              }),
                              /* @__PURE__ */ jsx("button", {
                                type: "button", disabled: !!toolTestModal.rejecting,
                                onClick: () => setToolTestModal((prev) => (prev ? { ...prev, rejectOpen: false, rejectReason: "" } : prev)),
                                className: "px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest",
                                children: "Cancel",
                              }),
                            ] }),
                          ],
                        }),
                        /* @__PURE__ */ jsx(AiAgreementIndicator, {
                          confidence: toolTestModal.meta?.confidence,
                          runs: toolTestModal.meta?.confidenceRuns,
                          validSamples: toolTestModal.meta?.validSamples,
                          reasoningRefs: toolTestModal.meta?.reasoningRefs,
                          unavailableReason: toolTestModal.meta?.confidenceUnavailableReason,
                        }),
                        toolTestModal.legacyUnlinkedWarning && /* @__PURE__ */ jsx("div", { "data-testid": "legacy-unlinked-warning", children: /* @__PURE__ */ jsx(Alert, { type: "warning", showIcon: true, message: toolTestModal.legacyUnlinkedWarning }) }),
                        toolTestModal.warnings.length > 0 && /* @__PURE__ */ jsx(Alert, {
                          type: "info",
                          showIcon: true,
                          message: `Normaliser warnings: ${toolTestModal.warnings.join(", ")}`,
                        }),
                        /* @__PURE__ */ jsxs("div", {
                          children: [
                            /* @__PURE__ */ jsxs("div", {
                              className: "flex items-center gap-2 mb-1",
                              children: [
                                /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Severity" }),
                                /* @__PURE__ */ jsx(Tag, {
                                  color: toolTestModal.summary.severity === "high" ? "red" : toolTestModal.summary.severity === "medium" ? "orange" : "green",
                                  children: toolTestModal.summary.severity,
                                }),
                              ],
                            }),
                            /* @__PURE__ */ jsx("ul", {
                              className: "list-disc list-inside text-sm text-[color:var(--color-text-muted-dark)] space-y-1",
                              children: toolTestModal.summary.summary.map((b, i) => /* @__PURE__ */ jsx("li", { children: b }, i)),
                            }),
                          ],
                        }),
                        toolTestModal.summary.mapped_controls.length > 0 && /* @__PURE__ */ jsxs("div", {
                          children: [
                            /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Mapped controls" }),
                            /* @__PURE__ */ jsx("div", {
                              className: "space-y-1 mt-1",
                              children: toolTestModal.summary.mapped_controls.map((c, i) => /* @__PURE__ */ jsxs(
                                "div",
                                {
                                  className: "text-xs text-[color:var(--color-text-muted-dark)]",
                                  children: [/* @__PURE__ */ jsx("span", { className: "font-bold", children: c.id }), ` ${c.title} - ${c.why}` ],
                                },
                                `${c.id}-${i}`,
                              )),
                            }),
                          ],
                        }),
                        toolTestModal.summary.recommended_next_steps.length > 0 && /* @__PURE__ */ jsxs("div", {
                          children: [
                            /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Recommended next steps" }),
                            /* @__PURE__ */ jsx("ul", {
                              className: "list-disc list-inside text-sm text-[color:var(--color-text-muted-dark)] space-y-1 mt-1",
                              children: toolTestModal.summary.recommended_next_steps.map((s, i) => /* @__PURE__ */ jsx("li", { children: s }, i)),
                            }),
                          ],
                        }),
                        toolTestModal.meta?.requestId && /* @__PURE__ */ jsxs("p", {
                          className: "text-[10px] text-[color:var(--color-text-muted)]",
                          children: ["Request ID: ", toolTestModal.meta.requestId, toolTestModal.meta.modelId ? ` \u00b7 Model: ${toolTestModal.meta.modelId}` : "", toolTestModal.meta.promptId ? ` \u00b7 Prompt: ${toolTestModal.meta.promptId}` : ""],
                        }),
                      ],
                    }),
                  ],
                }),
              }),
              ]
            })
            ]
          }),
          advSubStep === "ai-eval-tech" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "PL AI Platform - Technical Evaluation" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Run technical assessment outcomes through the AI evaluation engine" })
              ]
            }),
        /* @__PURE__ */ jsx("div", {
              className: "p-8 space-y-6", children: !techAiEvalRun ? /* @__PURE__ */ jsxs("div", {
                className: "text-center py-16", children: [
          /* @__PURE__ */ jsx("div", { className: "w-24 h-24 mx-auto mb-6 rounded-[2rem] bg-[var(--color-accent-fill)]   flex items-center justify-center shadow-2xl", children: /* @__PURE__ */ jsx("svg", { className: "w-12 h-12 text-white", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" }) }) }),
          /* @__PURE__ */ jsx("h4", { className: "text-lg font-black text-[color:var(--color-text-muted-dark)] mb-2", children: "Ready to Run Technical AI Evaluation" }),
          /* @__PURE__ */ jsxs("p", {
                  className: "text-sm text-[color:var(--color-text-muted)] mb-8 max-w-md mx-auto", children: [
                    "The AI engine will analyze ",
                    toolTestSummaries.length,
                    " approved technical tool-test finding(s) and generate readiness scores with remediation guidance."
                  ]
                }),
          toolTestSummaries.length === 0 && /* @__PURE__ */ jsx("p", {
                  className: "text-xs text-[color:var(--color-warn-fg)] font-medium mb-4",
                  children: "Upload and approve at least one tool-test summary in Conduct Assessment first.",
                }),
          techEvalBlocked && /* @__PURE__ */ jsx("p", { "data-testid": "tech-run-blocked-reason", className: "text-xs font-medium text-[color:var(--color-warn-fg)] mb-4", children: `Run is disabled: ${techEvalBlocked}` }),
          techRunError && /* @__PURE__ */ jsx("div", { className: "max-w-xl mx-auto mb-4 text-left", children: /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: "Technical AI evaluation did not run. Nothing was saved; you can run it again.", description: techRunError }) }),
          /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => void runTechnicalAiEvaluation(),
                    disabled: !!techEvalBlocked || toolTestSummaries.length === 0 || techEvalRunning || advSubStepReadOnly,
                    title: techEvalBlocked || undefined,
                    className: `px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-2xl ${techEvalBlocked || toolTestSummaries.length === 0 ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)]   text-white  "}`,
                    children: techEvalRunning ? "Running…" : "Run Technical AI Evaluation"
                  }
                )
                ]
              }) : /* @__PURE__ */ jsxs("div", {
                className: "space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-ok-bg)] rounded-2xl p-5 border border-[color:var(--color-ok-line)] flex items-center gap-4", children: [
            /* @__PURE__ */ jsx("div", { className: "w-10 h-10 bg-[var(--color-ok-bg)] ring-1 ring-inset ring-[color:var(--color-ok-line)] rounded-xl flex items-center justify-center", children: /* @__PURE__ */ jsx("svg", { className: "w-5 h-5 text-[color:var(--color-ok-fg)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "3", d: "M5 13l4 4L19 7" }) }) }),
            /* @__PURE__ */ jsxs("div", {
                    children: [
              /* @__PURE__ */ jsx("p", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: "Technical AI Evaluation Complete" }),
              /* @__PURE__ */ jsxs("p", {
                      className: "text-[10px] text-[color:var(--color-ok-fg)]", children: [
                        techObservations.length,
                        " observations scored with remediation mapped"
                      ]
                    })
                    ]
                  })
                  ]
                }),
                  (techFailedRowIds.size > 0 || techAiWarnings.length > 0 || techFailedBatches.length > 0) && /* @__PURE__ */ jsx(GapAnalysisAiFailureBanner, {
                    warnings: techAiWarnings,
                    failedBatches: techFailedBatches,
                    failedRowCount: techFailedRowIds.size,
                    retrying: techEvalRunning,
                    retryLabel: "Retry failed rows",
                    onRetry: () => void runTechnicalAiEvaluation({ retryFailedOnly: true }),
                    disabled: techScoresValidated
                  }),
                  techRunError && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: "Retry did not complete. Nothing was changed.", description: techRunError }),
                  renderAssessmentTable(
                    "Technical Observations",
                    buildTechTableItems(),
                    techScores,
                    setTechScores,
                    false
                  )
                ]
              })
            })
            ]
          }),
          advSubStep === "review-scores-tech" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        // Task #74 (Gap A) / lesson [[silent-zero-scoring-ai-fallback]] — mirrors the LA
        // "Review & Validate Scores" banner (Task #72). techObservations is real,
        // backend-persisted data (fixed — see the techObservations useMemo further above),
        // so this guard is live, not dormant.
        (techAiWarnings.length > 0 || techFailedBatches.length > 0 || techFailedRowIds.size > 0) && /* @__PURE__ */ jsx("div", {
              className: "px-8 pt-6", children: /* @__PURE__ */ jsx(GapAnalysisAiFailureBanner, {
                warnings: techAiWarnings,
                failedBatches: techFailedBatches,
                failedRowCount: techFailedRowIds.size,
                retrying: techEvalRunning,
                retryLabel: "Retry failed rows",
                onRetry: () => void runTechnicalAiEvaluation({ retryFailedOnly: true }),
                disabled: techScoresValidated
              })
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Review & Validate Technical Scores" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Review the AI-drafted scores below (edit if needed), then explicitly approve - this is the human-approval step; it never happens automatically from the AI run." })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          !techScoresValidated && techObservations.length > 0 && !techObservations.some((t) => typeof t.confidence === "number") && /* @__PURE__ */ jsx(Alert, {
                type: "warning",
                showIcon: true,
                message: "Agreement score was not computed for this output. Review each row's reasoning and the score itself manually before approving.",
              }),
                techRunError && /* @__PURE__ */ jsx(Alert, { type: "error", showIcon: true, message: "Retry did not complete. Nothing was changed.", description: techRunError }),
                renderAssessmentTable(
                  "Technical Observations - Editable",
                  buildTechTableItems(),
                  techScores,
                  setTechScores,
                  !techScoresValidated
                ),
                renderTechApprovalAttribution(),
          /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => void approveTechnicalAiEvaluation(),
                    disabled: techScoresValidated || techApproving || advSubStepReadOnly || !!techApproveBlockedReason,
                    title: techApproveBlockedReason || undefined,
                    className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${techScoresValidated ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : techApproveBlockedReason ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                    children: techScoresValidated ? "\u2713 Technical Scores Validated" : techApproving ? "Approving\u2026" : "Approve & Validate Technical Scores"
                  }
                ),
          !techScoresValidated && techApproveBlockedReason && /* @__PURE__ */ jsx("p", { "data-testid": "tech-approve-blocked-reason", className: "text-xs font-medium text-[color:var(--color-warn-fg)]", children: `Approve is disabled: ${techApproveBlockedReason}` })
              ]
            })
            ]
          }),
          advSubStep === "share-la" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Share Final Scores with Lead Advisor" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Submit validated technical scores. Technical findings are shown separately alongside the readiness score." })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-surface-sunken)] rounded-2xl p-6 border", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] mb-4", children: "Technical Assessment Summary" }),
            /* @__PURE__ */ jsxs("div", {
                  className: "grid grid-cols-3 gap-4 text-center mb-4", children: [
              /* @__PURE__ */ jsxs("div", {
                    className: "bg-[var(--color-bg-white)] rounded-xl p-3 border", children: [
                /* @__PURE__ */ jsx("span", { className: "text-[9px] text-[color:var(--color-text-muted)] font-black uppercase", children: "Observations" }),
                /* @__PURE__ */ jsx("p", { className: "text-xl font-black text-[color:var(--color-text-hero)]", children: techObservations.length })
                    ]
                  }),
              /* @__PURE__ */ jsxs("div", {
                    className: "bg-[var(--color-bg-white)] rounded-xl p-3 border", children: [
                /* @__PURE__ */ jsx("span", { className: "text-[9px] text-[color:var(--color-text-muted)] font-black uppercase", children: "Non-Compliant" }),
                /* @__PURE__ */ jsx("p", { className: "text-xl font-black text-[color:var(--color-danger-fg)]", children: techObservations.filter((t) => t.response === "Non-compliant").length })
                    ]
                  }),
              /* @__PURE__ */ jsxs("div", {
                    className: "bg-[var(--color-bg-white)] rounded-xl p-3 border", children: [
                /* @__PURE__ */ jsx("span", { className: "text-[9px] text-[color:var(--color-text-muted)] font-black uppercase", children: "Partial" }),
                /* @__PURE__ */ jsx("p", { className: "text-xl font-black text-[color:var(--color-warn-fg)]", children: techObservations.filter((t) => t.response === "Partial").length })
                    ]
                  })
                  ]
                })
                ]
              }),
          /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: async () => {
                    setSharedWithLA(true);
                    const ok = await persistGapWizard({ sharedWithLA: true });
                    if (!ok) { setSharedWithLA(false); message.error(takeSaveFailureText("The server did not accept sharing the technical scores.")); }
                  },
                  disabled: !techPrereqMet || sharedWithLA || advSubStepReadOnly,
                  title: techPrereqMet ? undefined : SHARE_NEEDS_VALIDATED_SCORES,
                  className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${sharedWithLA ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : !techPrereqMet ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: sharedWithLA ? "\u2713 Shared with Lead Advisor" : "Share Technical Scores"
                }
              ),
          sharedWithLA && isAdvisor && step4Status === "Draft" && /* @__PURE__ */ jsx("button", {
                type: "button", "data-testid": "withdraw-share", onClick: () => setWithdrawDialog({ busy: false, error: null }),
                className: "px-4 py-2 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[color:var(--color-text-muted-dark)] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-bg-alt)]",
                children: "Withdraw share",
              }),
          /* @__PURE__ */ jsx(Modal, {
                open: !!withdrawDialog, title: "Withdraw the share?", maskClosable: false, closable: !withdrawDialog?.busy,
                onCancel: () => { if (!withdrawDialog?.busy) setWithdrawDialog(null); },
                footer: /* @__PURE__ */ jsxs("div", { className: "flex justify-end gap-2", children: [
                  /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "withdraw-cancel", disabled: !!withdrawDialog?.busy, onClick: () => setWithdrawDialog(null), className: "px-4 py-2 border border-[color:var(--color-border)] rounded-lg text-xs font-bold disabled:opacity-50", children: "Cancel" }),
                  /* @__PURE__ */ jsx("button", { type: "button", "data-testid": "withdraw-confirm", disabled: !!withdrawDialog?.busy, onClick: () => void withdrawShare(), className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-lg text-xs font-bold disabled:opacity-50", children: withdrawDialog?.busy ? "Withdrawing\u2026" : "Withdraw share" }),
                ] }),
                children: /* @__PURE__ */ jsxs("div", { className: "space-y-3 text-sm text-[color:var(--color-text-muted-dark)]", children: [
                  /* @__PURE__ */ jsx("p", { "data-testid": "withdraw-notice", children: "The Lead Advisor will no longer see these technical scores as shared. After withdrawing you can reopen the assessment to change the evidence, then complete, validate and share it again." }),
                  withdrawDialog?.error ? /* @__PURE__ */ jsx("p", { "data-testid": "withdraw-error", role: "alert", className: "text-[color:var(--color-danger-fg)] font-medium", children: withdrawDialog.error }) : null,
                ] }),
              }),
          !sharedWithLA && !techPrereqMet && /* @__PURE__ */ jsx("p", { "data-testid": "share-blocked-reason", className: "text-[10px] text-[color:var(--color-text-muted)]", children: SHARE_NEEDS_VALIDATED_SCORES })
              ]
            })
            ]
          })
        ]
      }),
      isSPOC && /* @__PURE__ */ jsxs("div", {
        className: "space-y-6", children: [
          /* @__PURE__ */ jsx(SubStepNav, { className: "mb-8", steps: buildSubSteps(SPOC_SUBSTEPS, { lockedKeys: lockedSpocKeys, completedKeys: completedSpocKeys, badgeByKey: { meetings: spocAwaitingDelegatesCount }, attentionTitleByKey: isSPOC && step4Status === "Under Review" ? { "report-review": "Your turn - review and approve the gap report" } : undefined }), activeKey: spocSubStep, onSelect: setSpocSubStep }),
          spocSubStepReadOnly && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-[color:var(--color-ok-line)] bg-[var(--color-ok-bg)] px-4 py-3 text-[11px] font-semibold text-[color:var(--color-ok-fg)]",
            children: "You are viewing a completed step (read-only)."
          }),
          spocSubStep === "respond" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Respond to Questions" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Answer the gap analysis questionnaire questions. Upload supporting documents for answered questions." }),
          /* @__PURE__ */ jsxs("div", {
                className: "flex items-center gap-4 mt-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-[var(--color-info-bg)] rounded-xl px-4 py-2 border border-[color:var(--color-info-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Total" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: releasedQuestions.length })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-[var(--color-ok-bg)] rounded-xl px-4 py-2 border border-[color:var(--color-ok-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "Answered" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: answeredQuestionIds.size })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-[var(--color-warn-bg)] rounded-xl px-4 py-2 border border-[color:var(--color-warn-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)]", children: "Remaining" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: unansweredQuestions.length })
            ] })
                ]
              })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: `p-8 space-y-4 max-h-[70vh] overflow-y-auto${spocResponded ? " pointer-events-none opacity-60" : ""}`, children: [
                releasedQuestions.map((q, i) => /* @__PURE__ */ jsxs("div", {
                  className: `rounded-xl px-5 py-4 border transition-all ${mergedSpocAnswers[q.id] ? "bg-[var(--color-ok-bg)] border-[color:var(--color-ok-line)]" : "bg-[var(--color-surface-sunken)] border-[color:var(--color-border)]"}`, children: [
            /* @__PURE__ */ jsxs("div", {
                    className: "flex items-start gap-3 mb-3", children: [
              /* @__PURE__ */ jsxs("span", {
                      className: "text-xs font-black text-[color:var(--color-text-muted)] min-w-[40px]", children: [
                        q.qNo || `Q${i + 1}`
                      ]
                    }),
              /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted-dark)] leading-relaxed font-medium", children: q.question }),
                q.domain && /* @__PURE__ */ jsx("span", { className: "text-[9px] font-bold text-[color:var(--color-accent)] uppercase tracking-widest mt-1 inline-block", children: q.domain })
              ] }),
              mergedSpocAnswers[q.id] && /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-ok-fg)] text-sm font-black", children: "\u2713" })
                    ]
                  }),
            /* @__PURE__ */ jsx("div", {
                    className: "flex items-center gap-2 ml-10 mb-3", children: ["Yes", "No", "Partial", "NA"].map((opt) => /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: () => setSpocAnswers((prev) => ({ ...prev, [q.id]: opt })),
                        className: `px-3 py-1.5 rounded-lg text-[9px] font-bold transition-all ${mergedSpocAnswers[q.id] === opt ? opt === "Yes" ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : opt === "No" ? "bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)] ring-1 ring-inset ring-[color:var(--color-danger-line)]" : opt === "Partial" ? "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)] ring-1 ring-inset ring-[color:var(--color-warn-line)]" : "bg-[var(--color-border-strong)] text-white" : "bg-[var(--color-bg-white)] border text-[color:var(--color-text-muted)] hover:bg-[var(--color-bg-alt)]"}`,
                        children: opt
                      },
                      opt
                    ))
                  }),
            /* @__PURE__ */ jsx("div", {
                    className: "ml-10 mt-2", children: /* @__PURE__ */ jsx("textarea", {
                      placeholder: "Add notes or remarks (optional)...",
                      value: spocNotes[q.id] || "",
                      onChange: (e) => setSpocNotes((prev) => ({ ...prev, [q.id]: e.target.value })),
                      rows: 2,
                      className: "w-full bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-[11px] text-[color:var(--color-text-muted-dark)] resize-none outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    })
                  }),
            mergedSpocAnswers[q.id] && /* @__PURE__ */ jsxs("div", {
                    className: "ml-10 mt-2 space-y-2", children: [
              /* @__PURE__ */ jsxs("div", {
                    className: "flex flex-wrap items-center gap-2", children: [
              /* @__PURE__ */ jsxs("label", {
                      className: `flex items-center gap-2 px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg ${spocUploadingQid === q.id ? "opacity-50 cursor-wait" : "cursor-pointer hover:bg-[var(--color-bg-alt)]"} transition-all`, children: [
                /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5 text-[color:var(--color-text-muted)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" }) }),
                /* @__PURE__ */ jsx("span", { className: "text-[9px] font-bold uppercase tracking-widest text-[color:var(--color-text-muted)]", children: spocUploadingQid === q.id ? "Uploading\u2026" : "Upload file (PDF, image, Word)" }),
                /* @__PURE__ */ jsx("input", { type: "file", disabled: spocUploadingQid === q.id, className: "hidden", accept: ".pdf,.png,.jpg,.jpeg,.doc,.docx", onChange: async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) await appendSpocQuestionFile(q.id, f, q.qNo || "");
                } })
                      ]
                    }),
              typeof spocDocs[q.id] === "string" && spocDocs[q.id] && !(Array.isArray(gw.spocQuestionAttachments?.[q.id]) && gw.spocQuestionAttachments[q.id].length > 0) && /* @__PURE__ */ jsx("span", { className: "text-[9px] text-[color:var(--color-warn-fg)]", children: [
                      "Previously noted file (not stored): ",
                      spocDocs[q.id]
                    ] })
                    ]
                  }),
              (Array.isArray(gw.spocQuestionAttachments?.[q.id]) ? gw.spocQuestionAttachments[q.id] : []).map((att) => /* @__PURE__ */ jsxs("div", {
                      className: "flex items-center gap-2 text-[9px] font-medium text-[color:var(--color-ok-fg)] bg-[var(--color-ok-bg)] border border-[color:var(--color-ok-line)] rounded-lg px-3 py-1.5 w-fit max-w-full",
                      children: [
                /* @__PURE__ */ jsx("svg", { className: "w-3 h-3 shrink-0", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 12l2 2 4-4" }) }),
                /* @__PURE__ */ jsx("a", {
                          href: att.url,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          className: "truncate underline hover:text-[color:var(--color-ok-fg)]",
                          children: att.fileName || "Download"
                        }),
                /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)]", children: "\u00b7" }),
                /* @__PURE__ */ jsx("button", {
                          type: "button",
                          disabled: deletingArtifactId === att.id,
                          onClick: () => removeSpocQuestionFile(q.id, att.id),
                          className: "shrink-0 text-[8px] font-black uppercase tracking-wider text-[color:var(--color-danger-fg)] underline-offset-2 hover:underline hover:text-[color:var(--color-danger-fg)] cursor-pointer bg-transparent border-none p-0 disabled:opacity-40 disabled:cursor-wait",
                          children: deletingArtifactId === att.id ? "Removing\u2026" : "Delete"
                        })
                      ]
                    }, att.id))
                    ]
                  })
                  ]
                }, q.id)),
          releasedQuestions.length === 0 && /* @__PURE__ */ jsx("div", {
                  className: "text-center py-12", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-[color:var(--color-text-muted)] font-medium", children: "No questions have been released yet. Please wait for the Lead Advisor to release the questionnaire." })
                })
              ]
            }),
          /* @__PURE__ */ jsxs("div", {
              className: "flex flex-wrap items-center gap-4 px-8 pb-8 pt-4 border-t border-[color:var(--color-border)]",
              children: [
            /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => {
                        setSpocResponded(true);
                        persistGapWizard({
                          spocAnswers: mergedSpocAnswers,
                          spocDocs,
                          spocNotes,
                          spocResponded: true,
                          spocSubStep: "respond",
                        });
                      },
                      disabled: spocResponded || answeredQuestionIds.size === 0 || spocSubStepReadOnly,
                      className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${spocResponded ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : answeredQuestionIds.size === 0 ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                      children: spocResponded ? "\u2713 Responses Submitted" : "Submit My Responses"
                    }
                  ),
            unansweredQuestions.length > 0 && spocResponded && /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => setSpocSubStep("delegate"),
                      className: "px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)] ring-1 ring-inset ring-[color:var(--color-warn-line)] hover:brightness-110 transition-all shadow-lg",
                      children: `Delegate ${unansweredQuestions.length} Remaining \u2192`
                    }
                  )
                  ]
                })
            ]
          }),
          spocSubStep === "delegate" && /* @__PURE__ */ jsxs("div", {
            className: `bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden${spocSubStepReadOnly ? " pointer-events-none opacity-60" : ""}`, children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Delegate Questionnaire" }),
              /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: [
                `${unansweredQuestions.length} questions to assign across ${delegateList.length} delegate${delegateList.length !== 1 ? "s" : ""}`
              ] })
            ] }),
            delegateList.length > 0 && poolQuestions.length > 0 && /* @__PURE__ */ jsx("button", {
              onClick: splitEvenly,
              className: "px-4 py-2 bg-[var(--color-accent-fill)] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-lg",
              children: "Split Evenly"
            })
          ] }),
          delegateList.length > 0 && unansweredQuestions.length > 0 && /* @__PURE__ */ jsxs("div", {
                className: "mt-5", children: [
            /* @__PURE__ */ jsx("div", {
                  className: "w-full h-3 bg-[var(--color-bg-alt)] rounded-full overflow-hidden flex", children: [
                    ...delegateList.map((d, i) => {
                      const pct = unansweredQuestions.length > 0 ? ((d.questions || []).length / unansweredQuestions.length * 100) : 0;
                      return pct > 0 ? /* @__PURE__ */ jsx("div", {
                        className: `h-full ${DELEGATE_COLORS[i % DELEGATE_COLORS.length]} transition-all duration-500`,
                        style: { width: `${pct}%` }
                      }, `bar-${d.id}`) : null;
                    }),
                    poolQuestions.length > 0 ? /* @__PURE__ */ jsx("div", {
                      className: "h-full bg-[var(--color-border)] transition-all duration-500 flex-1"
                    }) : null
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-4 mt-2 flex-wrap", children: [
                    ...delegateList.map((d, i) => /* @__PURE__ */ jsxs("div", {
                      className: "flex items-center gap-1.5", children: [
                  /* @__PURE__ */ jsx("div", { className: `w-2.5 h-2.5 rounded-full ${DELEGATE_COLORS[i % DELEGATE_COLORS.length]}` }),
                  /* @__PURE__ */ jsxs("span", { className: "text-[10px] font-bold text-[color:var(--color-text-muted-dark)]", children: [d.name.split(" ")[0], ": ", (d.questions || []).length] })
                      ]
                    }, `legend-${d.id}`)),
              /* @__PURE__ */ jsxs("div", {
                      className: "flex items-center gap-1.5", children: [
                  /* @__PURE__ */ jsx("div", { className: "w-2.5 h-2.5 rounded-full bg-[var(--color-border)]" }),
                  /* @__PURE__ */ jsxs("span", { className: "text-[10px] font-bold text-[color:var(--color-text-muted)]", children: ["Unassigned: ", poolQuestions.length] })
                      ]
                    })
                  ]
                })
                ]
              })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          delegateList.length === 0 && /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-info-bg)] rounded-2xl p-6 border border-[color:var(--color-info-line)] text-center", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-[color:var(--color-accent)]", children: "No delegates have been invited to this project yet." }),
            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-accent)] mt-2", children: "Invite company delegates from the Members page to assign them questions." })
                ]
              }),
          unansweredQuestions.length === 0 && /* @__PURE__ */ jsxs("div", {
                className: "bg-[var(--color-ok-bg)] rounded-2xl p-6 border border-[color:var(--color-ok-line)] text-center", children: [
            /* @__PURE__ */ jsx("span", { className: "text-3xl", children: "\u2713" }),
            /* @__PURE__ */ jsx("p", { className: "text-sm font-black text-[color:var(--color-ok-fg)] mt-2", children: "All questions have been answered by SPOC. No delegation needed." })
                ]
              }),
          delegateList.length > 0 && poolQuestions.length > 0 && /* @__PURE__ */ jsxs("div", {
                className: "space-y-4", children: [
            /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsxs("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: ["Unassigned Questions (", poolQuestions.length, ")"] }),
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs("select", {
                      value: domainFilter,
                      onChange: (e) => setDomainFilter(e.target.value),
                      className: "text-[10px] font-bold text-[color:var(--color-text-muted-dark)] bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/20", children: [
                  /* @__PURE__ */ jsxs("option", { value: "all", children: ["All Domains (", poolQuestions.length, ")"] }),
                  ...poolDomains.map((d) => /* @__PURE__ */ jsxs("option", { value: d, children: [d, " (", poolQuestions.filter((q) => q.domain === d).length, ")"] }, d))
                      ]
                    }),
                /* @__PURE__ */ jsx("button", {
                      onClick: () => {
                        const allChecked = filteredPoolQuestions.every((q) => delegateQuestionAssign[q.id]);
                        if (allChecked) {
                          setDelegateQuestionAssign((prev) => {
                            const next = { ...prev };
                            filteredPoolQuestions.forEach((q) => delete next[q.id]);
                            return next;
                          });
                        } else {
                          setDelegateQuestionAssign((prev) => {
                            const next = { ...prev };
                            filteredPoolQuestions.forEach((q) => { next[q.id] = true; });
                            return next;
                          });
                        }
                      },
                      className: "text-[10px] font-bold text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] transition-all px-2 py-1.5 rounded-lg hover:bg-[var(--color-info-bg)]",
                      children: filteredPoolQuestions.every((q) => delegateQuestionAssign[q.id]) && filteredPoolQuestions.length > 0 ? "Deselect All" : "Select All"
                    })
              ] })
                  ]
                }),
            /* @__PURE__ */ jsx("div", {
                  className: "space-y-1.5 max-h-[280px] overflow-y-auto border rounded-xl p-3 bg-[var(--color-surface-sunken)]", children: filteredPoolQuestions.length > 0 ? filteredPoolQuestions.map((q, i) => /* @__PURE__ */ jsxs("label", {
                    className: `flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${delegateQuestionAssign[q.id] ? "bg-[var(--color-info-bg)] border-[color:var(--color-info-line)] shadow-sm" : "bg-[var(--color-bg-white)] border-[color:var(--color-border)] hover:border-[color:var(--color-border-strong)]"}`, children: [
              /* @__PURE__ */ jsx("input", {
                      type: "checkbox",
                      checked: !!delegateQuestionAssign[q.id],
                      onChange: (e) => setDelegateQuestionAssign((prev) => {
                        const next = { ...prev };
                        if (e.target.checked) next[q.id] = true; else delete next[q.id];
                        return next;
                      }),
                      className: "mt-0.5 rounded border-[color:var(--color-border-strong)] text-[color:var(--color-accent)]"
                    }),
              /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black text-[color:var(--color-text-muted)] shrink-0", children: q.qNo || `Q${i + 1}` }),
                  q.domain && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-bold text-[color:var(--color-accent)] uppercase tracking-widest bg-[var(--color-info-bg)] px-1.5 py-0.5 rounded shrink-0", children: q.domain })
                ] }),
                /* @__PURE__ */ jsx("p", { className: "text-[11px] text-[color:var(--color-text-muted-dark)] leading-relaxed mt-0.5 line-clamp-2", children: q.question })
              ] })
                    ]
                  }, q.id)) : /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] text-center py-4", children: "No questions match this filter." })
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "flex items-center gap-2 pt-2 border-t border-[color:var(--color-border)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-bold text-[color:var(--color-text-muted)] shrink-0", children: "Assign to:" }),
              /* @__PURE__ */ jsxs("select", {
                    value: assignTarget,
                    onChange: (e) => setAssignTarget(e.target.value),
                    className: "flex-1 text-[11px] font-bold text-[color:var(--color-text-muted-dark)] bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500/20", children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Select delegate..." }),
                ...delegateList.map((d) => /* @__PURE__ */ jsx("option", { value: d.id, children: d.name }, d.id))
                    ]
                  }),
              /* @__PURE__ */ jsx("button", {
                    onClick: () => { assignCheckedToTarget(assignTarget); setAssignTarget(""); },
                    disabled: !assignTarget || Object.keys(delegateQuestionAssign).filter((k) => delegateQuestionAssign[k]).length === 0,
                    className: `px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${assignTarget && Object.keys(delegateQuestionAssign).filter((k) => delegateQuestionAssign[k]).length > 0 ? "bg-[var(--color-accent-fill)] text-white hover:brightness-110 shadow-md" : "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed"}`,
                    children: "Assign"
                  }),
              delegateList.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 ml-2 border-l border-[color:var(--color-border)] pl-3", children:
                delegateList.map((d) => /* @__PURE__ */ jsx("button", {
                  onClick: () => assignAllRemaining(d.id),
                  title: `Assign all ${poolQuestions.length} remaining to ${d.name}`,
                  className: "px-2 py-1.5 rounded-lg text-[9px] font-bold text-[color:var(--color-text-muted)] bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] hover:bg-[var(--color-bg-alt)] hover:border-[color:var(--color-border-strong)] transition-all whitespace-nowrap",
                  children: `All \u2192 ${d.name.split(" ")[0]}`
                }, `allto-${d.id}`))
              })
                  ]
                })
                ]
              }),
          delegateList.length > 0 && delegateList.some((d) => (d.questions || []).length > 0) && /* @__PURE__ */ jsxs("div", {
                className: "space-y-3", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Delegate Assignments" }),
            /* @__PURE__ */ jsx("div", {
                  className: `grid gap-4 ${delegateList.length === 1 ? "grid-cols-1" : delegateList.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`, children: delegateList.map((d, di) => {
                    const dqs = (d.questions || []).map(resolveDelegatedQuestion);
                    if (dqs.length === 0) return null;
                    const isExpanded = expandedDelegateCards[d.id] !== false;
                    return /* @__PURE__ */ jsxs("div", {
                      className: "border rounded-2xl overflow-hidden bg-[var(--color-surface-sunken)]", children: [
                /* @__PURE__ */ jsxs("div", {
                        className: `px-4 py-3 flex items-center justify-between ${DELEGATE_COLORS[di % DELEGATE_COLORS.length].replace("bg-", "bg-").replace("500", "50")} border-b`, children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx("div", { className: `w-7 h-7 ${DELEGATE_COLORS[di % DELEGATE_COLORS.length]} rounded-lg flex items-center justify-center text-[10px] font-black text-white`, children: d.name.charAt(0).toUpperCase() }),
                    /* @__PURE__ */ jsxs("div", { children: [
                      /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-[color:var(--color-text-hero)] block", children: d.name }),
                      /* @__PURE__ */ jsxs("span", { className: "text-[9px] font-medium text-[color:var(--color-text-muted)]", children: [dqs.length, " question", dqs.length !== 1 ? "s" : ""] })
                    ] })
                  ] }),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                    /* @__PURE__ */ jsx("button", {
                          onClick: () => setExpandedDelegateCards((prev) => ({ ...prev, [d.id]: !isExpanded })),
                          className: "text-[9px] font-bold text-[color:var(--color-text-muted)] text-[color:var(--color-text-muted-dark)] px-1.5 py-1 rounded transition-all",
                          children: isExpanded ? "\u25B2 Hide" : "\u25BC Show"
                        }),
                    /* @__PURE__ */ jsx("button", {
                          onClick: () => unassignAllFromDelegate(d.id),
                          className: "text-[9px] font-bold text-[color:var(--color-danger-fg)] hover:text-[color:var(--color-danger-fg)] px-1.5 py-1 rounded hover:bg-[var(--color-danger-bg)] transition-all",
                          children: "Clear All"
                        })
                  ] })
                        ]
                      }),
                isExpanded && /* @__PURE__ */ jsx("div", {
                        className: "p-3 space-y-1 max-h-[200px] overflow-y-auto", children: dqs.map((q) => /* @__PURE__ */ jsxs("div", {
                          className: "flex items-start justify-between gap-2 px-2 py-1.5 rounded-lg bg-[var(--color-bg-white)] border border-[color:var(--color-border)] group hover:border-[color:var(--color-border-strong)] transition-all", children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                      /* @__PURE__ */ jsxs("span", { className: "text-[9px] font-black text-[color:var(--color-text-muted)]", children: [q.qNo || q.id, " "] }),
                      /* @__PURE__ */ jsx("span", { className: "text-[10px] text-[color:var(--color-text-muted-dark)] line-clamp-1", children: q.question })
                    ] }),
                    /* @__PURE__ */ jsx("button", {
                              onClick: () => unassignQuestion(q.id),
                              className: "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-danger-fg)] transition-all shrink-0 opacity-0 group-hover:opacity-100",
                              title: "Remove from delegate",
                              children: "\u2715"
                            })
                          ]
                        }, q.id))
                      })
                      ]
                    }, d.id);
                  })
                })
                ]
              }),
          /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  disabled: !canConfirmDelegation,
                  onClick: () => {
                    if (!canConfirmDelegation) return;
                    setDelegationFinalized(false);
                    setSpocWorkComplete(false);
                    if (!delegated) setDelegated(true);
                    const assignments = delegateList
                      .filter((d) => (d.questions || []).length > 0)
                      .map((d) => ({ delegateId: d.id, delegateName: d.name, delegateEmail: d.email || "", questionIds: d.questions }));
                    persistGapWizard({
                      delegateAssignments: assignments,
                      delegated: true,
                      spocSubStep: "review-approve",
                      delegationFinalized: false,
                      spocWorkComplete: false,
                    });
                    setSpocSubStep("review-approve");
                  },
                  className: `w-full px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-current ${delegationCommitComplete ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)] hover:brightness-110" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: delegationCommitComplete
                    ? "\u2713 Delegation Confirmed - Review & Finalize \u2192"
                    : "Confirm Delegation & Continue"
                }
              )
              ]
            })
            ]
          }),
          spocSubStep === "review-approve" && (() => {
            const assignedDelegates = delegateList.filter((d) => (d.questions || []).length > 0);
            const totalDelegated = assignedDelegates.reduce((s, d) => s + (d.questions || []).length, 0);
            return /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden", children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Review & Finalize Delegation" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Review the assignments below before sending them to delegates. Once finalized, delegates will receive their questions." })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [
            /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-info-bg)]   rounded-2xl p-5 border border-[color:var(--color-info-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Assigned to Delegates" }),
              /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1", children: totalDelegated })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-ok-bg)]   rounded-2xl p-5 border border-[color:var(--color-ok-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "Delegates" }),
              /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1", children: assignedDelegates.length })
                  ]
                }),
            /* @__PURE__ */ jsxs("div", {
                  className: "bg-[var(--color-info-bg)]   rounded-2xl p-5 border border-[color:var(--color-info-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Assigned to SPOC" }),
              /* @__PURE__ */ jsx("p", { className: "text-2xl font-black text-[color:var(--color-text-hero)] mt-1", children: Object.keys(mergedSpocAnswers).filter((k) => mergedSpocAnswers[k]).length })
                  ]
                })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "space-y-4", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Delegate Assignment Summary" }),
            ...assignedDelegates.map((d, di) => {
              const dqs = (d.questions || []).map(resolveDelegatedQuestion);
              const domainCounts = {};
              dqs.filter((q) => !q.notFound).forEach((q) => { domainCounts[q.domain || UNCLASSIFIED_DOMAIN] = (domainCounts[q.domain || UNCLASSIFIED_DOMAIN] || 0) + 1; });
              return /* @__PURE__ */ jsxs("div", {
                className: "border rounded-2xl overflow-hidden", children: [
                /* @__PURE__ */ jsxs("div", {
                  className: "px-5 py-3 bg-[var(--color-surface-sunken)] flex items-center justify-between border-b", children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
                      /* @__PURE__ */ jsx("div", { className: `w-8 h-8 ${DELEGATE_COLORS[di % DELEGATE_COLORS.length]} rounded-lg flex items-center justify-center text-[10px] font-black text-white`, children: d.name.charAt(0).toUpperCase() }),
                      /* @__PURE__ */ jsxs("div", { children: [
                        /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-[color:var(--color-text-hero)] block", children: d.name }),
                        /* @__PURE__ */ jsxs("span", { className: "text-[9px] text-[color:var(--color-text-muted)]", children: [dqs.length, " question", dqs.length !== 1 ? "s" : ""] })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2 flex-wrap", children: Object.entries(domainCounts).map(([dom, cnt]) => /* @__PURE__ */ jsxs("span", { className: "text-[8px] font-bold text-[color:var(--color-accent)] uppercase bg-[var(--color-info-bg)] px-2 py-0.5 rounded", children: [dom, " (", cnt, ")"] }, dom)) })
                  ]
                }),
                /* @__PURE__ */ jsx("div", {
                  className: "p-4 space-y-1 max-h-[180px] overflow-y-auto", children: dqs.map((q) => /* @__PURE__ */ jsxs("div", {
                    className: "flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-bg-white)] border border-[color:var(--color-border)] text-[10px]", children: [
                      /* @__PURE__ */ jsx("span", { className: "font-black text-[color:var(--color-text-muted)] shrink-0", children: q.qNo || q.id }),
                      /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted-dark)] line-clamp-1 flex-1", children: q.question }),
                      q.domain && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-bold text-[color:var(--color-accent)] uppercase shrink-0", children: q.domain })
                    ]
                  }, q.id))
                })
              ]
              }, d.id);
            })
                ]
              }),
          delegationFinalized === true ? /* @__PURE__ */ jsx("div", { className: "bg-[var(--color-ok-bg)] rounded-2xl p-5 border border-[color:var(--color-ok-line)] text-center", children: /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: "\u2713 Delegation Finalized - Questions sent to delegates" }) }) :
          /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: async () => {
                    if (project?.id && fetchProjectProfile) {
                      const merged = await fetchProjectProfile(project.id);
                      if (merged && typeof merged === "object") {
                        projectProfileRef.current = merged;
                      }
                    }
                    setDelegationFinalized(true);
                    setSpocWorkComplete(true);
                    const finalizeAssignments = delegateList
                      .filter((d) => (d.questions || []).length > 0)
                      .map((d) => ({
                        delegateId: d.id,
                        delegateName: d.name,
                        delegateEmail: d.email || "",
                        questionIds: d.questions,
                      }));
                    persistGapWizard({
                      delegateAssignments: finalizeAssignments,
                      delegationFinalized: true,
                      spocWorkComplete: true,
                      spocSubStep: "review-approve",
                    });
                  },
                  className: "w-full px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg bg-[var(--color-accent-fill)] text-white hover:brightness-110",
                  children: "Finalize & Send to Delegates"
                }
              )
              ]
            })
            ]
          });
          })(),
          spocSubStep === "meetings" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden",
            children: [
              /* @__PURE__ */ jsxs("div", {
                className: "px-8 py-6 border-b border-[color:var(--color-border)]",
                children: [
                  /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Meeting requests" }),
                  /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Meetings scheduled by the Lead Advisor for this project appear here. Add delegates so they are notified and the meeting is confirmed." })
                ]
              }),
              /* @__PURE__ */ jsxs("div", {
                className: "p-8 space-y-8",
                children: [
                  spocMeetingsLoading && /* @__PURE__ */ jsxs("div", {
                    className: "text-center py-10",
                    children: [
                      /* @__PURE__ */ jsx("div", { className: "w-8 h-8 border-2 border-[color:var(--color-accent)] border-t-transparent rounded-full animate-spin mx-auto" }),
                      /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)] mt-3 font-medium", children: "Loading meetings..." })
                    ]
                  }),
                  !spocMeetingsLoading && /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      /* @__PURE__ */ jsxs("div", {
                        className: "space-y-4",
                        children: [
                          /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Awaiting your action" }),
                          spocMeetingsAwaiting.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)]", children: "No meeting requests pending delegates." }),
                          ...spocMeetingsAwaiting.map((m) => {
                            const pickIds = spocDelegatePicksByMeeting[m.id] || [];
                            const saving = spocSavingMeetingId === m.id;
                            return /* @__PURE__ */ jsxs("div", {
                              className: "border border-[color:var(--color-info-line)] bg-[var(--color-info-bg)]   rounded-2xl overflow-hidden shadow-sm",
                              children: [
                                /* @__PURE__ */ jsxs("div", {
                                  className: "p-4 md:p-5 border-b border-[color:var(--color-info-line)]",
                                  children: [
                                    /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-[color:var(--color-text-hero)] block", children: m.title }),
                                    /* @__PURE__ */ jsxs("span", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-0.5 block", children: [formatSpocMeetingDate(m.meetingDate), " \u2022 ", SPOC_MEETING_TYPE_LABELS[m.meetingType] || m.meetingType, m.department ? ` \u2022 Dept: ${m.department}` : ""] }),
                                    m.notes && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-2 line-clamp-2", children: m.notes }),
                                    (m.meetingLocation || m.meetingLink) && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-3 mt-2 text-[10px] text-[color:var(--color-text-muted)]", children: [
                                      m.meetingLocation && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: ["\uD83D\uDCCD ", m.meetingLocation] }),
                                      m.meetingLink && /* @__PURE__ */ jsx("a", { href: m.meetingLink, target: "_blank", rel: "noreferrer", className: "text-[color:var(--color-accent)] hover:underline font-semibold", children: "Join link" })
                                    ] })
                                  ]
                                }),
                                /* @__PURE__ */ jsxs("div", {
                                  className: "bg-[var(--color-bg-white)] px-4 py-4 md:px-5 md:py-5",
                                  children: [
                                    /* @__PURE__ */ jsx("p", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)] mb-0.5", children: "Assign attendees" }),
                                    /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mb-3", children: "Select project members below. They receive an in-app notification when you confirm." }),
                                    spocRosterLoading && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted-dark)] bg-[var(--color-bg-alt)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 mb-2", children: "Loading project roster\u2026" }),
                                    !spocRosterLoading && spocTabProjectMembers.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] bg-[var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 mb-2", children: "No members on this project yet." }),
                                    /* @__PURE__ */ jsx("div", {
                                      className: "space-y-1.5 max-h-56 overflow-y-auto rounded-xl border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] p-2",
                                      children: spocTabProjectMembers.map((mem) => {
                                        const selected = pickIds.includes(mem.id);
                                        return /* @__PURE__ */ jsxs("button", {
                                          type: "button",
                                          onClick: () => toggleSpocMeetingDelegatePick(m.id, mem.id),
                                          className: `w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${selected ? "bg-[var(--color-info-bg)] border-[color:var(--color-info-line)] shadow-sm" : "bg-[var(--color-bg-white)] border-[color:var(--color-border)] hover:border-[color:var(--color-border-strong)] hover:bg-[var(--color-bg-alt)]"}`,
                                          children: [
                                            /* @__PURE__ */ jsx("span", { className: `w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black shrink-0 ${selected ? "bg-[var(--color-accent-fill)] text-white" : "bg-[var(--color-border)] text-[color:var(--color-text-muted)]"}`, children: selected ? "\u2713" : "" }),
                                            /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
                                              /* @__PURE__ */ jsx("div", { className: "text-xs font-bold text-[color:var(--color-text-hero)] truncate", children: mem.name || mem.email }),
                                              /* @__PURE__ */ jsx("div", { className: "text-[10px] text-[color:var(--color-text-muted)] truncate", children: mem.email })
                                            ] }),
                                            /* @__PURE__ */ jsx("span", { className: "text-[8px] font-bold text-[color:var(--color-text-muted)] uppercase shrink-0 max-w-[100px] truncate", children: (mem.role || "").replace(/_/g, " ") })
                                          ]
                                        }, mem.id);
                                      })
                                    }),
                                    /* @__PURE__ */ jsxs("div", {
                                      className: "mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[color:var(--color-border)]",
                                      children: [
                                        /* @__PURE__ */ jsxs("span", { className: "text-[10px] font-medium text-[color:var(--color-text-muted)]", children: [pickIds.length, " attendee", pickIds.length !== 1 ? "s" : "", " selected"] }),
                                        /* @__PURE__ */ jsx("button", {
                                          type: "button",
                                          disabled: pickIds.length === 0 || saving || spocRosterLoading,
                                          onClick: () => confirmSpocDelegatesForMeeting(m.id, pickIds),
                                          className: `w-full sm:w-auto px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-sm ${pickIds.length === 0 || saving || spocRosterLoading ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                                          children: saving ? "Saving\u2026" : "Confirm & notify attendees"
                                        })
                                      ]
                                    })
                                  ]
                                })
                              ]
                            }, m.id);
                          })
                        ]
                      }),
                      /* @__PURE__ */ jsxs("div", {
                        className: "space-y-4 pt-2 border-t border-[color:var(--color-border)]",
                        children: [
                          /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]", children: "Scheduled" }),
                          spocMeetingsScheduled.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted)]", children: "No scheduled meetings for this project yet." }),
                          ...spocMeetingsScheduled.map((m) => {
                            const att = Array.isArray(m.attendees) ? m.attendees : [];
                            return /* @__PURE__ */ jsxs("div", {
                              className: "border border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] rounded-xl p-4",
                              children: [
                                /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-[color:var(--color-text-hero)] block", children: m.title }),
                                /* @__PURE__ */ jsxs("span", { className: "text-[10px] text-[color:var(--color-text-muted)]", children: [formatSpocMeetingDate(m.meetingDate), " \u2022 ", SPOC_MEETING_TYPE_LABELS[m.meetingType] || m.meetingType, m.department ? ` \u2022 Dept: ${m.department}` : ""] }),
                                att.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-1.5 mt-2", children: [
                                  /* @__PURE__ */ jsx("span", { className: "text-[9px] font-bold text-[color:var(--color-text-muted)] uppercase mr-1", children: "Attendees:" }),
                                  ...att.map((a, i) => /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded text-[9px] text-[color:var(--color-text-muted-dark)]", children: [
                                    (a.name || "?").charAt(0).toUpperCase(),
                                    " ",
                                    a.name
                                  ] }, a.userId || i))
                                ] })
                              ]
                            }, m.id);
                          })
                        ]
                      })
                    ]
                  })
                ]
              })
            ]
          }),
          spocSubStep === "report-review" && /* @__PURE__ */ jsxs("div", {
            className: "bg-[var(--color-bg-white)] rounded-2xl border border-[color:var(--color-border)] shadow-sm overflow-hidden flex flex-col",
            style: { maxHeight: "min(92vh, 1280px)" },
            children: [
              /* @__PURE__ */ jsxs("div", {
                className: "px-6 py-5 border-b border-[color:var(--color-border)] bg-[var(--color-surface-sunken)] text-[color:var(--color-text-hero)]",
                children: [
                  /* @__PURE__ */ jsx("h3", { className: "text-lg font-black uppercase tracking-widest", children: "Gap Analysis Report (read-only)" }),
                  /* @__PURE__ */ jsxs("p", { className: "text-[10px] text-[color:var(--color-accent)] font-medium mt-1", children: [
                    [project?.certification, reportCompanyName(project), "Review and add comments"].filter(Boolean).join(" \u00b7 ")
                  ] })
                ]
              }),
              !reportSections.executiveSummary ? /* @__PURE__ */ jsxs("div", { className: "p-10 text-center text-[color:var(--color-text-muted)] text-sm", children: [
                /* @__PURE__ */ jsx("p", { children: "No report draft is available yet." }),
                /* @__PURE__ */ jsx("p", { className: "text-[10px] mt-2", children: "The Lead Advisor will send the report when it is ready." })
              ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
              // Task #75 Gap B: the SPOC report-review view was a hand-copied duplicate of
              // the LA report's markup that never got the aiFailed marker treatment. Now
              // read-only (no onRetry) — SPOC's persona at this stage only ever reviews and
              // comments, never retries AI scoring (that action stays LA/Advisor-only,
              // Task #74). Same GapAnalysisAiFailureBanner used above, same count.
              reportFailedRowCount > 0 && /* @__PURE__ */ jsx("div", { className: "px-6 pt-4", children: /* @__PURE__ */ jsx(GapAnalysisAiFailureBanner, { failedRowCount: reportFailedRowCount }) }),
              /* @__PURE__ */ jsxs("div", {
                className: "flex flex-col lg:flex-row flex-1 min-h-0 min-w-0 overflow-hidden",
                children: [
                  /* @__PURE__ */ jsx(ContractGoogleDocsShell, {
                    showSidebar: false,
                    enableSelectionToolbar: isSPOC && step4Status === "Under Review",
                    annotations: gapReportAnnotations,
                    selectedAnchorKey: gapReportAnnotationFocusKey,
                    onSelectAnchor: setGapReportAnnotationFocusKey,
                    onPostAnnotation: handleGapReportAnnotationPost,
                    shellOuterClassName: "flex-1 min-w-0 min-h-0 items-stretch justify-start flex-[1] !gap-0",
                    deskClassName:
                      "flex-1 min-h-0 min-w-0 overflow-y-auto bg-transparent rounded-none border-0 shadow-none p-0 max-h-[min(88vh,1200px)] justify-center",
                    paperClassName:
                      "shadow-none border-0 rounded-none min-h-[52vh] max-w-none w-full px-10 py-10 sm:px-10 sm:py-10",
                    userDisplayName: user?.name || user?.email || "SPOC",
                    userInitial: String(user?.name || user?.email || "S").trim().slice(0, 1).toUpperCase() || "S",
                    emptySidebarHint: "No comments yet.",
                    children: /* @__PURE__ */ jsx("div", {
                      "data-contract-anchor": "gapReport",
                      className: "min-h-0 paper-surface bg-white text-slate-900",
                      style: { fontFamily: "'Times New Roman', Georgia, serif" },
                      children: /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsxs("div", {
                        className: "text-center border-b-2 border-slate-200 pb-6 mb-8", children: [
                          /* @__PURE__ */ jsxs("div", {
                            className: "flex justify-between items-start mb-6", children: [
                              /* @__PURE__ */ jsxs("div", { className: "text-left", children: [
                                /* @__PURE__ */ jsx("span", { className: "text-[10px] text-slate-400 uppercase", children: "Company" }),
                                /* @__PURE__ */ jsx("p", { className: "text-sm font-bold", children: reportCompanyName(project) || "-" })
                              ] }),
                              /* @__PURE__ */ jsxs("div", { className: "text-right", children: [
                                /* @__PURE__ */ jsx("span", { className: "text-[10px] text-slate-400 uppercase", children: "Advisor" }),
                                reportSections.affirmationName ? /* @__PURE__ */ jsx("p", { className: "text-sm font-bold", children: reportSections.affirmationName }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-400 italic", children: "Not provided" })
                              ] })
                            ]
                          }),
                          /* @__PURE__ */ jsx("h1", { className: "text-3xl font-bold tracking-tight mb-1", children: "Gap Analysis Report" }),
                          /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500", children: project.certification || "" })
                        ]
                      }),
                      /* @__PURE__ */ jsxs("section", { className: "mb-8", children: [
                        /* @__PURE__ */ jsx("h2", { className: "text-lg font-bold border-b border-slate-200 pb-2 mb-4", children: "1. Executive Summary" }),
                        /* @__PURE__ */ jsx("p", { className: "text-sm leading-relaxed text-slate-700", children: reportSections.executiveSummary })
                      ] }),
                      /* @__PURE__ */ jsxs("section", { className: "mb-8", children: [
                        /* @__PURE__ */ jsx("h2", { className: "text-lg font-bold border-b border-slate-200 pb-2 mb-4", children: "2. Scope" }),
                        /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
                          /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("legalEntities", false), label: "Legal Entities + Locations" }),
                          /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("productsServices", false), label: "Products and Services" }),
                          /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("activitiesDepts", false), label: "Activities / Departments" })
                        ] })
                      ] }),
                      renderReportAssessmentBody("gap-analysis-report-spoc-review"),
                      /* @__PURE__ */ jsxs("section", { className: "mb-4 border-t-2 border-slate-200 pt-6", children: [
                        /* @__PURE__ */ jsx("h2", { className: "text-lg font-bold mb-4", children: "8. Affirmation Statement" }),
                        /* @__PURE__ */ jsxs("p", { className: "text-sm leading-relaxed text-slate-700 italic mb-6", children: [
                          '"In my opinion the responses submitted present fairly the compliance readiness position of ',
                          /* @__PURE__ */ jsx("strong", { children: reportCompanyName(project) || "the company" }),
                          '."'
                        ] }),
                        /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-4", children: [
                          /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("affirmationName", false), label: "Name" }),
                          /* @__PURE__ */ jsx(GapReportSectionField, { ...reportFieldProps("affirmationDate", false), label: "Date", inputType: "date" })
                        ] }),
                        /* @__PURE__ */ jsx("p", { className: "text-[10px] text-slate-400 mt-4 text-center font-bold uppercase tracking-widest", children: "- Confidential -" })
                      ] })
                    ] })
                  }),
                  }),
                  /* @__PURE__ */ jsxs("div", {
                    className: "flex flex-col min-h-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-50/80 w-full lg:max-w-[360px] lg:w-[360px] lg:shrink-0",
                    children: [
                      step4Status === "Under Review" && /* @__PURE__ */ jsxs("div", {
                        className: "p-3 border-b border-slate-200 space-y-2 shrink-0",
                        children: [
                          /* @__PURE__ */ jsx("textarea", {
                            value: gapReportNewCommentDraft,
                            onChange: (e) => setGapReportNewCommentDraft(e.target.value),
                            placeholder: "Add a comment\u2026",
                            rows: 3,
                            className: "w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/20"
                          }),
                          /* @__PURE__ */ jsx("button", {
                            type: "button",
                            onClick: () => void handleGapReportAnnotationPost(),
                            className: "w-full px-3 py-2 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-600",
                            children: "Post comment"
                          })
                        ]
                      }),
                      /* @__PURE__ */ jsx("div", {
                        className: "flex-1 min-h-0 flex flex-col overflow-hidden p-3 lg:p-4",
                        children: /* @__PURE__ */ jsx(ContractAnnotationsSidebar, {
                          fillAvailableHeight: true,
                          annotations: gapReportAnnotations,
                          selectedAnchorKey: gapReportAnnotationFocusKey,
                          onSelectAnchor: setGapReportAnnotationFocusKey,
                          onReply: handleGapReportAnnotationReply,
                          onResolve: handleGapReportAnnotationResolve,
                          canReply: step4Status === "Under Review",
                          canResolve: false,
                          title: "Report comments",
                          emptyHint: "No comments yet."
                        })
                      })
                    ]
                  })
                ]
              })
              ] })
            ]
          })
        ]
      }),
      isDelegate && (() => {
        const assignments = gw.delegateAssignments || [];
        const members = project?.members || [];
        const myMemberRecord = members.find((m) => m.email && myDelegateEmail && m.email.toLowerCase() === myDelegateEmail.toLowerCase());
        const myDbUserId = myMemberRecord?.id || "";
        const myAssignment = assignments.find((a) => {
          if (a.delegateId === myDelegateId) return true;
          if (a.delegateEmail && myDelegateEmail && a.delegateEmail.toLowerCase() === myDelegateEmail.toLowerCase()) return true;
          if (myDbUserId && a.delegateId === myDbUserId) return true;
          if (a.delegateName && user?.name && a.delegateName.toLowerCase() === user.name.toLowerCase()) return true;
          return false;
        });
        const myQuestionIds = myAssignment?.questionIds || [];
        const myQuestions = myQuestionIds.map((qid) => releasedQuestions.find((q) => q.id === qid)).filter(Boolean);
        const myDomains = [...new Set(myQuestions.map((q) => q.domain).filter(Boolean))];
        const answeredCount = myQuestions.filter((q) => !!delAnswers[q.id]).length;
        const remainingDelegateQuestions = Math.max(0, myQuestions.length - answeredCount);
        const allAnswered = myQuestions.length > 0 && answeredCount >= myQuestions.length;
        const lockedDelKeys = !delAcknowledged ? new Set(["respond-del"]) : new Set();
        const completedDelKeys = /* @__PURE__ */ new Set();
        if (delAcknowledged) completedDelKeys.add("acknowledge");
        if (delResponded) completedDelKeys.add("respond-del");
        const delSubStepReadOnly = completedDelKeys.has(delSubStep);
        return /* @__PURE__ */ jsxs("div", {
        className: "space-y-6", children: [
          delegationFinalized !== true && /* @__PURE__ */ jsxs("div", {
            className: "bg-amber-50 rounded-2xl p-5 border border-amber-200 text-center", children: [
              /* @__PURE__ */ jsx("span", { className: "text-lg", children: "\u23F3" }),
              /* @__PURE__ */ jsx("p", { className: "text-xs font-bold text-amber-800 mt-1", children: "The SPOC has not yet finalized the delegation. Your questions will appear once the delegation is finalized." })
            ]
          }),
          delegationFinalized === true && myQuestions.length === 0 && /* @__PURE__ */ jsxs("div", {
            className: "bg-slate-50 rounded-2xl p-5 border border-slate-200 text-center", children: [
              /* @__PURE__ */ jsx("span", { className: "text-lg", children: "\u2139\uFE0F" }),
              /* @__PURE__ */ jsx("p", { className: "text-xs font-bold text-slate-600 mt-1", children: "No questions have been assigned to you for this project." })
            ]
          }),
          delegationFinalized === true && myQuestions.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(SubStepNav, { className: "mb-8", steps: buildSubSteps(DEL_SUBSTEPS, { lockedKeys: lockedDelKeys, completedKeys: completedDelKeys, badgeByKey: undefined, attentionTitleByKey: undefined }), activeKey: delSubStep, onSelect: setDelSubStep }),
          delSubStepReadOnly && /* @__PURE__ */ jsx("div", {
            className: "rounded-2xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 text-[11px] font-semibold text-emerald-900",
            children: "You are viewing a completed step (read-only)."
          }),
          delSubStep === "acknowledge" && /* @__PURE__ */ jsxs("div", {
            className: `bg-white rounded-[2rem] border border-slate-100 shadow-2xl overflow-hidden${delSubStepReadOnly ? " pointer-events-none opacity-60" : ""}`, children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-slate-100", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-slate-700", children: "Acknowledge Questionnaire Assignment" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-slate-400 mt-1 font-medium", children: "Review your assignment and confirm receipt before responding" })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-6", children: [
          /* @__PURE__ */ jsx("div", {
                className: "bg-blue-50 rounded-2xl p-6 border border-blue-200", children: /* @__PURE__ */ jsxs("p", {
                  className: "text-xs text-slate-700 leading-relaxed", children: [
                    "You have been assigned ",
            /* @__PURE__ */ jsx("strong", { children: myQuestions.length }),
                    " question", myQuestions.length !== 1 ? "s" : "", " from the ",
            /* @__PURE__ */ jsx("strong", { children: project.certification || "selected standard" }),
                    " gap analysis questionnaire by the Company SPOC."
                  ]
                })
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "bg-slate-50 rounded-2xl p-5 border", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3", children: "Assignment Details" }),
            /* @__PURE__ */ jsxs("div", {
                  className: "space-y-2", children: [
              /* @__PURE__ */ jsxs("div", {
                    className: "flex justify-between py-1", children: [
                /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500", children: "Assigned By" }),
                /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-slate-700", children: "Company SPOC" })
                    ]
                  }),
              /* @__PURE__ */ jsxs("div", {
                    className: "flex justify-between py-1", children: [
                /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500", children: "Questions Assigned" }),
                /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-slate-700", children: myQuestions.length })
                    ]
                  }),
              /* @__PURE__ */ jsxs("div", {
                    className: "flex justify-between py-1", children: [
                /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-500", children: "Domains" }),
                /* @__PURE__ */ jsx("span", { className: "text-xs font-bold text-slate-700", children: myDomains.join(", ") || "None specified" })
                    ]
                  })
                  ]
                })
                ]
              }),
          /* @__PURE__ */ jsxs("div", {
                className: "space-y-1 max-h-[200px] overflow-y-auto", children: [
            /* @__PURE__ */ jsx("h5", { className: "text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2", children: "Your Questions" }),
            ...myQuestions.map((q) => /* @__PURE__ */ jsxs("div", {
              className: "flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-100", children: [
                /* @__PURE__ */ jsx("span", { className: "text-[9px] font-black text-slate-400 shrink-0", children: q.qNo || q.id }),
                /* @__PURE__ */ jsx("span", { className: "text-[10px] text-slate-600 flex-1 line-clamp-1", children: q.question }),
                q.domain && /* @__PURE__ */ jsx("span", { className: "text-[8px] font-bold text-blue-500 uppercase shrink-0", children: q.domain })
              ]
            }, q.id))
                ]
              }),
          /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => {
                    setDelAcknowledged(true);
                    setDelSubStep("respond-del");
                    persistGapWizard({
                      delegateProgress: {
                        ...gw.delegateProgress,
                        [myDelegateProgressKey]: { ...myDelegateProgress, acknowledged: true },
                        ...(myDelegateId !== myDelegateProgressKey ? { [myDelegateId]: { ...myDelegateProgress, acknowledged: true } } : {}),
                      }
                    });
                  },
                  disabled: delAcknowledged,
                  className: `px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${delAcknowledged ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                  children: delAcknowledged ? "\u2713 Acknowledged" : "Acknowledge Assignment"
                }
              )
              ]
            })
            ]
          }),
          delSubStep === "respond-del" && /* @__PURE__ */ jsxs("div", {
            className: `bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden${delSubStepReadOnly ? " pointer-events-none opacity-60" : ""}`, children: [
        /* @__PURE__ */ jsxs("div", {
              className: "px-8 py-6 border-b border-[color:var(--color-border)]", children: [
          /* @__PURE__ */ jsx("h4", { className: "text-sm font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)]", children: "Respond to Questions" }),
          /* @__PURE__ */ jsx("p", { className: "text-[10px] text-[color:var(--color-text-muted)] mt-1 font-medium", children: "Answer the gap analysis questionnaire questions. Upload supporting documents for answered questions." }),
          /* @__PURE__ */ jsxs("div", {
              className: "flex flex-wrap items-center gap-4 mt-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-[var(--color-info-bg)] rounded-xl px-4 py-2 border border-[color:var(--color-info-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]", children: "Total" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: myQuestions.length })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-[var(--color-ok-bg)] rounded-xl px-4 py-2 border border-[color:var(--color-ok-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]", children: "Answered" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: answeredCount })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 bg-[var(--color-warn-bg)] rounded-xl px-4 py-2 border border-[color:var(--color-warn-line)]", children: [
              /* @__PURE__ */ jsx("span", { className: "text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)]", children: "Remaining" }),
              /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-text-hero)]", children: remainingDelegateQuestions })
            ] }),
              ]
            })
              ]
            }),
        /* @__PURE__ */ jsxs("div", {
              className: "p-8 space-y-4", children: [
                ...myQuestions.map((q, i) => {
                  const ans = delAnswers[q.id] || "";
                  const note = delNotes[q.id] || "";
                  let attsForRow = delAttachments[q.id];
                  if (!Array.isArray(attsForRow) || attsForRow.length === 0) {
                    const ex = gw.delegateProgress?.[myDelegateProgressKey]?.answers?.[q.id];
                    attsForRow = ex && typeof ex === "object" && Array.isArray(ex.attachments) ? ex.attachments : [];
                  }
                  if (!Array.isArray(attsForRow)) attsForRow = [];
                  return /* @__PURE__ */ jsxs("div", {
                  className: `rounded-xl px-5 py-4 border transition-all ${ans ? "bg-[var(--color-ok-bg)] border-[color:var(--color-ok-line)]" : "bg-[var(--color-surface-sunken)] border-[color:var(--color-border)]"}`, children: [
            /* @__PURE__ */ jsxs("div", {
                    className: "flex items-start gap-3 mb-3", children: [
              /* @__PURE__ */ jsxs("span", { className: "text-xs font-black text-[color:var(--color-text-muted)] min-w-[40px]", children: [
                        q.qNo || `Q${i + 1}`
                      ] }),
              /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0", children: [
                /* @__PURE__ */ jsx("p", { className: "text-xs text-[color:var(--color-text-muted-dark)] leading-relaxed font-medium", children: q.question }),
                q.domain && /* @__PURE__ */ jsx("span", { className: "text-[9px] font-bold text-[color:var(--color-accent)] uppercase tracking-widest mt-1 inline-block", children: q.domain })
              ] }),
              ans && /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-ok-fg)] text-sm font-black shrink-0", children: "\u2713" })
                    ]
                  }),
            /* @__PURE__ */ jsx("div", {
                    className: "flex items-center gap-2 ml-10 mb-3", children: ["Yes", "No", "Partial", "NA"].map((opt) => /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: () => setDelAnswers((prev) => ({ ...prev, [q.id]: opt })),
                        className: `px-3 py-1.5 rounded-lg text-[9px] font-bold transition-all ${ans === opt ? opt === "Yes" ? "bg-[var(--color-ok-bg)] text-[color:var(--color-ok-fg)] ring-1 ring-inset ring-[color:var(--color-ok-line)]" : opt === "No" ? "bg-[var(--color-danger-bg)] text-[color:var(--color-danger-fg)] ring-1 ring-inset ring-[color:var(--color-danger-line)]" : opt === "Partial" ? "bg-[var(--color-warn-bg)] text-[color:var(--color-warn-fg)] ring-1 ring-inset ring-[color:var(--color-warn-line)]" : "bg-[var(--color-border-strong)] text-white" : "bg-[var(--color-bg-white)] border text-[color:var(--color-text-muted)] hover:bg-[var(--color-bg-alt)]"}`,
                        children: opt
                      },
                      opt
                    ))
                  }),
            /* @__PURE__ */ jsx("div", {
                    className: "ml-10 mt-2", children: /* @__PURE__ */ jsx("textarea", {
                      placeholder: "Add notes or remarks (optional)...",
                      value: note,
                      onChange: (e) => setDelNotes((prev) => ({ ...prev, [q.id]: e.target.value })),
                      rows: 2,
                      className: "w-full bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg px-3 py-2 text-[11px] text-[color:var(--color-text-muted-dark)] resize-none outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    })
                  }),
            ans && /* @__PURE__ */ jsxs("div", {
                    className: "ml-10 mt-2 space-y-2", children: [
              /* @__PURE__ */ jsxs("label", {
                      className: `flex items-center gap-2 px-3 py-1.5 bg-[var(--color-bg-white)] border border-[color:var(--color-border)] rounded-lg ${delUploadingQid === q.id ? "opacity-50 cursor-wait" : "cursor-pointer hover:bg-[var(--color-bg-alt)]"} transition-all`, children: [
                /* @__PURE__ */ jsx("svg", { className: "w-3.5 h-3.5 text-[color:var(--color-text-muted)]", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" }) }),
                /* @__PURE__ */ jsx("span", { className: "text-[9px] font-bold uppercase tracking-widest text-[color:var(--color-text-muted)]", children: delUploadingQid === q.id ? "Uploading\u2026" : "Upload file (PDF, image, Word)" }),
                /* @__PURE__ */ jsx("input", {
                          type: "file",
                          disabled: delUploadingQid === q.id,
                          className: "hidden",
                          accept: ".pdf,.png,.jpg,.jpeg,.doc,.docx",
                          onChange: async (e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) await appendDelegateQuestionFile(q.id, f, q.qNo || "");
                          }
                        })
                      ]
                    }),
              attsForRow.map((att) => /* @__PURE__ */ jsxs("div", {
                        className: "flex items-center gap-2 text-[9px] font-medium text-[color:var(--color-ok-fg)] bg-[var(--color-ok-bg)] border border-[color:var(--color-ok-line)] rounded-lg px-3 py-1.5 w-fit max-w-full",
                        children: [
                          /* @__PURE__ */ jsx("svg", { className: "w-3 h-3 shrink-0", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 12l2 2 4-4" }) }),
                          /* @__PURE__ */ jsx("a", {
                            href: att.url,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            className: "truncate underline hover:text-[color:var(--color-ok-fg)]",
                            children: att.fileName || "Download"
                          }),
                          !delResponded && /* @__PURE__ */ jsxs(Fragment, { children: [
                            /* @__PURE__ */ jsx("span", { className: "text-[color:var(--color-text-muted)]", children: "\u00b7" }),
                            /* @__PURE__ */ jsx("button", {
                              type: "button",
                              disabled: deletingArtifactId === att.id,
                              onClick: () => removeDelegateQuestionFile(q.id, att.id),
                              className: "shrink-0 text-[8px] font-black uppercase tracking-wider text-[color:var(--color-danger-fg)] underline-offset-2 hover:underline hover:text-[color:var(--color-danger-fg)] cursor-pointer bg-transparent border-none p-0 disabled:opacity-40 disabled:cursor-wait",
                              children: deletingArtifactId === att.id ? "Removing\u2026" : "Delete"
                            })
                          ]})
                        ]
                      }, att.id))
                    ]
                  })
                  ]
                }, q.id);
                }),
          delResponded ? /* @__PURE__ */ jsx("div", { className: "bg-[var(--color-ok-bg)] rounded-2xl p-5 border border-[color:var(--color-ok-line)] text-center", children: /* @__PURE__ */ jsx("span", { className: "text-sm font-black text-[color:var(--color-ok-fg)]", children: "\u2713 Responses Submitted" }) }) :
          /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => {
                      setDelResponded(true);
                      const answersPayload = {};
                      myQuestions.forEach((q) => {
                        let atts = delAttachments[q.id];
                        if (!Array.isArray(atts) || atts.length === 0) {
                          const ex = gw.delegateProgress?.[myDelegateProgressKey]?.answers?.[q.id];
                          atts = ex && typeof ex === "object" && Array.isArray(ex.attachments) ? ex.attachments : [];
                        }
                        answersPayload[q.id] = {
                          response: delAnswers[q.id] || "",
                          notes: delNotes[q.id] || "",
                          attachments: atts || []
                        };
                      });
                      const progressEntry = {
                        ...myDelegateProgress,
                        answers: answersPayload,
                        submitted: true,
                        submittedAt: Date.now()
                      };
                      persistGapWizard({
                        delegateProgress: {
                          ...gw.delegateProgress,
                          [myDelegateProgressKey]: progressEntry,
                          ...(myDelegateId !== myDelegateProgressKey ? { [myDelegateId]: progressEntry } : {}),
                        }
                      });
                    },
                    disabled: !delAcknowledged || !allAnswered,
                    className: `mt-4 w-full px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${!delAcknowledged || !allAnswered ? "bg-[var(--color-border)] text-[color:var(--color-text-muted)] cursor-not-allowed" : "bg-[var(--color-accent-fill)] text-white hover:brightness-110"}`,
                    children: allAnswered ? "Submit Responses" : `Answer all questions to submit (${answeredCount}/${myQuestions.length})`
                  }
                )
              ]
            })
            ]
          })
          ] })
        ]
      });
      })(),
    ]
  });
};
